/**
 * Operations — the verbs of the tool.
 *
 * Each one takes a node, asks the model for a specific kind of reasoning move,
 * and threads the results onto the graph *as they arrive*. The pacing is
 * deliberate: nodes land one at a time with a beat between them, because a
 * branch that unfurls reads as thinking and a branch that appears at once
 * reads as a page load.
 */

import type {
  Audit,
  Bridge,
  CorpusEvidence,
  DeriveOp,
  Directive,
  DocNotice,
  FieldSurvey,
  Frame,
  Lens,
  ModelCallReceipt,
  ModelStamp,
  NodeKind,
  Outcome,
  PanelCheck,
  PanelVerdict,
  PanelVote,
  PortfolioRecord,
  BridgeEnd,
  JoiningSource,
  PassageCite,
  PriorArt,
  ReasonNode,
  RunMove,
  RunVerb,
  Session,
  SourceDesign,
  SourceReading,
  Spend,
  StoredLinkage,
  Temper,
  SurveyPart,
  SurveyRow,
  SurveySource,
  Verification,
  VerifyScope,
  WebSource,
  Programme,
  ProgrammeStep,
  IndexLinkage,
} from '../core/types'
import {
  LENS_LABEL,
  LINK_READING_LABEL,
  OP_LABEL,
  READING_LABEL,
  SOURCE_DESIGNS,
  bridgeReadable,
  endKey,
  auditableBy,
  normalizeNodeRef,
  branchAuthor,
  localEnds,
  passageRef,
  reasoningOf,
  refutedIsEarned,
  pairKey,
} from '../core/types'
import { assess, worthReasoning, type LinkDoc, type LinkPile, type Linkage } from '../core/link'
import { STATUS_WORD, chargeLine, pressEstimate } from '../core/vocabulary'
import {
  DEPARTS_LOCK_REFUSAL,
  EXECUTABLE_LOCK_REFUSAL,
  EXPLAIN_LOCK_REFUSAL,
  ONE_THOUGHT_BRANCH,
  ONE_THOUGHT_PASS,
} from '../core/upNext'
import { rewriteLandedSentence, rewriteLoss } from '../core/rewrite'
import { undoOffer } from '../core/undoOffer'
import { objectionToLand, panelLenses, panelSplit, panelSummary } from '../core/panel'
import { pulses } from '../core/pulse'
import {
  clipToSentence,
  textRetention,
  nodeTextTruncationNotes,
  RESEARCH_ITEM_CHARS,
  SOURCE_QUOTE_CHARS,
} from '../core/textRetention'
import {
  activeEntry,
  checkCanRead,
  lockHeld,
  makeNode,
  providerFor,
  resolveAuditor,
  resolveFor,
  resolveJudge,
  useStore,
  type Toast,
} from '../core/store'
import {
  estimateSpendUsd,
  estimateUsd,
  priceOf,
  stampOf,
  type ModelEntry,
  type ResolvedModel,
} from '../core/models'
import { parseCap } from '../core/ledger'
import { uid } from '../core/id'
import { clamp } from '../core/geometry'
import { BASE } from '../core/tempo'
import { canonicalUrl, hostOf, isHttpUrl } from '../core/url'
import { foldText, quoteFoundOn, simHash, textHash } from '../core/similarity'
import {
  appendVerification,
  evidenceRebaseQueue,
  pendingEvidenceStaleForSource,
  verificationEvents,
} from '../core/rebase'
import { resolveFalsificationPortfolio, type FalsificationFuture } from '../core/falsification'
import {
  SURVEY_PARTS,
  SURVEY_PART_CAP,
  SURVEY_QUERY_CAP,
  SURVEY_ROW_REFS,
  SURVEY_SOURCE_CAP,
  SURVEY_NEED,
  SURVEY_TEXT_CHARS,
  appendSurvey,
  questionHash,
} from '../core/survey'
import {
  createResearchState,
  upsertResearchEntities,
  type ResearchAuditOutcome,
  type ResearchPromptReceipt,
} from '../core/research'
import { projectSessionEvidence } from '../core/researchProjection'
import { CHUNKER, loadCorpus, searchlessCheckSentence, type LoadedDoc } from '../core/corpus'
import { liveOriginUrl } from '../core/sources'
import {
  DEMO_SESSION_ID,
  listRecent,
  listScanCandidates,
  loadDigest,
  loadForeignNode,
  loadOutcomeRecords,
  scanPoolKey,
  type Digest,
} from '../core/persist'
import { calibrate, resultPointer, resultPointerLine } from '../core/calibration'
import { claimQuery, retrieve, type Retrieval } from '../core/retrieve'
import { resolvePassEffort } from './effort'
import { askJudge } from './judge'
import { stanceInputsFor, stanceQuestions, withJudgedStances } from './judgeQuestions'
import {
  ModelError,
  failureNote,
  matchSeen,
  streamObjectsWithReceipt,
  type RetryWait,
  type SearchOutcome,
  type StreamOptions,
} from './transport'
import {
  declinedMessage,
  failureSentence,
  quietLine,
  refusalSentence,
  seatsQuietLine,
  waitLine,
  watchQuiet,
  type QuietWatch,
} from '../core/failure'
import {
  enabledLiveRows,
  fetchLiveSources,
  liveFailureNote,
  liveQuery,
  noLiveSources,
  type LiveResult,
} from './liveSources'
import {
  buildCheckAudit,
  type CheckAuditInterruptedRound,
  type CheckAuditRetrievalBatch,
  type CheckAuditRetrievalRow,
} from './checkAudit'
import {
  admissionSentence,
  conjectureAdmitter,
  conjectureTermsOnGraph,
  consensusNodeOf,
  reregister,
  toConsensusNode,
} from '../core/conjecture'
import {
  PRIOR_ART_MAX_FETCHES,
  PRIOR_ART_MAX_SEARCHES,
  PRIOR_ART_READING_LABEL,
  assessPriorArt,
  priorArtInstrument,
  withPriorArt,
} from '../core/priorArt'
import {
  SWEEP_MAX_FETCHES,
  SWEEP_MAX_SEARCHES,
  assessSweep,
  sweepInstrument,
  sweepReach,
  sweepSentence,
} from '../core/sweep'
import {
  conjectureContract,
  executableRefusal,
  readExecutableProposal,
  withConjectureContract,
} from '../core/executableConjecture'
import { EXECUTABLE_PURPOSE, markExecutableTest } from '../core/narrationReceipt'
import { emptyExperimentWorkspace } from '../core/experiments'
import {
  AUDIT_SCHEMA,
  BRIDGES_SCHEMA,
  CONJECTURES_SCHEMA,
  GATHER_SCHEMA,
  LINK_SCHEMA,
  NODES_SCHEMA,
  ELABORATING_NODES_SCHEMA,
  EXECUTABLE_SCHEMA,
  EXECUTABLE_SYSTEM,
  PRIOR_ART_SCHEMA,
  SWEEP_SCHEMA,
  VERIFY_SCHEMA,
  buildAuditSystem,
  buildAuditTurn,
  buildElaboratingUserTurn,
  buildExecutableTurn,
  buildConjunctionSystem,
  buildConjunctionTurn,
  buildGatherSystem,
  buildGatherTurn,
  buildLinkSystem,
  buildLinkTurn,
  buildPriorArtSystem,
  buildPriorArtTurn,
  buildSweepSystem,
  buildSweepTurn,
  buildVerifySystem,
  PANEL_SCHEMA,
  SURVEY_SCHEMA,
  buildBridgeTurn,
  defaultExtraFor,
  buildConjectureTurn,
  buildCorpusBlock,
  buildScanCorpus,
  buildOtherBranch,
  buildSourceRecordBlock,
  buildEvidenceRebaseExtra,
  buildOutcomeExtra,
  buildPanelSystem,
  buildPanelTurn,
  buildPortfolioTurn,
  buildSystem,
  buildSurveySystem,
  buildSurveyTurn,
  buildUserTurn,
  buildInvestigationTurn,
  buildVerifyTurn,
  dateLine,
  portfolioSchema,
  PLAN_SCHEMA,
  PLAN_STEPS_MAX,
  PLAN_VERBS,
  buildPlanTurn,
  SHARPEN_MAX,
  SHARPEN_SCHEMA,
  buildSharpenSystem,
  buildSharpenTurn,
} from './prompts'
import { revealDemo, hasDemoReserve, cancelDemo } from './demo'
import { startLander } from './lander'
import { maxYieldFor, conesOf, underAny } from './runPolicy'
import { projectVerbUsd, stopRun } from './run'
import { bridgeStanding } from '../core/rung'
import { track } from '../core/analytics'
import { basisOf, candidatePairs } from '../core/pairs'
import { indexScanFailed, scanIndexPiles } from './indexPiles'
import {
  mathematicsInPlay,
  researchContractBlock,
  researchContractFor,
  type ResearchActionContract,
} from '../core/researchContract'
import { parseAuditFinding } from '../core/auditReview'

/**
 * Beat between node arrivals, at the current tempo.
 *
 * Deliberately shorter than a strand's full growth, so siblings overlap and you
 * watch several cones in flight at once rather than a queue of one-at-a-time
 * lines — while still being long enough that each reads as a separate decision.
 */
const beat = () => BASE.beat / useStore.getState().tempo

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/**
 * What a declined pass writes onto its op: that it was declined, the provider's
 * note, and which safety filter declined it where the provider named one (D312).
 * One shape for every pass that can be refused, so none of them records the
 * refusal without the category the message is built from.
 */
const declinedOf = (res: { refusalNote?: string; refusalCategory?: string }) => ({
  refused: true as const,
  refusalNote: res.refusalNote,
  ...(res.refusalCategory ? { refusalCategory: res.refusalCategory } : {}),
})

let inFlight: AbortController | null = null

/** A structured research review shares the ordinary Stop control and busy lock. */
export function ownResearchAbort(controller: AbortController) {
  inFlight = controller
  return () => {
    if (inFlight === controller) inFlight = null
  }
}

/**
 * A pass's user turn as it leaves the machine: the research contract's block, the
 * date, then the pass's own words. One builder, for the call and for anything that
 * prices the call (`scanRequestText`).
 *
 * The date rides after the contract block, never before it: the codec, the audit
 * capsule and the standalone verifier each require a retained request to START
 * with the block its contract renders.
 */
const preparedTurn = (contract: ResearchActionContract, user: string): string =>
  `${researchContractBlock(contract)}${dateLine(Date.now())}\n\n${user}`

/**
 * A wait to ask again, said on the live pass (`waiting`): in the trace's place
 * while nothing has arrived, on the action line under it after. Something the
 * transport is doing, never something the model thought, so it is never part of
 * the retained trace; it goes as the request goes out again.
 */
function waitOnLine(): (w: RetryWait) => void {
  return (w) => useStore.getState().setWaiting(w.ms > 0 ? waitLine(w) : null)
}

/**
 * A lone pass gone quiet, said on the same line a wait uses (`watchQuiet` in
 * `core/failure.ts`, which says why and on what threshold). Never a stop
 * condition and never a retry; paused while the transport waits to ask again,
 * which says so itself.
 */
const sayQuietOnLine = (ms: number | null): void =>
  useStore.getState().setWaiting(ms === null ? null : quietLine(ms))

/** One route from an operation-owned physical call into its durable receipt set. */
function streamOpObjects(
  opId: string,
  options: StreamOptions & {
    researchContext?: {
      session?: Session
      purpose?: string
      targetIds?: string[]
      onPrepared?: (system: string, user: string) => void
    }
    /** A request that runs alongside others under this op. See `OverlapSeat`. */
    overlap?: OverlapSeat
  },
) {
  const { researchContext: context, overlap, ...request } = options
  const session = useStore.getState().session
  const op = session.ops.find((row) => row.id === opId)
  if (!op) throw new Error('The research operation disappeared before dispatch.')
  const ordinal = (op?.modelCalls?.length ?? 0) + 1
  const contract = researchContractFor(
    context?.session ?? session,
    op.kind,
    context?.targetIds ?? op.targetIds,
    context?.purpose,
  )
  // Every request watches for quiet. One alone under its op says so on the line;
  // one seat of several shares that line with the others, so it tells its panel
  // instead (`OverlapSeat.quiet`), which names the seats. Started as the request
  // goes out, below — never before a refusal above it could throw and leave it
  // ticking.
  let quiet: QuietWatch | null = null
  const onWait = request.onWait ?? waitOnLine()
  const onSearch = request.onSearch
  const prepared: StreamOptions = {
    ...request,
    user: preparedTurn(contract, request.user),
    onWait: (w) => {
      quiet?.waiting(w.ms > 0)
      onWait(w)
    },
    onThinking: (d) => {
      quiet?.heard()
      request.onThinking(d)
    },
    onObject: (o) => {
      quiet?.heard()
      request.onObject(o)
    },
    onOpen: () => {
      quiet?.heard()
      request.onOpen?.()
    },
    ...(onSearch
      ? {
          onSearch: (e) => {
            quiet?.heard()
            onSearch(e)
          },
        }
      : {}),
  }
  // A run with a spending ceiling reserved an 80k-token brief. Full definitions
  // must not silently outgrow that reserve. UTF-8 bytes conservatively bound the
  // supplied text tokens, with 16k left for provider framing. An uncapped manual
  // investigation keeps the larger context allowance below.
  const activeRun = useStore.getState().run
  if (
    activeRun &&
    activeRun.phase !== 'ended' &&
    activeRun.budget.usd !== undefined &&
    new TextEncoder().encode(
      JSON.stringify([prepared.system, prepared.user, prepared.schema.schema]),
    ).length > 64_000
  )
    throw new ModelError(
      'The complete research context exceeds the input reserve for this capped run. No request was sent. Select a smaller target or start an uncapped investigation; no definitions were removed.',
      'request',
    )
  // Refuse before spending when the next request could not fit the durable
  // exact-call boundary. Otherwise a large batch could erase its whole receipt
  // set on reload even though every individual request was valid.
  const retainedChars = JSON.stringify({
    calls: op.modelCalls ?? [],
    contract,
    messages: [prepared.system, prepared.user],
    schema: prepared.schema.schema,
  }).length
  // A request sent alongside others counts theirs as already retained: their
  // receipts have not landed yet, and a check that could not see them would let
  // three together outgrow the allowance each one alone fits inside.
  const ownChars = overlap
    ? JSON.stringify({
        contract,
        messages: [prepared.system, prepared.user],
        schema: prepared.schema.schema,
      }).length
    : 0
  if (retainedChars + (overlap?.reservedChars() ?? 0) > 550_000)
    throw new ModelError(
      'The operation reached its exact-request retention allowance. Start a separate bounded continuation; existing requests remain retained.',
      'request',
    )
  overlap?.reserve(ownChars)
  context?.onPrepared?.(prepared.system, prepared.user)
  quiet = watchQuiet(overlap ? overlap.quiet : sayQuietOnLine)
  return streamObjectsWithReceipt(prepared, {
    id: `${opId}:call:${ordinal}`,
    ordinal,
    record: (receipt) =>
      overlap
        ? overlap.record({ ...receipt, researchContract: contract })
        : useStore
            .getState()
            .appendModelCallReceipt(opId, { ...receipt, researchContract: contract }),
  }).finally(() => quiet?.stop())
}

/**
 * One of several requests in flight under one op at once — the panel's seats.
 *
 * `appendModelCallReceipt` is append-only in ordinal order, and an ordinal is
 * read off the receipts already landed when a request is sent. Requests that
 * overlap would all read the same count, and a receipt that finished out of turn
 * would be refused outright. So a seat's receipt goes to its ledger instead,
 * which appends in the order the seats were sent and numbers each one then. See
 * `seatReceipts`.
 */
interface OverlapSeat {
  /** Retained characters the op's other seats have sent and not yet landed. */
  reservedChars: () => number
  /** This seat's own request passed the allowance and is going out. */
  reserve: (chars: number) => void
  /** This seat's receipt, held until every seat sent before it has landed. */
  record: (receipt: ModelCallReceipt) => void
  /** This seat's quiet, in ms since it last heard anything, or null once it has. */
  quiet: (ms: number | null) => void
}

/**
 * The panel's receipt ledger: seat `i`'s receipt lands after seats `0..i-1` have
 * each either landed theirs or settled without one (a request refused before it
 * was sent leaves none), numbered at landing so the ordinals stay contiguous.
 * `settle` is the seat's `finally`; the adapter has recorded by then.
 */
function seatReceipts(opId: string) {
  const reserved = new Map<number, number>()
  const held = new Map<number, ModelCallReceipt | null>()
  let next = 0
  const land = () => {
    while (held.has(next)) {
      const receipt = held.get(next)!
      held.delete(next)
      reserved.delete(next)
      next++
      if (!receipt) continue
      const op = useStore.getState().session.ops.find((row) => row.id === opId)
      const ordinal = (op?.modelCalls?.length ?? 0) + 1
      useStore
        .getState()
        .appendModelCallReceipt(opId, { ...receipt, id: `${opId}:call:${ordinal}`, ordinal })
    }
  }
  return {
    seat(i: number): Omit<OverlapSeat, 'quiet'> {
      return {
        reservedChars: () => {
          let total = 0
          for (const [k, chars] of reserved) if (k !== i) total += chars
          return total
        },
        reserve: (chars) => void reserved.set(i, chars),
        record: (receipt) => void held.set(i, receipt),
      }
    },
    settle(i: number) {
      if (!held.has(i)) held.set(i, null)
      land()
    },
  }
}

/**
 * Abort the request in flight, if there is one. Idempotent.
 *
 * Split out from `cancelCurrent` so `stopRun` can reach it without the two calling
 * each other: stopping a run has to abort the pass it is inside, or the pod's own
 * tooltip — "the pass in flight is cancelled" — is false for up to a minute while
 * a paid pass the user has already stopped keeps streaming.
 */
export function abortInFlight() {
  // Every Stop reaches here — the thought line's, the feed's, the composer's,
  // the pod's and the space bar's — and a Stop means "and do not start
  // another", so what was up next goes with it. FIRST, before the abort: the
  // pass it ends releases the lock, and that release is when the queue runs.
  useStore.getState().clearUpNext()
  inFlight?.abort()
  inFlight = null
}

export function cancelCurrent() {
  // Stopping the pass in flight and stopping the run are one gesture: a user who
  // presses Stop means "and do not start another". Without this the abort below
  // ends one pass and the loop takes the next one 200 ms later.
  //
  // The import is circular by construction — `run.ts` drives the verbs in this
  // file — and safe because neither side reaches the other during module
  // evaluation: both bindings are called from inside function bodies.
  stopRun()
  abortInFlight()
  // The demo unfurl has no request to abort; it checks a flag between beats.
  cancelDemo()
}

/**
 * The kinds an ordinary pass may return. `conjecture` is absent on purpose: a
 * conjecture is its record, and only `toConjectureNode` — reading the
 * conjecture schema — can build one. A derive verb that wrote the word would
 * land a claim with no prediction under the open-proposal glyph.
 */
const VALID_KINDS = new Set<NodeKind>([
  'mechanism',
  'evidence',
  'assumption',
  'implication',
  'contradiction',
  'gap',
  'analogy',
  'experiment',
  'insight',
  'synthesis',
])
const RUN_VERBS = new Set<RunVerb>([
  'expand',
  'deepen',
  'challenge',
  'diverge',
  'experiment',
  'invert',
])

/** The kinds of evidence a source may be stored as; anything else, `unclear`
 *  included, stores nothing. */
const DESIGNS = new Set<SourceDesign>(SOURCE_DESIGNS)

function str(v: unknown, max = 2000): string {
  return typeof v === 'string' ? v.trim().slice(0, max) : ''
}
function num(v: unknown, dflt: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? clamp(v) : dflt
}

/**
 * Model output → a node we are willing to render. Returns null for junk.
 *
 * `model` is captured once at the top of the op and threaded in, rather than read
 * from the store here: the store's selection can change while a pass is in flight,
 * and a node stamped with the model the user has just switched TO would attribute
 * reasoning to something that never saw the claim.
 *
 * EXPORTED FOR THE EVAL SEAM, and for nothing in the app — no other module calls
 * it. `evals/epistemics` replays recorded model output through the deterministic
 * gates, and this is the admission path a real pass uses. An eval that carried its
 * own copy of these coercions would score its copy: the first edit here would
 * silently stop being measured, which is the one failure a frozen workload exists
 * to catch.
 */
export function toNode(
  raw: unknown,
  parentId: string,
  origin: DeriveOp,
  model: ModelStamp,
  opts: { compact?: boolean } = {},
): ReasonNode | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const retained = textRetention('admission')
  const label = retained.text('label', r.label, 90)
  const claim = retained.text('claim', r.claim)
  if (!label || !claim) return null

  const kindRaw = str(r.kind) as NodeKind
  const kind: NodeKind = VALID_KINDS.has(kindRaw) ? kindRaw : 'mechanism'
  const terminal = r.terminal === true
  const terminalReason = retained.text('terminalReason', r.terminalReason, RESEARCH_ITEM_CHARS)

  return makeNode({
    parentId,
    label,
    claim,
    reasoning: retained.text('reasoning', r.reasoning),
    compact: opts.compact ? true : undefined,
    kind,
    confidence: num(r.confidence, 0.55),
    novelty: num(r.novelty, 0.4),
    tension: num(r.tension, kind === 'contradiction' ? 0.8 : 0.12),
    evidence: retained.list('evidence', r.evidence),
    nextSteps: opts.compact ? [] : retained.list('nextSteps', r.nextSteps),
    terminal,
    terminalReason: terminal ? terminalReason || 'Reasoned out.' : undefined,
    // Kept only when it means something: closed, and closed by the user's own
    // bound rather than by the argument. The schema makes the model emit the
    // field on every node; this is where "reasoned everywhere else" is enforced
    // rather than trusted.
    terminalKind: terminal && r.terminalKind === 'constraint' ? 'constraint' : undefined,
    origin,
    model,
    ...retained.fields(),
  })
}

/**
 * The provider's own classification, for the op record.
 *
 * `run.ts` reads this to decide whether a run can continue — see `endsTheRun`. It
 * used to recover the same fact by matching English against the message below,
 * which meant a copy edit could disable the run's stop condition silently.
 *
 * Anything that is not a `ModelError` is a bug rather than the API, and 'unknown'
 * is the honest classification for it.
 */
const kindOf = (err: unknown): ModelError['kind'] =>
  err instanceof ModelError ? err.kind : 'unknown'

function reportError(err: unknown): string {
  if (err instanceof ModelError) {
    if (err.kind === 'aborted') return 'Stopped.'
    return err.message
  }
  return 'Something went wrong talking to the model.'
}

/* ── The main verb ────────────────────────────────────────────────────── */

export async function runOp(
  op: DeriveOp,
  targetId: string,
  extra?: string,
  opts: {
    mark?: boolean
    /**
     * Suppress the four toasts this verb raises on its own.
     *
     * For a caller that makes many passes in a row: the toast queue keeps three
     * and dismisses them in under four seconds, so a run's toasts are noise to
     * whoever is watching and invisible to whoever walked away. The one thing a
     * run says, it says once, at the end.
     */
    quiet?: boolean
    /** Explicit queue attempts disable SDK retries; other callers keep defaults. */
    maxRetries?: number
    /** A bounded receipt revision can request less output; never raise the ordinary ceiling. */
    maxTokens?: number
    /** A local workflow stops its own request without cancelling an unrelated pass. */
    signal?: AbortSignal
    /** An explicitly queued evidence-led purpose, without ordinary branching quotas. */
    investigationPurpose?: string
    researchContractPurpose?: string
    /**
     * Called with the op id the moment `beginOp` returns, and never otherwise.
     *
     * A caller cannot learn the op id any other way: four paths out of this
     * function return before `beginOp` — the busy guard being the live one, since
     * a user pressing `d` in the gap between a run's passes takes the lock — and
     * a caller that instead indexed `session.ops` after the fact would read
     * `undefined` on every one of them. A callback that either fires or does not
     * is the only unambiguous signal that a call got past its guards.
     */
    /**
     * Run this pass on a specific model instead of the selection. `/replicate`
     * is the one caller: its second pass runs on the other house by definition,
     * and an op row stamped with the selected model would attribute one house's
     * reasoning to the other — the audit's provenance rule, inverted.
     */
    model?: ResolvedModel
    /**
     * Build the prompt as though the target had no children yet.
     *
     * For the blind half of `/replicate`: the context block prints existing
     * children under "do not repeat these", so a second house shown them
     * produces MANUFACTURED divergence — it goes elsewhere because it was told
     * to, not because it reasons differently. Narrowing the prompt's view to the
     * root alone is what makes the two openings comparable. A view, never a
     * store change: the children are still on the graph and still land beside
     * the new ones.
     */
    blindTo?: 'children'
    /**
     * Finish a compact target in this same one-angle pass. Used only when a
     * progressive portfolio reaches a mechanical boundary that requires the
     * ordinary sequential path.
     */
    targetDetail?: boolean
    onStart?: (opId: string) => void
    /** A complete response omitted the detail required to develop a compact target. */
    onIncomplete?: () => void
    /**
     * Called with the failure's classification when the pass ends in an error,
     * and never otherwise.
     *
     * The op row records a failure as prose — `reportError`'s message, written
     * for the person reading the ledger — and the two providers write that prose
     * differently, so a caller deciding whether a failed pass is worth re-running
     * cannot read the kind back out of it. `/replicate` is the one caller: its
     * second pass retries once on a transport failure, and nothing else in the
     * app re-runs a failed pass on its own. (A failed press may OFFER itself
     * again on its toast — `retryOffer` — but that is a press, not a re-run.)
     */
    onFailure?: (kind: ModelError['kind']) => void
    /**
     * `false` withholds the failure toast's "Try again" (`retryOffer`). For a
     * caller whose pass means more than the verb alone: `/replicate`'s first
     * opening, where a second try after the blind half landed would be shown
     * that half's children and go elsewhere because it was told to.
     */
    retry?: false
  } = {},
): Promise<string[]> {
  let s = useStore.getState()
  opts.signal?.throwIfAborted()
  // A paid pass holds the lock. The worked example's unfurl does not: it is
  // stopped below, once this press is known to go somewhere.
  if (lockHeld(s)) {
    s.toast('One thought at a time — let the current branch finish.', 'warn')
    return []
  }
  const target = s.session.nodes[targetId]
  if (!target) return []

  // Demo mode: pre-authored branches unfurl with the same choreography, so the
  // interaction is real even before a key is added. `resolveModel` and not "is
  // there a key": with two providers, a user holding one key with the other
  // provider's model selected must be told WHICH key is missing rather than
  // silently handed the demo.
  const m = opts.model ?? resolveFor(s, 'reason')
  const reserve = !m && RUN_VERBS.has(op as RunVerb) && hasDemoReserve(targetId, op as RunVerb)
  if (!m && !reserve) {
    s.toast(
      `This is the demo exploration. Add your ${providerFor(s, 'reason').name} key to branch anywhere.`,
      'warn',
    )
    s.askForMissingKey('reason')
    return []
  }
  // A verb pressed while the opening unfurls stops it rather than being
  // refused by it — only now, so a press refused above leaves it running.
  if (!s.yieldUnpaid()) {
    s.toast('One thought at a time — let the current branch finish.', 'warn')
    return []
  }
  s = useStore.getState()
  if (!m) return revealDemo(targetId, op as RunVerb)
  const model = stampOf(m.entry)
  const wantsTargetDetail =
    opts.targetDetail === true && target.compact === true && !target.elaboration
  // A Challenge aimed at a conjecture is told where the conjecture said it is
  // weakest, and a Test what the conjecture said would kill it, whoever pressed
  // it — the button or a run's move. An explicit `extra` from the caller wins;
  // the brief itself never changes.
  const turnExtra = extra ?? defaultExtraFor(op, target)

  // One step of history per verb, taken after every guard and before the first
  // mutation. Marking inside `addNodes` instead would give twenty-five undo
  // steps for one Deepen, because the drain threads nodes on one at a time.
  if (opts.mark !== false) s.mark(OP_LABEL[op])

  // Stamped explicitly only when the caller forced a model — the default stamp
  // is the selection, which is right for every other pass.
  const opId = s.beginOp(op, [targetId], { model })
  opts.onStart?.(opId)
  const controller = new AbortController()
  const forwardAbort = () => controller.abort(opts.signal?.reason)
  opts.signal?.addEventListener('abort', forwardAbort, { once: true })
  if (opts.signal?.aborted) forwardAbort()
  inFlight = controller

  const produced: string[] = []
  let admitted = 0
  /** Nodes the response offered past `maxYieldFor(op)`, which were not admitted. */
  let overBrief = 0

  // Drain independently of the stream so arrival pacing is ours, not the API's —
  // and only while somebody could be watching. The lander is what keeps this
  // pass, and therefore a whole run, moving in a hidden tab; see lander.ts.
  const lander = startLander<ReasonNode>((n) => {
    useStore.getState().addNodes([n])
    pulses.mark([targetId])
    produced.push(n.id)
  }, beat)

  let thought = ''

  const patchResponseModel = (answeredBy: ModelStamp) => {
    if (sameModelStamp(answeredBy, model)) return
    for (const id of produced) useStore.getState().patchNode(id, { model: answeredBy })
  }

  try {
    const promptSession =
      opts.blindTo === 'children'
        ? { ...s.session, nodes: { [targetId]: target }, order: [targetId] }
        : s.session
    const res = await streamOpObjects(opId, {
      researchContext: {
        session: promptSession,
        purpose: opts.researchContractPurpose ?? opts.investigationPurpose ?? turnExtra,
      },
      model: m,
      system: buildSystem(s.lens, s.temper, s.session.frame, s.session.brief),
      user:
        opts.investigationPurpose !== undefined
          ? buildInvestigationTurn(promptSession, targetId, op, opts.investigationPurpose)
          : wantsTargetDetail
            ? buildElaboratingUserTurn(promptSession, targetId, op, turnExtra)
            : buildUserTurn(promptSession, targetId, op, turnExtra),
      schema: wantsTargetDetail ? ELABORATING_NODES_SCHEMA : NODES_SCHEMA,
      ...(wantsTargetDetail ? { rootArrayKey: 'nodes' } : {}),
      effort: resolvePassEffort({ pass: 'derive', levels: s.effortLevels }),
      maxTokens:
        opts.maxTokens === undefined || !Number.isFinite(opts.maxTokens)
          ? 20000
          : Math.max(1000, Math.min(20000, Math.floor(opts.maxTokens))),
      ...(opts.maxRetries !== undefined ? { maxRetries: opts.maxRetries } : {}),
      signal: controller.signal,
      onObject: (obj) => {
        // The verb's ceiling, applied HERE because no request can carry it: the
        // brief asks for "3–4 nodes" in prose, and Anthropic's structured outputs
        // support array `minItems` only for 0 and 1 and no `maxItems` at all, so
        // nothing between the ask and this line is enforcing anything. A measured
        // cohort returned five where the brief said four.
        //
        // It matters because the run loop's size stop fires BETWEEN moves, which
        // means the graph is allowed to overshoot `budget.nodes` by one ordinary
        // pass — and an ordinary pass is defined as at most this many nodes. An
        // unbounded admission here is what makes that tolerance a wrong number.
        if (admitted >= maxYieldFor(op)) {
          overBrief++
          return
        }
        const n = toNode(obj, targetId, op, model)
        if (!n) return
        admitted++
        lander.push(n)
      },
      onThinking: (delta) => {
        thought += delta
        // The WHOLE trace, not a tail: the feed streams it as the pass's own
        // message, and the ticker takes the tail it wants itself (Thoughtstream).
        useStore.getState().setThought(thought)
      },
    })

    lander.close()
    await lander.done

    useStore.getState().addSpend(res.spend, opId)
    patchResponseModel(res.spend.model)

    let detailIncomplete = false
    if (wantsTargetDetail && !res.refused) {
      const raw = res.envelope?.targetDetail
      const detail = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null
      const retained = textRetention('admission')
      const reasoning = retained.text('reasoning', detail?.reasoning)
      const nextSteps = retained.list('nextSteps', detail?.nextSteps)
      if (!res.truncated && res.envelope && reasoning) {
        useStore.getState().patchNode(targetId, {
          elaboration: {
            reasoning,
            nextSteps,
            model: res.spend.model,
            opId,
            authoredAt: Date.now(),
            ...retained.fields(),
          },
        })
      } else {
        detailIncomplete = true
      }
    }
    if (wantsTargetDetail && (detailIncomplete || res.refused)) {
      // Without target detail, accepting the children would turn an explicitly
      // compact leaf into an internal node whose reader-facing explanation was
      // never authored. Keep the paid pass in the ledger but fail the graph
      // mutation closed; the run records `incomplete` and falls back.
      for (const id of produced.slice().reverse()) useStore.getState().dropSubtree(id)
      produced.length = 0
      if (detailIncomplete) opts.onIncomplete?.()
    }

    const fieldLosses = produced.flatMap((id) =>
      nodeTextTruncationNotes(useStore.getState().session.nodes[id]!),
    )
    if (wantsTargetDetail)
      fieldLosses.push(...nodeTextTruncationNotes(useStore.getState().session.nodes[targetId]!))
    if (fieldLosses.length) opts.onIncomplete?.()
    if (!opts.quiet) {
      if (res.refused) {
        // With what it wrote before declining, where it wrote anything: a bare
        // "declined" over three nodes that had just landed read as a pass that
        // produced nothing.
        useStore.getState().toast(
          refusalSentence(
            declinedMessage(
              'The model declined this line of reasoning.',
              res,
              res.spend.model,
              // A model handed in did not come from the reasoning route — the
              // blind half of an idea opened twice runs on the other provider's
              // — so re-pointing that route would not move this pass.
              opts.model ? undefined : 'reason',
            ),
            produced.length,
          ),
          'warn',
        )
      } else if (!produced.length) {
        useStore.getState().toast('No new distinct moves found on that branch.', 'info')
      } else if (res.truncated) {
        useStore
          .getState()
          .toast('Response hit the length cap — some branches may be clipped.', 'warn')
      } else if (fieldLosses.length) {
        useStore
          .getState()
          .toast(
            'Some returned text exceeded a retention limit. The retained findings name the missing content.',
            'warn',
          )
      } else if (overBrief > 0) {
        // Said rather than swallowed. The nodes were paid for and are being
        // dropped, so a user who counts what landed against what this verb asks
        // for gets the reason instead of a discrepancy.
        useStore
          .getState()
          .toast(
            `The model offered ${overBrief} move${overBrief === 1 ? '' : 's'} past what this verb asks for; ${overBrief === 1 ? 'it was' : 'they were'} not added.`,
            'warn',
          )
      }
    }

    // The refusal goes onto the op, not only into a toast. A caller reading the
    // ledger afterwards has to be able to tell "the model declined" from "this
    // branch is exhausted", because they are opposite findings and only one of
    // them is about the idea.
    useStore.getState().endOp(opId, produced, {
      thinking: thought,
      model: res.spend.model,
      ...(res.refused ? declinedOf(res) : {}),
    })
    // A refusal, an empty result or a stopped call all leave the graph exactly
    // as it was; a step back that announces it undid something and changes
    // nothing is worse than no step.
    if (!produced.length && opts.mark !== false) useStore.getState().dropMark(OP_LABEL[op])
    return produced
  } catch (err) {
    lander.close()
    await lander.done
    if (wantsTargetDetail && produced.length) {
      for (const id of produced.slice().reverse()) useStore.getState().dropSubtree(id)
      produced.length = 0
    }
    // Whatever the API had already billed is billed, whether the pass finished or
    // the user pressed Stop halfway through it.
    if (err instanceof ModelError && err.spent) {
      useStore.getState().addSpend(err.spent, opId)
      patchResponseModel(err.spent.model)
    }
    const msg = reportError(err)
    useStore.getState().endOp(opId, produced, {
      error: msg,
      errorKind: kindOf(err),
      thinking: thought,
      ...(err instanceof ModelError && err.spent ? { model: err.spent.model } : {}),
    })
    if (!produced.length && opts.mark !== false) useStore.getState().dropMark(OP_LABEL[op])
    // After the op row is written, so a caller reading the ledger from the
    // callback sees the pass it is being told about. Anything that is not a
    // `ModelError` is a bug rather than the API, and 'unknown' is the honest
    // classification for it.
    opts.onFailure?.(kindOf(err))
    // The provider's sentence and then what stayed: a stream cut after two moves
    // leaves two moves on the graph, and the toast is where a reader who pressed
    // one key learns whether the pass did anything at all.
    if (msg !== 'Stopped.' && !opts.quiet)
      useStore
        .getState()
        .toast(
          failureSentence(msg, produced.length),
          'bad',
          retryOffer(op, targetId, extra, opts, produced.length, kindOf(err)),
        )
    // An auth failure reopens the sheet even under `quiet`: it is the one error a
    // caller cannot recover from and the user must act on. On the key of the house
    // this pass actually went to (`askForKey`), which a routed role can make other
    // than the selection's.
    if (err instanceof ModelError && err.kind === 'auth')
      useStore.getState().askForKey(m.entry.provider)
    return produced
  } finally {
    opts.signal?.removeEventListener('abort', forwardAbort)
    if (inFlight === controller) inFlight = null
  }
}

/**
 * Whether a press that failed is worth OFFERING again, as a press on its toast.
 *
 * The provider's sentence already says "try again shortly", and the reader had
 * no way to do it but to find the node and the verb a second time — after a
 * failed opening, behind the seed's folded card. Decided on the error's kind and
 * never its English (invariant 7), and exhaustively, with no `default`, so a new
 * kind stops the build until somebody decides whether a second press can fix it:
 *
 *  - 'network', 'server', 'rate', 'timeout' — the plumbing, not the request: the
 *    same call a beat later may simply work, and the reader decides when the beat
 *    is over. `replicateRetryDue` admits fewer, because it retries ON ITS OWN;
 *    this is a person's press, so a rate limit's minute is theirs to wait out.
 *  - 'auth' opens the key's field instead, 'quota' is not fixed by waiting,
 *    'request', 'storage' and 'unknown' would fail the same way again, and
 *    'aborted' is the reader's own stop.
 *
 * Only a pass that landed nothing: one that landed two moves before the wire
 * dropped has two moves on the graph, and the same press again would branch the
 * node a second time rather than finish the first.
 *
 * Pure and exported for its tests, like `replicateRetryDue`.
 */
export function pressRetryDue(produced: number, failure: ModelError['kind']): boolean {
  if (produced > 0) return false
  switch (failure) {
    case 'network':
    case 'server':
    case 'rate':
    case 'timeout':
      return true
    case 'auth':
    case 'quota':
    case 'request':
    case 'storage':
    case 'aborted':
    case 'unknown':
      return false
  }
}

/**
 * The failure toast's "Try again · est. +$0.12", for `runOp` (D335): the same
 * verb on the same node, with the same words handed to it, as a press — the
 * figure the verb's own button shows (`projectVerbUsd`'s move), so the carried
 * press names its estimate (D227) and is never called anything but one
 * (invariant 5). Nothing re-runs by itself: the toast offers, a person presses
 * (invariant 9), and `runOp`'s guards run again on the new press.
 *
 * Offered only for the six plain verbs pressed plainly. A revise, an outcome or
 * a rebase is one half of a flow that did work around the pass, and the pass
 * again without that work would be a different press; a pass on a forced model,
 * on a caller's signal, or on a queued purpose belongs to the caller that set
 * it up. The press refuses while a pass holds the lock or a run is live — the
 * toast then stays, so the offer can be taken once the way clears — and does
 * nothing once the node, or the exploration it was on, is gone.
 */
function retryOffer(
  op: DeriveOp,
  targetId: string,
  extra: string | undefined,
  opts: Parameters<typeof runOp>[3] & {},
  produced: number,
  failure: ModelError['kind'],
): Toast['action'] | undefined {
  if (opts.retry === false || !pressRetryDue(produced, failure)) return undefined
  if (!RUN_VERBS.has(op as RunVerb)) return undefined
  if (
    opts.model ||
    opts.signal ||
    opts.investigationPurpose !== undefined ||
    opts.researchContractPurpose !== undefined ||
    opts.targetDetail
  )
    return undefined
  const st = useStore.getState()
  const sessionId = st.session.id
  const usd = projectVerbUsd(st, 'move')
  return {
    label: usd === null ? 'Try again' : `Try again · ${pressEstimate(usd)}`,
    run: () => {
      const cur = useStore.getState()
      if (lockHeld(cur) || (cur.run !== null && cur.run.phase !== 'ended')) {
        cur.toast(ONE_THOUGHT_BRANCH, 'warn')
        return false
      }
      if (cur.session.id !== sessionId || !cur.session.nodes[targetId]) return
      void runOp(op, targetId, extra, {
        ...(opts.mark !== undefined ? { mark: opts.mark } : {}),
        ...(opts.blindTo ? { blindTo: opts.blindTo } : {}),
      })
    },
  }
}

/* ── Conjectures ─────────────────────────────────────────────────────── */

/**
 * Propose, about one node, what no source the model knows states — and land
 * only the proposals that carry the whole contract.
 *
 * `runOp`'s shape, with three differences that are the feature. The schema is
 * `CONJECTURES_SCHEMA`, so a conjecture arrives with what it departs from, its
 * leap, a prediction and a kill test. Admission is `toConjectureNode`, which
 * drops a proposal whose prediction the consensus would share, one missing part
 * of its contract, and one that restates a conjecture already here — and every
 * drop is COUNTED onto the op (`Op.admission`), because each was a paid-for
 * proposal and a silent drop would make "3 asked, 1 landed" unexplainable. And
 * there is no demo branch: a pre-authored proposal is exactly the artefact this
 * pass exists not to produce.
 *
 * It never says anything is new. The toasts below report counts; whether anyone
 * has already said a landed conjecture is for a search to report, afterwards.
 */
export async function runConjecture(
  targetId: string,
  opts: {
    mark?: boolean
    /** Suppress this pass's own toasts, for a caller making many passes. */
    quiet?: boolean
    maxRetries?: number
    signal?: AbortSignal
    /** Called with the op id the moment `beginOp` returns, and never otherwise. */
    onStart?: (opId: string) => void
    /** The failure's classification, after the op row is written. */
    onFailure?: (kind: ModelError['kind']) => void
    /**
     * A sweep item the pass is asked to explain — the "Propose what explains
     * this" press. The item is pinned into the sweep block and named after the
     * brief; the brief itself never changes.
     */
    focus?: string
  } = {},
): Promise<string[]> {
  const s = useStore.getState()
  opts.signal?.throwIfAborted()
  // Any pass in flight, the worked example's unfurl included, as a check refuses:
  // the Up next line holds a press met here (`upNextWaits`), and it waits for the
  // unfurl rather than stopping it. A press aimed at one sweep item cannot wait
  // there, and says so by name (D286).
  if (s.busy) {
    s.toast(opts.focus ? EXPLAIN_LOCK_REFUSAL : ONE_THOUGHT_PASS, 'warn')
    return []
  }
  const target = s.session.nodes[targetId]
  if (!target) return []
  // The block and admission read one reach, so a label the turn printed is a
  // label admission can resolve.
  const reach = sweepReach(s.session, targetId, opts.focus)
  if (opts.focus && !reach.items.some((x) => x.item.id === opts.focus)) {
    if (!opts.quiet) {
      s.toast(
        'That sweep item is no longer on the record here, so there is nothing to aim the pass at. Nothing was sent.',
        'warn',
      )
    }
    return []
  }

  const m = resolveFor(s, 'reason')
  if (!m) {
    s.toast(
      `An insight is proposed by a real pass, so the demo has none. Add your ${providerFor(s, 'reason').name} key to propose from here.`,
      'warn',
    )
    // The reasoning route's own field, as `runOp` opens it (D202): the card
    // says "Reasoning runs on <model>", the line the press printed before it.
    s.askForMissingKey('reason')
    return []
  }
  const model = stampOf(m.entry)
  if (opts.mark !== false) s.mark(OP_LABEL.conjecture)

  const opId = s.beginOp('conjecture', [targetId], { model })
  opts.onStart?.(opId)
  const controller = new AbortController()
  const forwardAbort = () => controller.abort(opts.signal?.reason)
  opts.signal?.addEventListener('abort', forwardAbort, { once: true })
  if (opts.signal?.aborted) forwardAbort()
  inFlight = controller

  const produced: string[] = []
  // Taken once, before the response: anchors may name what the graph held when
  // the pass was asked, and duplicates are judged against every conjecture
  // already here plus each one this response lands before it. The admitter is
  // the one `evals/conjectures` replays recordings through.
  const admit = conjectureAdmitter({
    parentId: targetId,
    model,
    resolvable: new Set([...s.session.order, ...reach.items.map((x) => x.item.id)]),
    aliases: reach.aliases,
    prior: conjectureTermsOnGraph(s.session),
    ceiling: maxYieldFor('conjecture'),
    now: Date.now,
  })
  const admission = admit.admission

  const lander = startLander<ReasonNode>((n) => {
    useStore.getState().addNodes([n])
    pulses.mark([targetId])
    produced.push(n.id)
  }, beat)

  let thought = ''
  const patchResponseModel = (answeredBy: ModelStamp) => {
    if (sameModelStamp(answeredBy, model)) return
    for (const id of produced) useStore.getState().patchNode(id, { model: answeredBy })
  }

  try {
    const res = await streamOpObjects(opId, {
      researchContext: { session: s.session },
      model: m,
      system: buildSystem(s.lens, s.temper, s.session.frame, s.session.brief),
      user: buildConjectureTurn(s.session, targetId, opts.focus),
      schema: CONJECTURES_SCHEMA,
      rootArrayKey: 'conjectures',
      effort: resolvePassEffort({ pass: 'derive', levels: s.effortLevels }),
      maxTokens: 20000,
      ...(opts.maxRetries !== undefined ? { maxRetries: opts.maxRetries } : {}),
      signal: controller.signal,
      onObject: (obj) => {
        const n = admit.push(obj)
        if (n) lander.push(n)
      },
      onThinking: (delta) => {
        thought += delta
        useStore.getState().setThought(thought)
      },
    })

    lander.close()
    await lander.done

    useStore.getState().addSpend(res.spend, opId)
    patchResponseModel(res.spend.model)

    if (!opts.quiet) {
      const counts = admissionSentence(admission)
      if (res.refused) {
        useStore
          .getState()
          .toast(
            declinedMessage(
              'The model declined to propose anything here.',
              res,
              res.spend.model,
              'reason',
            ),
            'warn',
          )
      } else if (!produced.length) {
        useStore
          .getState()
          .toast(
            admission.returned
              ? `${STATUS_WORD.noConjectureLanded}: ${counts}.`
              : 'No insight came back. An empty answer is one this pass is allowed to give.',
            'info',
          )
      } else if (res.truncated) {
        useStore
          .getState()
          .toast('Response hit the length cap — an insight may have been cut off.', 'warn')
      } else if (admission.landed < admission.returned || admission.anchorsDropped) {
        useStore.getState().toast(`${counts}.`, 'info')
      }
    }

    useStore.getState().endOp(opId, produced, {
      thinking: thought,
      model: res.spend.model,
      admission,
      ...(res.refused ? declinedOf(res) : {}),
    })
    if (!produced.length && opts.mark !== false) useStore.getState().dropMark(OP_LABEL.conjecture)
    return produced
  } catch (err) {
    lander.close()
    await lander.done
    if (err instanceof ModelError && err.spent) {
      useStore.getState().addSpend(err.spent, opId)
      patchResponseModel(err.spent.model)
    }
    const msg = reportError(err)
    useStore.getState().endOp(opId, produced, {
      error: msg,
      errorKind: kindOf(err),
      thinking: thought,
      // What had arrived before the failure was admitted by the same rules, and
      // its count is as true of a broken pass as of a finished one.
      ...(admission.returned ? { admission } : {}),
      ...(err instanceof ModelError && err.spent ? { model: err.spent.model } : {}),
    })
    if (!produced.length && opts.mark !== false) useStore.getState().dropMark(OP_LABEL.conjecture)
    opts.onFailure?.(kindOf(err))
    if (msg !== 'Stopped.' && !opts.quiet) useStore.getState().toast(msg, 'bad')
    // The card of the house this pass went to, as `runOp` opens it (D193).
    if (err instanceof ModelError && err.kind === 'auth')
      useStore.getState().askForKey(m.entry.provider)
    return produced
  } finally {
    opts.signal?.removeEventListener('abort', forwardAbort)
    if (inFlight === controller) inFlight = null
  }
}

/**
 * One prior-art search's budget. Written once so the request and the sentence in
 * the brief cannot drift — `VERIFY_BUDGET`'s rule. The numbers live in
 * `core/priorArt.ts`, beside the rules that read the search.
 */
export const PRIOR_ART_BUDGET = {
  maxSearches: PRIOR_ART_MAX_SEARCHES,
  maxFetches: PRIOR_ART_MAX_FETCHES,
}

/** The provider tool budget one prior-art search may spend, billed per request.
 *  What its price projection charges for, as `SEARCHES_PER_CHECK` is a check's. */
export const SEARCHES_PER_PRIOR_ART = PRIOR_ART_BUDGET.maxSearches + PRIOR_ART_BUDGET.maxFetches

/**
 * Search for work that already states a conjecture.
 *
 * The link scan's shape — one searched pass on the check role, refused rather
 * than degraded on a model with no search, and no corpus fallback: the question
 * is whether anyone has said this, and the user's own documents saying nothing
 * about it means nothing — with the source check's gate. The model lists pages;
 * `assessPriorArt` gates them against what the search returned, checks their
 * quotes, compares every returned page against the conjecture's terms, and
 * derives the reading. The record lands on the conjecture, stamped with the
 * wording it searched.
 *
 * Never available in the demo, for the source check's reason: a pre-authored
 * "not found" is the same forgery as a pre-authored citation.
 */
export async function runPriorArt(
  targetId: string,
  opts: {
    quiet?: boolean
    maxRetries?: number
    onStart?: (opId: string) => void
  } = {},
): Promise<boolean> {
  const s = useStore.getState()
  if (s.busy) {
    if (!opts.quiet) s.toast('One thought at a time — let the current pass finish.', 'warn')
    return false
  }
  const target = s.session.nodes[targetId]
  if (!target) return false
  if (target.kind !== 'conjecture' || !target.conjecture) {
    if (!opts.quiet) {
      s.toast(
        'A prior-art search reads what an insight departs from, its leap and its prediction, and this node has none of them.',
        'info',
      )
    }
    return false
  }
  const m = resolveFor(s, 'check')
  if (!m) {
    if (!opts.quiet) {
      s.toast(
        `A prior-art search searches the live web, so it needs your ${providerFor(s, 'check').name} key.`,
        'warn',
      )
      s.askForMissingKey('check', PRIOR_ART_NEED)
    }
    return false
  }
  // No search, no null. A pass run without one would come back empty and read
  // as "not found", which is the one confusion this pass exists to prevent.
  if (m.entry.search === 'none') {
    if (!opts.quiet) {
      s.toast(
        `${m.entry.name} has no web search, so there is no record for a prior-art search to come back empty from.`,
        'warn',
      )
    }
    return false
  }

  const opId = s.beginOp('priorArt', [targetId], { model: stampOf(m.entry) })
  opts.onStart?.(opId)
  const controller = new AbortController()
  inFlight = controller
  let thought = ''
  const raw: unknown[] = []
  // Built for the search this model has: the budget is a sentence only where
  // nothing in the request enforces it. The instrument is these exact bytes.
  const system = buildPriorArtSystem(
    m.entry.search,
    m.entry.search === 'results+text' ? undefined : PRIOR_ART_BUDGET,
  )

  try {
    const res = await streamOpObjects(opId, {
      model: m,
      system,
      user: buildPriorArtTurn(s.session, targetId),
      schema: PRIOR_ART_SCHEMA,
      rootArrayKey: 'priorArt',
      // Pinned, and the ask has no field for a temper: no reasoning mood may
      // change how hard this app looks before it prints that it found nothing.
      effort: resolvePassEffort({ pass: 'priorArt', levels: s.effortLevels }),
      maxTokens: 12000,
      ...(opts.maxRetries !== undefined ? { maxRetries: opts.maxRetries } : {}),
      web: PRIOR_ART_BUDGET,
      signal: controller.signal,
      onObject: (obj) => raw.push(obj),
      onThinking: (d) => {
        thought += d
        useStore.getState().setThought(thought)
      },
      onSearch: (ev) => {
        useStore
          .getState()
          .setSearching(
            ev.kind === 'query' ? `searching: ${ev.text}` : `reading: ${hostOf(ev.url)}`,
          )
      },
    })
    // Billed before anything can back out: a search that found nothing still
    // searched, and the searches are inside `res.spend`.
    useStore.getState().addSpend(res.spend, opId)

    if (res.refused) {
      if (!opts.quiet) {
        useStore
          .getState()
          .toast(
            declinedMessage(
              'The model declined to search for this.',
              res,
              res.spend.model,
              'check',
            ),
            'warn',
          )
      }
      useStore.getState().endOp(opId, [], { thinking: thought, ...declinedOf(res) })
      return false
    }

    const record = assessPriorArt({
      raw,
      summary: res.envelope?.summary,
      search: res.search,
      truncated: res.truncated,
      node: target,
      // The model that answered, from the response — never the row requested.
      model: res.spend.model,
      instrument: priorArtInstrument(system),
      now: Date.now(),
    })
    const current = useStore.getState().session.nodes[targetId]
    if (current?.conjecture) {
      useStore.getState().patchNode(targetId, {
        conjecture: withPriorArt(current.conjecture, { ...record, opId }),
        lastPulse: Date.now(),
      })
    }
    useStore.getState().endOp(opId, [], { thinking: thought, model: res.spend.model })
    if (!opts.quiet) {
      const dropped = record.dropped
        ? ` · ${record.dropped} named page${record.dropped === 1 ? '' : 's'} the search never returned, deleted`
        : ''
      const charge = chargeSuffix(opId)
      useStore
        .getState()
        .toast(
          `${PRIOR_ART_READING_LABEL[record.reading]}${dropped}${charge.text}.`,
          record.reading === 'unsound' || charge.over ? 'warn' : 'info',
        )
    }
    return true
  } catch (err) {
    if (err instanceof ModelError && err.spent) useStore.getState().addSpend(err.spent, opId)
    const msg = reportError(err)
    useStore.getState().endOp(opId, [], {
      error: msg,
      errorKind: kindOf(err),
      thinking: thought,
      ...(err instanceof ModelError && err.spent ? { model: err.spent.model } : {}),
    })
    if (msg !== 'Stopped.' && !opts.quiet) useStore.getState().toast(msg, 'bad')
    // The card of the house this pass went to, as `runOp` opens it (D193).
    if (err instanceof ModelError && err.kind === 'auth')
      useStore.getState().askForKey(m.entry.provider)
    return false
  } finally {
    if (inFlight === controller) inFlight = null
  }
}

/** One sweep's budget, written once for the request and the brief's sentence. */
export const SWEEP_BUDGET = { maxSearches: SWEEP_MAX_SEARCHES, maxFetches: SWEEP_MAX_FETCHES }

/** The provider tool budget one sweep may spend, billed per request. */
export const SEARCHES_PER_SWEEP = SWEEP_BUDGET.maxSearches + SWEEP_BUDGET.maxFetches

/**
 * Sweep where the literature on one node's subject is unsettled.
 *
 * The prior-art search's shape on the check role — its own system prompt, a
 * pinned effort, a refusal on a model with no search and no corpus fallback —
 * with the source check's gate and quote check, applied by `assessSweep`. The
 * record lands on the SESSION, keyed to the node: its items are reportage, and
 * none of them becomes a node. A conjecture pass anywhere in the node's
 * dependency cone reads them, quoted and framed as data.
 *
 * Never available in the demo: pre-authored "what the field finds unsettled"
 * would be quotes nobody searched for, in the one block a reasoning pass is told
 * came from pages a search returned.
 */
export async function runSweep(
  targetId: string,
  opts: { quiet?: boolean; maxRetries?: number; onStart?: (opId: string) => void } = {},
): Promise<boolean> {
  const s = useStore.getState()
  if (s.busy) {
    if (!opts.quiet) s.toast('One thought at a time — let the current pass finish.', 'warn')
    return false
  }
  const target = s.session.nodes[targetId]
  if (!target) return false
  const m = resolveFor(s, 'check')
  if (!m) {
    if (!opts.quiet) {
      s.toast(
        `A field sweep searches the live web, so it needs your ${providerFor(s, 'check').name} key.`,
        'warn',
      )
      s.askForMissingKey('check', SWEEP_NEED)
    }
    return false
  }
  if (m.entry.search === 'none') {
    if (!opts.quiet) {
      s.toast(
        `${m.entry.name} has no web search, so there is no literature for a field sweep to read.`,
        'warn',
      )
    }
    return false
  }

  const opId = s.beginOp('sweep', [targetId], { model: stampOf(m.entry) })
  opts.onStart?.(opId)
  const controller = new AbortController()
  inFlight = controller
  let thought = ''
  const raw: unknown[] = []
  const system = buildSweepSystem(
    m.entry.search,
    m.entry.search === 'results+text' ? undefined : SWEEP_BUDGET,
  )

  try {
    const res = await streamOpObjects(opId, {
      model: m,
      system,
      user: buildSweepTurn(s.session, targetId),
      schema: SWEEP_SCHEMA,
      rootArrayKey: 'items',
      effort: resolvePassEffort({ pass: 'sweep', levels: s.effortLevels }),
      maxTokens: 12000,
      ...(opts.maxRetries !== undefined ? { maxRetries: opts.maxRetries } : {}),
      web: SWEEP_BUDGET,
      signal: controller.signal,
      onObject: (obj) => raw.push(obj),
      onThinking: (d) => {
        thought += d
        useStore.getState().setThought(thought)
      },
      onSearch: (ev) => {
        useStore
          .getState()
          .setSearching(
            ev.kind === 'query' ? `searching: ${ev.text}` : `reading: ${hostOf(ev.url)}`,
          )
      },
    })
    useStore.getState().addSpend(res.spend, opId)

    if (res.refused) {
      if (!opts.quiet) {
        useStore
          .getState()
          .toast(
            declinedMessage('The model declined to sweep this.', res, res.spend.model, 'check'),
            'warn',
          )
      }
      useStore.getState().endOp(opId, [], { thinking: thought, ...declinedOf(res) })
      return false
    }

    const sweep = assessSweep({
      raw,
      search: res.search,
      truncated: res.truncated,
      id: uid('sv'),
      node: target,
      model: res.spend.model,
      instrument: sweepInstrument(system),
      now: Date.now(),
    })
    useStore.getState().addSweep({ ...sweep, opId })
    useStore.getState().endOp(opId, [], { thinking: thought, model: res.spend.model })
    if (!opts.quiet) {
      const deleted = sweep.dropped + sweep.unquoted
      const charge = chargeSuffix(opId)
      useStore
        .getState()
        .toast(
          `${sweepSentence(sweep)}${deleted ? ` ${deleted} item${deleted === 1 ? '' : 's'} deleted: a page the search never returned, or a quote not on its page.` : ''}${charge.text}`,
          sweep.partial || sweep.failures?.length || charge.over ? 'warn' : 'info',
        )
    }
    return true
  } catch (err) {
    if (err instanceof ModelError && err.spent) useStore.getState().addSpend(err.spent, opId)
    const msg = reportError(err)
    useStore.getState().endOp(opId, [], {
      error: msg,
      errorKind: kindOf(err),
      thinking: thought,
      ...(err instanceof ModelError && err.spent ? { model: err.spent.model } : {}),
    })
    if (msg !== 'Stopped.' && !opts.quiet) useStore.getState().toast(msg, 'bad')
    // The card of the house this pass went to, as `runOp` opens it (D193).
    if (err instanceof ModelError && err.kind === 'auth')
      useStore.getState().askForKey(m.entry.provider)
    return false
  } finally {
    if (inFlight === controller) inFlight = null
  }
}

/**
 * Put what a conjecture departs from on the graph, beside it — free, and once.
 *
 * The counterpart of the prior-art search. That search asks whether the
 * departure has already been said; this makes the thing departed from a node
 * the ordinary source check can aim at, which asks whether specialists actually
 * hold it. Pressed twice, it goes to the node already landed rather than landing
 * a second copy. Returns the node's id, or null when nothing landed.
 */
export function landDepartsFrom(conjectureId: string): string | null {
  const s = useStore.getState()
  const cj = s.session.nodes[conjectureId]
  if (!cj || cj.kind !== 'conjecture' || !cj.conjecture) return null
  // Going to the node already landed is a selection, and a selection works under
  // a pass as it always has (D203's free doors).
  const existing = consensusNodeOf(s.session, conjectureId)
  if (existing) {
    s.revealNode(existing.id)
    s.select(existing.id)
    return existing.id
  }
  // Landing one is a step of history, and a step taken while a pass is landing
  // nodes would split that pass's own: its undo would take the expectation and
  // half the pass's nodes, and an empty pass could no longer withdraw its step.
  // So it refuses under the lock, and says which press (D286).
  if (s.busy) {
    s.toast(DEPARTS_LOCK_REFUSAL, 'warn')
    return null
  }
  const node = toConsensusNode(cj, Date.now())
  if (!node) return null
  s.mark('Expectation it departs from')
  s.addNodes([node])
  if (!useStore.getState().session.nodes[node.id]) {
    useStore.getState().dropMark('Expectation it departs from')
    return null
  }
  pulses.mark([node.id])
  return node.id
}

/**
 * Propose an executable test of a conjecture: one computation — a simulation, a
 * search for a counterexample, a calculation, a re-analysis — whose evaluator
 * can tell the prediction apart from the expectation it departs from (D283).
 *
 * A pass on the `reason` role with a pinned brief of its own, and it lands
 * nothing on the graph. Its product is a contract in Research Lab's workspace,
 * unbound and unrun: every file `model-proposed`, the evaluator an
 * `unreviewed-proposal`, and the two hypotheses written from the conjecture's
 * own registered words by `core/executableConjecture.ts`, never by the model.
 * Running it is Research Lab's own press, in the sandboxed local runner; the
 * receipt, not this pass, says how the prediction fared, and nothing a receipt
 * says becomes the node's outcome.
 *
 * Pressed only. No run reaches it and no cue offers it: finishing an execution
 * authorizes no spending, and no result becomes a paid call (D87, D92). Every
 * refusal is decided before a request is sent. Returns the contract's id, or
 * null when nothing landed.
 */
export async function runExecutableTest(
  targetId: string,
  opts: {
    quiet?: boolean
    maxRetries?: number
    onStart?: (opId: string) => void
  } = {},
): Promise<string | null> {
  const s = useStore.getState()
  // Named, because it cannot wait on the Up next line printed just below it: no
  // dispatcher but this press reaches the pass (D283, D286).
  if (s.busy) {
    if (!opts.quiet) s.toast(EXECUTABLE_LOCK_REFUSAL, 'warn')
    return null
  }
  const target = s.session.nodes[targetId]
  if (!target) return null
  const refusal = executableRefusal(s.session, target)
  if (refusal) {
    if (!opts.quiet) s.toast(`${refusal} Nothing was sent.`, 'info')
    return null
  }
  const m = resolveFor(s, 'reason')
  if (!m) {
    if (!opts.quiet) {
      s.toast(
        `An executable test is written by a real pass, so the demo has none. Add your ${providerFor(s, 'reason').name} key to propose one from here.`,
        'warn',
      )
      s.askForMissingKey('reason', EXECUTABLE_NEED)
    }
    return null
  }

  const opId = s.beginOp('research', [targetId], { model: stampOf(m.entry) })
  // Filed as `research` (D283): until its receipt and its contract say what it
  // is, only the press can, and the live line and the announcer read it (D289).
  markExecutableTest(opId)
  opts.onStart?.(opId)
  // Read after `beginOp`, which re-mints a demo's id the moment it spends. The
  // contract names the exploration it lands in, and the node as it was SENT: a
  // rewrite while the pass is out is a rewrite after the test was written.
  const sessionId = useStore.getState().session.id
  const sent = target
  const controller = new AbortController()
  inFlight = controller
  let thought = ''
  const raw: unknown[] = []

  try {
    const res = await streamOpObjects(opId, {
      // The test-writer never learns how this conjecture has fared. The turn
      // carries no result, and the conjecture's own recorded one is withheld from
      // the shared research contract too: a test written by a model that had seen
      // the answer would be written to it. The contract records exactly this.
      researchContext: {
        session: {
          ...s.session,
          nodes: { ...s.session.nodes, [targetId]: { ...target, outcome: undefined } },
        },
        purpose: EXECUTABLE_PURPOSE,
      },
      model: m,
      system: EXECUTABLE_SYSTEM,
      user: buildExecutableTurn(s.session, targetId),
      schema: EXECUTABLE_SCHEMA,
      rootArrayKey: 'tests',
      // Pinned, and the ask has no field for a temper: no reasoning mood reaches
      // the instrument a receipt is read against.
      effort: resolvePassEffort({ pass: 'executable', levels: s.effortLevels }),
      maxTokens: 20000,
      ...(opts.maxRetries !== undefined ? { maxRetries: opts.maxRetries } : {}),
      signal: controller.signal,
      onObject: (obj) => raw.push(obj),
      onThinking: (d) => {
        thought += d
        useStore.getState().setThought(thought)
      },
    })
    useStore.getState().addSpend(res.spend, opId)
    const toast = (text: string, tone: 'info' | 'warn') => {
      if (!opts.quiet) useStore.getState().toast(text, tone)
    }

    if (res.refused) {
      useStore.getState().endOp(opId, [], {
        thinking: thought,
        model: res.spend.model,
        ...declinedOf(res),
      })
      toast(
        declinedMessage(
          'The model declined to write a test for this.',
          res,
          res.spend.model,
          'reason',
        ),
        'warn',
      )
      return null
    }
    useStore.getState().endOp(opId, [], { thinking: thought, model: res.spend.model })

    const summary = typeof res.envelope?.summary === 'string' ? res.envelope.summary.trim() : ''
    if (!raw.length) {
      toast(
        res.truncated
          ? 'The response hit the length cap before a whole test arrived, so nothing was added.'
          : `No executable test came back${summary ? `: ${summary.replace(/\.$/, '')}` : ''}. An empty answer is one this pass is allowed to give.`,
        res.truncated ? 'warn' : 'info',
      )
      return null
    }
    const read = readExecutableProposal(raw[0], sent.conjecture!.move === 'transplant')
    if ('refused' in read) {
      toast(`The proposed test was not added: ${read.refused}.`, 'warn')
      return null
    }
    const live = useStore.getState()
    if (live.session.id !== sessionId) {
      toast(
        'The exploration changed before the test arrived, so it was not added to either.',
        'warn',
      )
      return null
    }
    const contract = conjectureContract({
      id: opId,
      sessionId,
      node: sent,
      proposal: read.proposal,
      // The model that answered, from the response — never the row requested.
      model: res.spend.model,
    })
    let next: ReturnType<typeof withConjectureContract> | null = null
    try {
      next = withConjectureContract(
        live.session.research?.experiments ?? emptyExperimentWorkspace(),
        contract,
      )
    } catch {
      next = null
    }
    if (!next) {
      toast(
        'The Research Lab workspace could not hold the proposed test, so nothing was added.',
        'warn',
      )
      return null
    }
    // The store says why itself when it refuses a workspace.
    if (!live.setExperimentWorkspace(next)) return null
    const notes = [
      ...read.notes,
      ...(raw.length > 1
        ? [
            `${raw.length - 1} further proposal${raw.length === 2 ? ' was' : 's were'} set aside: one test a press`,
          ]
        : []),
    ]
    toast(
      `An executable test is in Research Lab → Tests: ${contract.spec.title}. Nothing has run it, and a model wrote its evaluator, which nobody has reviewed.${notes.length ? ` (${notes.join('; ')}.)` : ''}`,
      'info',
    )
    return opId
  } catch (err) {
    if (err instanceof ModelError && err.spent) useStore.getState().addSpend(err.spent, opId)
    const msg = reportError(err)
    useStore.getState().endOp(opId, [], {
      error: msg,
      errorKind: kindOf(err),
      thinking: thought,
      ...(err instanceof ModelError && err.spent ? { model: err.spent.model } : {}),
    })
    if (msg !== 'Stopped.' && !opts.quiet) useStore.getState().toast(msg, 'bad')
    // The card of the house this pass went to, as `runOp` opens it (D193).
    if (err instanceof ModelError && err.kind === 'auth')
      useStore.getState().askForKey(m.entry.provider)
    return null
  } finally {
    if (inFlight === controller) inFlight = null
  }
}

/* ── Shared-context autonomous portfolios ────────────────────────────── */

export interface PortfolioPartResult {
  kind: RunVerb
  produced: string[]
  outcome: RunMove['outcome']
  note?: string
}

export interface PortfolioResult {
  opId: string | null
  parts: PortfolioPartResult[]
  errorKind?: ModelError['kind']
  /** One physical request failed, however many logical angles it owned. */
  requestFailed: boolean
  /** One physical refusal event, however many logical rows report it. */
  refused: boolean
  /** The rest of this run should use the proven sequential execution shape. */
  incomplete: boolean
}

interface PortfolioDraft {
  produced: string[]
  complete: boolean
  invalid: boolean
  /**
   * Why THIS angle is short, which the packet-wide counters cannot say.
   *
   * The note used to be selected from those counters, so one angle overrunning
   * its ceiling put the overrun sentence on every other incomplete angle in the
   * packet — including one that was invalid for a duplicate or a malformed node,
   * which is a different failure with a different fix. `run.ts` persists each
   * part's note on its `RunMove`, so that misattribution outlived the run.
   */
  overBrief: boolean
  droppedForSize: boolean
}

const sameModelStamp = (a: ModelStamp, b: ModelStamp): boolean =>
  a.provider === b.provider && a.id === b.id

const emptyPortfolio = (verbs: readonly RunVerb[]): PortfolioResult => ({
  opId: null,
  parts: verbs.map((kind) => ({
    kind,
    produced: [],
    outcome: 'error',
    note: 'The portfolio pass did not start.',
  })),
  requestFailed: false,
  refused: false,
  incomplete: false,
})

/**
 * Execute two or three compatible run verbs over one immutable target snapshot.
 *
 * This is intentionally separate from `runOp`: manual buttons and sequential
 * runs retain their exact one-verb request contract. A portfolio owns one op,
 * one thought trace, one response-model stamp, and one spend write; its parts
 * remain separate logical moves through the result below.
 */
export async function runPortfolio(
  targetId: string,
  verbs: readonly RunVerb[],
  opts: {
    mark?: boolean
    quiet?: boolean
    compactChildren?: boolean
    /** Hard admission headroom. The planner reserves against the same bound. */
    maxNodes?: number
    onStart?: (opId: string) => void
    onFailure?: (kind: ModelError['kind']) => void
  } = {},
): Promise<PortfolioResult> {
  const requested = [...verbs]
  const s = useStore.getState()
  if (s.busy) {
    s.toast('One thought at a time — let the current branch finish.', 'warn')
    return emptyPortfolio(requested)
  }
  const target = s.session.nodes[targetId]
  if (!target) return emptyPortfolio(requested)
  if (
    requested.length < 2 ||
    requested.length > 3 ||
    new Set(requested).size !== requested.length
  ) {
    if (!opts.quiet) s.toast('A portfolio needs two or three distinct reasoning angles.', 'warn')
    return emptyPortfolio(requested)
  }

  const m = resolveFor(s, 'reason')
  if (!m) {
    s.toast(
      `This run needs your ${providerFor(s, 'reason').name} key for its reasoning portfolio.`,
      'warn',
    )
    s.askForMissingKey('reason')
    return emptyPortfolio(requested)
  }
  const requestedModel = stampOf(m.entry)
  const wantsTargetDetail = target.compact === true && !target.elaboration
  const extras = Object.fromEntries(
    requested.flatMap((verb) => {
      const extra = defaultExtraFor(verb, target)
      return extra ? [[verb, extra]] : []
    }),
  ) as Partial<Record<RunVerb, string>>
  const capacity = Number.isFinite(opts.maxNodes)
    ? Math.max(0, Math.floor(opts.maxNodes ?? 0))
    : Number.POSITIVE_INFINITY

  if (opts.mark !== false) s.mark(OP_LABEL.portfolio)
  const opId = s.beginOp('portfolio', [targetId], {
    model: requestedModel,
    portfolio: {
      parts: requested.map((kind) => ({ kind, targetId, produced: [] })),
    },
  })
  opts.onStart?.(opId)
  const controller = new AbortController()
  inFlight = controller

  const drafts = new Map<RunVerb, PortfolioDraft>(
    requested.map((verb) => [
      verb,
      { produced: [], complete: false, invalid: false, overBrief: false, droppedForSize: false },
    ]),
  )
  const allProduced: string[] = []
  const exactNodes = new Set<string>()
  let admitted = 0
  let droppedForSize = 0
  /** Nodes offered past a verb's `maxYieldFor`, across every angle in the packet. */
  let overBrief = 0
  let nextAngle = 0
  let packetMalformed = false
  let thought = ''

  const portfolioRecord = (
    elaboratedTargetId?: string,
    landed?: ReadonlySet<string>,
  ): PortfolioRecord => ({
    parts: requested.map((kind) => ({
      kind,
      targetId,
      produced: (drafts.get(kind)?.produced ?? []).filter((id) => !landed || landed.has(id)),
    })),
    ...(elaboratedTargetId ? { elaboratedTargetId } : {}),
    ...(droppedForSize > 0 ? { droppedForSize } : {}),
  })

  // The same lander as `runOp`'s — pacing only for a watcher, a wake instead of
  // a poll; see lander.ts — with the packet's per-node bookkeeping in the
  // landing step.
  const lander = startLander<ReasonNode>((n) => {
    useStore.getState().addNodes([n])
    pulses.mark([targetId])
    allProduced.push(n.id)
    const landed = new Set(allProduced)
    useStore
      .getState()
      .patchPortfolioOp(opId, allProduced.slice(), portfolioRecord(undefined, landed))
  }, beat)

  const admitAngle = (raw: unknown) => {
    if (!raw || typeof raw !== 'object') {
      packetMalformed = true
      return
    }
    const record = raw as Record<string, unknown>
    const verb = str(record.verb) as RunVerb
    const expected = requested[nextAngle]
    if (!expected || verb !== expected || !RUN_VERBS.has(verb)) {
      packetMalformed = true
      return
    }
    const draft = drafts.get(verb)!
    draft.complete = true
    nextAngle++
    if (!Array.isArray(record.nodes)) {
      draft.invalid = true
      packetMalformed = true
      return
    }

    // Per ANGLE, not per packet: each verb carries its own ceiling and the
    // packet reserved capacity against the sum of them, so one angle overrunning
    // is what spends a later angle's reserved room. Counted here rather than off
    // `draft.produced`, which the graph-size drop below also holds back.
    /**
     * The DISTINCT moves this angle offered, which is neither what the graph had
     * room for nor what the packet has already landed.
     *
     * Two sets, because they answer different questions. `exactNodes` is what
     * reached the graph and stops a second visible copy of it; this one is what
     * this angle put forward and is what the ceiling is measured against — the
     * ceiling being a property of the response, so a node a spent allowance
     * turns away still spent one of the angle's four.
     *
     * It also has to be per-angle and populated on OFFER rather than on
     * admission. Registering only on admission left repeats invisible once
     * capacity ran out — five identical nodes against a spent allowance read as
     * four size drops and an overrun, blaming the brief for an angle that offered one
     * move five times — while registering them packet-wide would put a node
     * nobody can see in the way of a later angle's identical, admissible one.
     *
     * The ceiling reads `size` rather than a counter beside it, for the reason
     * this whole change exists: a second copy of a number is a number that can
     * drift from the first.
     */
    const angleOffers = new Set<string>()
    for (const rawNode of record.nodes) {
      const n = toNode(rawNode, targetId, verb, requestedModel, {
        compact: opts.compactChildren,
      })
      if (!n) {
        draft.invalid = true
        packetMalformed = true
        continue
      }
      // Exact duplicates are mechanically knowable. Semantic similarity remains
      // a quality-evaluation question and never becomes a stop or ranking signal.
      const key = `${n.label.trim().toLocaleLowerCase()}\n${n.claim.trim().toLocaleLowerCase()}`
      if (exactNodes.has(key) || angleOffers.has(key)) {
        draft.invalid = true
        packetMalformed = true
        continue
      }
      // Checked BEFORE the size drop so the two stay distinguishable: this angle
      // left its brief, which is the model's doing, where a size drop is the
      // graph running out of room and is ours. Counting an overrun node as
      // dropped-for-size would file the packet's own arithmetic error under a
      // budget that was working correctly.
      if (angleOffers.size >= maxYieldFor(verb)) {
        overBrief++
        draft.invalid = true
        draft.overBrief = true
        continue
      }
      angleOffers.add(key)
      if (admitted >= capacity) {
        droppedForSize++
        draft.invalid = true
        draft.droppedForSize = true
        continue
      }
      // Registered at ADMISSION, not on arrival. The set exists to keep a second
      // copy of a node off the graph, so a node that never reached the graph must
      // not occupy it: an overrun fifth node, or one dropped for size, would
      // otherwise reject a later angle's identical-but-admissible move as a
      // duplicate of something nobody can see, and mark that angle invalid too.
      exactNodes.add(key)
      admitted++
      draft.produced.push(n.id)
      lander.push(n)
    }
  }

  const patchResponseModel = (model: ModelStamp) => {
    if (sameModelStamp(model, requestedModel)) return
    for (const id of allProduced) useStore.getState().patchNode(id, { model })
  }

  const completedParts = (
    mode: 'complete' | 'refused' | 'error',
    note?: string,
  ): PortfolioPartResult[] =>
    requested.map((kind) => {
      const draft = drafts.get(kind)!
      // Refusal belongs to the one physical request, not to whichever angle was
      // streaming when the provider stopped it. Completed objects remain on the
      // graph as partial output, but none is allowed to turn a refused request
      // into a successful logical result.
      if (mode === 'refused') {
        return {
          kind,
          produced: draft.produced.slice(),
          outcome: 'refused',
          ...(note ? { note } : {}),
        }
      }
      if (draft.complete && !draft.invalid) {
        return {
          kind,
          produced: draft.produced.slice(),
          outcome: draft.produced.length ? 'nodes' : 'nothing',
        }
      }
      if (draft.complete || mode === 'complete') {
        return {
          kind,
          produced: draft.produced.slice(),
          outcome: 'incomplete',
          note: draft.overBrief
            ? 'The response offered more moves than this angle asks for; the extra ones were not added.'
            : draft.droppedForSize
              ? 'The response exceeded the remaining graph-size allowance.'
              : 'This angle did not arrive as one complete, valid group.',
        }
      }
      return {
        kind,
        produced: [],
        outcome: 'error',
        ...(note ? { note } : {}),
      }
    })

  try {
    const res = await streamOpObjects(opId, {
      model: m,
      system: buildSystem(s.lens, s.temper, s.session.frame, s.session.brief),
      user: buildPortfolioTurn(s.session, targetId, requested, {
        compact: opts.compactChildren,
        targetDetail: wantsTargetDetail,
        // A packeted Challenge on a conjecture carries what a pressed one does;
        // before this, a run's default move reached a conjecture without it.
        ...(Object.keys(extras).length ? { extras } : {}),
      }),
      schema: portfolioSchema(requested, {
        compact: opts.compactChildren,
        targetDetail: wantsTargetDetail,
      }),
      rootArrayKey: 'angles',
      // The whole packet at one level, Reasoning's, as each of its angles would
      // take alone: no verb and no temper moves a level (D313).
      effort: resolvePassEffort({ pass: 'derive', levels: s.effortLevels }),
      maxTokens: 20000,
      signal: controller.signal,
      onObject: admitAngle,
      onThinking: (delta) => {
        thought += delta
        useStore.getState().setThought(thought)
      },
    })

    lander.close()
    await lander.done
    useStore.getState().addSpend(res.spend, opId)
    patchResponseModel(res.spend.model)

    if (res.refused && allProduced.length) {
      // A request-level refusal authorises no logical angle. Objects streamed
      // before that terminal signal are provisional transport output, not a
      // partial success. Remove them before the final op/run account is written.
      for (const id of allProduced.slice().reverse()) useStore.getState().dropSubtree(id)
      allProduced.length = 0
      for (const draft of drafts.values()) draft.produced.length = 0
      useStore.getState().patchPortfolioOp(opId, [], portfolioRecord())
    }

    let elaboratedTargetId: string | undefined
    let detailIncomplete = false
    if (wantsTargetDetail && !res.refused) {
      const raw = res.envelope?.targetDetail
      const detail = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null
      const retained = textRetention('admission')
      const reasoning = retained.text('reasoning', detail?.reasoning)
      const nextSteps = retained.list('nextSteps', detail?.nextSteps)
      if (!res.truncated && reasoning) {
        useStore.getState().patchNode(targetId, {
          elaboration: {
            reasoning,
            nextSteps,
            model: res.spend.model,
            opId,
            authoredAt: Date.now(),
            ...retained.fields(),
          },
        })
        elaboratedTargetId = targetId
      } else {
        detailIncomplete = true
      }
    } else if (wantsTargetDetail) {
      detailIncomplete = true
    }

    const parts = completedParts(res.refused ? 'refused' : 'complete', res.refusalNote)
    const incomplete =
      res.truncated ||
      !res.envelope ||
      res.refused ||
      packetMalformed ||
      droppedForSize > 0 ||
      overBrief > 0 ||
      detailIncomplete ||
      allProduced.some(
        (id) => nodeTextTruncationNotes(useStore.getState().session.nodes[id]!).length > 0,
      ) ||
      (elaboratedTargetId !== undefined &&
        nodeTextTruncationNotes(useStore.getState().session.nodes[elaboratedTargetId]!).length >
          0) ||
      parts.some((part) => part.outcome === 'incomplete')
    useStore.getState().endOp(opId, allProduced, {
      thinking: thought,
      model: res.spend.model,
      portfolio: portfolioRecord(elaboratedTargetId),
      ...(res.refused ? declinedOf(res) : {}),
    })
    if (!allProduced.length && opts.mark !== false) useStore.getState().dropMark(OP_LABEL.portfolio)

    if (!opts.quiet && res.refused) {
      useStore
        .getState()
        .toast(
          declinedMessage(
            'The model declined this reasoning portfolio.',
            res,
            res.spend.model,
            'reason',
          ),
          'warn',
        )
    } else if (!opts.quiet && incomplete) {
      useStore
        .getState()
        .toast('The shared pass was incomplete. This run will continue one move at a time.', 'warn')
    }
    return {
      opId,
      parts,
      requestFailed: false,
      refused: res.refused,
      incomplete,
    }
  } catch (err) {
    lander.close()
    await lander.done
    if (err instanceof ModelError && err.spent) {
      useStore.getState().addSpend(err.spent, opId)
      patchResponseModel(err.spent.model)
    }
    const msg = reportError(err)
    const errorKind = kindOf(err)
    const parts = completedParts('error', msg)
    useStore.getState().endOp(opId, allProduced, {
      error: msg,
      errorKind,
      thinking: thought,
      ...(err instanceof ModelError && err.spent ? { model: err.spent.model } : {}),
      portfolio: portfolioRecord(),
    })
    if (!allProduced.length && opts.mark !== false) useStore.getState().dropMark(OP_LABEL.portfolio)
    opts.onFailure?.(errorKind)
    if (msg !== 'Stopped.' && !opts.quiet) useStore.getState().toast(msg, 'bad')
    if (err instanceof ModelError && err.kind === 'auth')
      useStore.getState().askForKey(m.entry.provider)
    return {
      opId,
      parts,
      errorKind,
      requestFailed: true,
      refused: false,
      incomplete: packetMalformed || parts.some((part) => part.outcome === 'incomplete'),
    }
  } finally {
    if (inFlight === controller) inFlight = null
  }
}

/* ── Seeding a new exploration ────────────────────────────────────────── */

/**
 * The beat before /replicate's one retry.
 *
 * Long enough for the blip it exists for — a dropped connection, a 5xx — to
 * clear; short enough that the pass starts while the toast announcing it is
 * still on screen. Not a backoff schedule: there is exactly one retry, so there
 * is nothing to back off from.
 */
const REPLICATE_RETRY_MS = 1500

/**
 * Whether /replicate's second opening is worth one more pass, after a first
 * attempt that landed `produced` nodes and ended in `failure` — null for a pass
 * that did not throw.
 *
 * ONE retry, taken by `runSeed`, and only when both halves hold:
 *
 *  - The pass landed NOTHING. The blind prompt shows the root alone — that is
 *    the point of it — so a rerun cannot see what a half-finished stream already
 *    put down and would open the same doors twice. A pass that died with nodes
 *    on the graph keeps its partial opening, and the closing toast reports the
 *    arithmetic as it stands.
 *  - The failure is transport, not verdict: 'network' is a connection that
 *    dropped, 'server' is the API answering 5xx, and both describe the plumbing
 *    rather than the request — the same call a beat later may simply work.
 *    Nothing else qualifies. 'auth' needs the user's hand, 'rate' clears on the
 *    limiter's clock rather than ours, 'request' and 'unknown' would fail the
 *    same way again, 'aborted' is the user's own stop honoured, and 'timeout'
 *    already spent the whole of `PASS_TIMEOUT_MS` deciding the request was
 *    hung — named rather than quoted in minutes, because that figure is
 *    recalibrated from what real passes do and has already moved once.
 *
 * The retry this admits is a new pass and bills like one — it goes back through
 * `runOp`, which opens its own op row and records its own spend, so the ledger
 * shows two passes because two were paid for.
 *
 * Pure and exported for its tests, like `scheduledDue`.
 */
export function replicateRetryDue(produced: number, failure: ModelError['kind'] | null): boolean {
  return produced === 0 && (failure === 'network' || failure === 'server')
}

/**
 * Whether a run held for the opening — the strip's Run, pressed while the
 * opening was going (D351) — should still start once the opening has settled.
 *
 * Both halves must hold. A pass that landed nothing has left nothing to run
 * on, and its own failure toast has already said why — `startRun`'s "explore
 * an idea first" arriving on its heels would read as advice about a graph the
 * user just watched fail to exist. And ANY reported failure holds the run,
 * 'aborted' above all: the user's Stop is a stop, and a run that starts itself
 * moments after one was pressed is the app overruling them with their own
 * money. Nothing is lost but the automation — the rail's Run press stays.
 *
 * Pure and exported for its tests, like `replicateRetryDue`.
 */
export function chainedRunDue(produced: number, failure: ModelError['kind'] | null): boolean {
  return produced > 0 && failure === null
}

/** What the opening left behind, for the one caller that starts a held run on it —
 *  see `chainedRunDue`. Reported by the pass rather than re-read from the
 *  store, because a caller counting children after the fact cannot tell a pass
 *  that landed three from one that landed three and then died. `sessionId`
 *  names the exploration the press actually seeded: opening a saved
 *  exploration takes no lock, so by the time the chain reads this the current
 *  session may be one the user opened to READ — and a run must only ever
 *  start on the graph the press seeded, so the caller compares ids and holds. */
export interface SeedOutcome {
  produced: number
  failure: ModelError['kind'] | null
  sessionId: string
}

/** What `/replicate`'s second opening needs, in the key card's sentence: "…runs
 *  on another company’s model — it needs an OpenAI key." */
export const REPLICATE_NEED = {
  what: '/replicate’s blind second opening',
  on: 'another company’s model',
} as const

/**
 * Whether `/replicate` would be refused for its second opening's key — and if
 * so, the refusal, made: the settings sheet open on the other company's field,
 * saying what needed it (`askForOtherKey`, D202). Asked at the press, BEFORE the
 * first opening spends, by the doors that seed with it.
 *
 * It used to be asked only after the first opening had landed, as a toast that
 * opened nothing: the press bought one house's half of a comparison that could
 * not be made, and the reader who then added the key had no way to run the
 * other half on the graph they had paid for — `/replicate` rides a new seed.
 * Refused here, the words stay in the box and the same Enter runs both halves
 * once the key is in. The first opening's own key is asked first: with none,
 * the refusal is its (`askForMissingKey`), and this says nothing. Calls nothing.
 */
export function replicateKeyless(): boolean {
  const st = useStore.getState()
  const first = resolveFor(st, 'reason')
  if (!first || resolveAuditor(st, first.entry.provider)) return false
  st.askForOtherKey([first.entry.provider], REPLICATE_NEED)
  return true
}

export async function runSeed(
  seed: string,
  frame?: Frame,
  brief?: Directive[],
  opts: { replicate?: boolean } = {},
): Promise<SeedOutcome> {
  const s = useStore.getState()
  // A seed typed while the worked example unfurls replaces it, so the unfurl
  // is stopped and settled on its own document first. Left running, it went on
  // holding the lock over the new graph and the opening pass below was refused.
  s.yieldUnpaid()
  const rootId = s.startSession(seed, frame, brief)
  // Read back rather than derived: `startSession` mints the id, and the chain's
  // whole session-pinning defence is comparing against what it actually minted.
  const sessionId = useStore.getState().session.id
  // startSession just cleared the stack; a mark here would leave one step back
  // whose restore is a lone seed node.
  let seedFailure: ModelError['kind'] | null = null
  // A failed opening carries its own "Try again" on the failure toast
  // (`retryOffer`, D335) — the one way back to a lone seed that did not open,
  // pressed by the reader, on the same root. Not under /replicate: by the time
  // the reader pressed it the blind half may have landed, and the first house
  // shown its children would diverge because it was told to. And never the
  // chain's run: the offer is one opening at one opening's price, and a press
  // priced as a move that then started a run would lean low.
  const opened = await runOp('expand', rootId, undefined, {
    mark: false,
    ...(opts.replicate ? { retry: false as const } : {}),
    onFailure: (kind) => {
      seedFailure = kind
    },
  })
  const outcome: SeedOutcome = { produced: opened.length, failure: seedFailure, sessionId }
  // No coach opens here. The opening used to raise the card over the ring it had
  // just landed, headed "After the run" after one pass that was not a run, and
  // standing on the very claims it described. What the coach has to say about a
  // fresh ring is now one quiet line that opens the digest when pressed
  // (`CoachLine` in `ui/RunDoneBar.tsx`); only a run's close opens anything by
  // itself (`guideAutoStarts`).

  if (!opts.replicate) return outcome
  // A stop is a stop for the WHOLE press. The blind half is a new billed pass,
  // and launching it seconds after the user halted the first would spend the
  // exact money the stop refused. Only 'aborted' short-circuits here: the other
  // failures never had the user's hand in them, and the second house's opening
  // may still land while the first one's error is being read.
  if (seedFailure === 'aborted') return outcome

  // The second opening, from the other house, BLIND to the first — see
  // `runOp`'s `blindTo` for why blindness is the whole point. Resolved against
  // the model the first pass actually ran on, and refused out loud when there is
  // no second house to ask: a /replicate that silently ran both halves on one
  // provider would produce a comparison that compares nothing.
  const st = useStore.getState()
  const first = resolveFor(st, 'reason')
  if (!first) return outcome
  const other = resolveAuditor(st, first.entry.provider)
  if (!other) {
    // Reached only when the second key went between the press, which asks the
    // same question before anything spends (`replicateKeyless`), and here — or
    // from a door that did not ask. Either way the refusal is what every other
    // missing key is: that company's field, open, with what needed it.
    st.toast(
      'Opening the idea twice needs a key for a second provider — both halves on one house would be a comparison that compares nothing.',
      'warn',
    )
    st.askForOtherKey([first.entry.provider], REPLICATE_NEED)
    return outcome
  }
  st.toast(
    `${other.entry.name} is opening the same idea, blind to what ${first.entry.name} said.`,
    'info',
  )
  let failure: ModelError['kind'] | null = null
  const blind = () =>
    runOp('expand', rootId, undefined, {
      mark: false,
      model: other,
      blindTo: 'children',
      onFailure: (kind) => {
        failure = kind
      },
    })
  const landed = await blind()
  // One more attempt when the second house's pass died in transit — see
  // `replicateRetryDue` for exactly when. Without this, a nine-second network
  // blip leaves the comparison half-made: the first house's opening stands, the
  // second never arrives, and the only account is a toast that is gone in four
  // seconds. Announced before the wait so the second billed pass is never
  // silent, and if the retry fails too, `runOp`'s own toast says so honestly.
  if (replicateRetryDue(landed.length, failure)) {
    useStore
      .getState()
      .toast(`The second opening failed in transit — asking ${other.entry.name} once more.`, 'info')
    await sleep(REPLICATE_RETRY_MS)
    // Unless the user took the lock during the wait — their verb wins, and the
    // failure toast has already said where the comparison stands — or opened a
    // DIFFERENT exploration, which takes no lock: the retry's target would be
    // missing from that session, so `runOp` would return empty without ever
    // firing `onFailure`, and clearing the failure first would hand the chain
    // a clean-looking outcome on a graph the user opened to read. The failure
    // is cleared only when the retry actually goes, so one that lands reads as
    // the success it is.
    const mid = useStore.getState()
    if (!mid.busy && mid.session.id === sessionId) {
      failure = null
      await blind()
    }
  }
  // A held run's view of this half: `produced` stays the first house's landing,
  // but a failure on EITHER pass holds the run — a stop most of all, whichever
  // pass it interrupted.
  if (outcome.failure === null && failure !== null) outcome.failure = failure
  // Counted mechanically, so the toast reports arithmetic and not an impression.
  const done = useStore.getState().session
  const byHouse = new Map<string, number>()
  for (const id of done.order) {
    const n = done.nodes[id]
    if (!n || n.parentId !== rootId) continue
    const name = n.model?.name ?? 'unstamped'
    byHouse.set(name, (byHouse.get(name) ?? 0) + 1)
  }
  const parts = [...byHouse.entries()].map(([name, k]) => `${k} from ${name}`)
  if (parts.length > 1) {
    useStore
      .getState()
      .toast(
        `Both houses opened the idea blind to each other — ${parts.join(', ')}. Where they landed on the same move, that is convergence; where only one went, that is the door to look behind.`,
        'good',
      )
  }
  return outcome
}

/* ── Revising a node's understanding ──────────────────────────────────── */

/**
 * The user rewrites what a node means; everything below it is re-derived.
 *
 * Old children are dropped rather than kept. A child derived from the previous
 * claim is, at best, unrelated to the new one — leaving it attached would be
 * quietly dishonest about where the reasoning came from.
 */
export async function reviseNode(
  targetId: string,
  next: { label?: string; claim: string; reasoning?: string },
): Promise<void> {
  const st = useStore.getState()
  const node = st.session.nodes[targetId]
  if (!node) return

  // The lock, before anything is dropped. `runOp` refuses a held lock too, but
  // only once this function has already taken the subtree down and patched the
  // words. The editor's press waits on the Up next line instead of reaching
  // here under the lock (`pressRewrite`); this is the floor under it.
  if (lockHeld(st)) {
    st.toast('One thought at a time — let the current branch finish.', 'warn')
    return
  }

  // Check we can actually re-derive *before* destroying anything. Without this
  // a revision in demo mode deletes the subtree and then discovers it has no
  // model to rebuild it with, which is the worst possible order of operations.
  // The reasoning role's key, which is what `runOp` below resolves — not the
  // selection's, which under routing can hold a key while the pass has none.
  if (!resolveFor(st, 'reason')) {
    st.toast(
      `Rewriting re-reasons the branch below — connect your ${providerFor(st, 'reason').name} key first.`,
      'warn',
    )
    st.askForMissingKey('reason', 'Rewriting')
    return
  }

  // What goes, counted BEFORE it goes, by the same reading the editor's button
  // printed — so the toast after the landing names what the press promised.
  const loss = rewriteLoss(st.session, targetId)
  const label = next.label?.trim() || node.label

  // One step of history wraps the whole rewrite: the drop, the patch and the
  // re-derivation below. Held by identity for the landing toast's way back.
  useStore.getState().mark('Rewrite')
  const entry = useStore.getState().past.at(-1)

  // Drop the subtree, keeping the node itself.
  const kids = st.session.order.filter((id) => st.session.nodes[id]?.parentId === targetId)
  for (const k of kids) useStore.getState().dropSubtree(k)

  const ownReasoning = next.reasoning?.trim()
  // One clock read for the whole patch. `reasoningPredatesRewrite` compares
  // `reasoningEditedAt >= editedAt`, and two reads a millisecond apart put the
  // reader's own reasoning BEFORE their rewrite — kept out of every pass and
  // shown as the previous wording's.
  const at = Date.now()
  useStore.getState().patchNode(targetId, {
    label,
    claim: next.claim.trim(),
    // The model's reasoning stays on the node when the reader wrote none — it is
    // what they may want to read against their own words — and
    // `reasoningPredatesRewrite` keeps it out of every pass from here on.
    reasoning: ownReasoning ?? node.reasoning,
    ...(ownReasoning !== undefined ? { reasoningEditedAt: at } : {}),
    // Only replacement text repairs a loss. Rewriting the claim cannot restore
    // a missing argument that the edit left untouched.
    textTruncations: node.textTruncations?.filter(
      (row) =>
        row.field !== 'claim' &&
        row.field !== 'terminalReason' &&
        !(row.field === 'label' && next.label?.trim()) &&
        !(row.field === 'reasoning' && next.reasoning !== undefined),
    ),
    // A rewrite is the complete authoritative record now. Detail attached to the
    // model-authored kernel no longer describes this wording and must not survive.
    compact: undefined,
    elaboration: undefined,
    editedAt: at,
    lastPulse: at,
    terminal: false,
    terminalReason: undefined,
    // A conjecture's registration pins the wording its prediction was set down
    // with. New wording is registered under a new date and the old stamp is
    // kept, so a claim rewritten after its result came in reads as a rewrite.
    ...(node.conjecture
      ? { conjecture: reregister(node.conjecture, next.claim.trim(), Date.now()) }
      : {}),
    // A panel that ruled on wording the user has replaced describes a claim that
    // is no longer there. The verification is deliberately NOT cleared: the check
    // really happened, and `verificationLive` reads the two timestamps so the
    // record survives while its hue does not.
    panel: undefined,
    // A node being re-derived into cannot stay folded, or the new branch grows
    // where nobody can see it. Only stamp the clock if there was a fold to
    // release — `foldAt` on a node that was never folded plays the release
    // animation on a mark that was never there.
    ...(node.collapsed ? { collapsed: false, foldAt: at } : {}),
  })

  // Short, and not the landing's sentence: on a quick pass the two stand
  // together, and the one that counts what was replaced must not read as a
  // repeat of this.
  useStore.getState().toast(`Re-deriving from your wording of “${label}”…`, 'good')
  const produced = await runOp(
    'revise',
    targetId,
    `THE USER'S REVISED UNDERSTANDING OF THIS NODE (authoritative):\n${next.claim.trim()}${
      ownReasoning ? `\n\n${ownReasoning}` : ''
    }`,
    { mark: false },
  )
  // After the landing, and whatever it landed: the count of what the rewrite
  // replaced and the way back. The pass's own failure, if any, was already said
  // by `runOp`; this is about the claims, which are gone either way until the
  // reader takes them back. `undo` restores the whole snapshot — the claims,
  // the connection candidates and the previous wording together. Not said into
  // another exploration opened meanwhile, where its way back would be nobody's.
  if (useStore.getState().session.id !== st.session.id) return
  useStore
    .getState()
    .toast(
      rewriteLandedSentence(label, loss, produced.length),
      'info',
      undoOffer(entry, loss.claims || loss.connections ? 'Put them back' : 'Put it back'),
    )
}

/* ── The source check ─────────────────────────────────────────────────────
   One search pass per node, answering two questions off one body of evidence:
   what do sources say about this claim, and has anyone already said it. */

/**
 * The passages for one claim, or null when there is nothing worth showing.
 *
 * Returns null rather than an empty retrieval when the match is `none`, so the
 * caller cannot accidentally send a corpus block that offers no passages and asks
 * for citations anyway.
 *
 * `extra` is `runVerify`'s live prior art — ephemeral records fetched from the
 * enabled source rows for this pass only. They are ranked in ONE pool with the
 * user's own documents rather than in a second pass of their own: the coverage
 * floor, `PER_DOC`, `K_MAX` and the character budget are the whole reason a
 * grounded prompt cannot be flooded, and a parallel retrieval would be a second
 * copy of all four, immediately free to drift. Nothing else passes `extra` — the
 * panel and the link scan retrieve from the user's documents alone.
 */
function retrieveFor(target: ReasonNode, extra: readonly LoadedDoc[] = []): Retrieval | null {
  const docs = loadCorpus()
  if (!docs.length && !extra.length) return null
  const r = retrieve(retrievalQueryFor(target), [...docs, ...extra])
  if (r.match === 'none') return null
  // `docsAtCheck` means the USER'S documents and keeps meaning that. The
  // inspector prints it as "N passages from your N documents", and a record this
  // app fetched on its own is not one of theirs — inflating it would make the
  // sentence a lie in the one place a reader goes to audit the check.
  return { ...r, docsAtCheck: docs.length }
}

/**
 * One spelling of the local retrieval query, shared by the ranker and its audit
 * record — and, through `claimQuery`, by the Sources dialog's preview, so the
 * passages it shows are the passages this pass is offered. The label and claim
 * only: see `claimQuery` for why the reasoning is not in it.
 */
function retrievalQueryFor(target: ReasonNode): string {
  return claimQuery(target.label, target.claim)
}

interface RetrievalAuditAddress {
  /** The work identity emitted by the index before this document became passages. */
  identity: string
  /** The index's stable locator, including for a work with no public URL. */
  locator: string
  url?: string
}

function retrievalAuditRows(
  retrieval: Retrieval | null,
  docs: readonly LoadedDoc[],
  reach: CheckAuditRetrievalRow['reach'],
  withOriginUrl = false,
  addresses: ReadonlyMap<string, RetrievalAuditAddress> = new Map(),
): CheckAuditRetrievalRow[] {
  if (!retrieval || docs.length === 0) return []
  const byId = new Map(docs.map((doc) => [doc.id, doc]))
  return retrieval.candidates.flatMap((passage) => {
    const doc = byId.get(passage.docId)
    if (!doc) return []
    const address = addresses.get(doc.id)
    // A live row and the passages ranked from it are two observable pipeline
    // stages, not two works. Keep every occurrence, but carry the index's full
    // identity through the passage stage so the coverage certificate can make
    // that relationship explicit as a duplicate. Corpus passages have no
    // upstream work row, so their passage identity remains the stable unit.
    const identity = address?.identity ?? `${doc.id}:chunker-${doc.chunker}:p${passage.n}`
    const url = address?.url ?? (withOriginUrl ? liveOriginUrl(doc.id) : null)
    return [
      {
        id: `${doc.id}:p${passage.n}`,
        identity,
        locator: address?.locator ?? url ?? `corpus:${identity}`,
        title: doc.title,
        reach,
        rank: passage.rank,
        documentId: doc.id,
        passage: passage.n,
        snippet: passage.text.slice(0, 1_000),
        text: passage.text,
        disposition: passage.disposition,
        ...(url ? { url } : {}),
      },
    ]
  })
}

function liveAuditAddresses(live: LiveResult): ReadonlyMap<string, RetrievalAuditAddress> {
  const addresses = new Map<string, RetrievalAuditAddress>()
  for (const index of live.indexes) {
    for (const row of index.rows) {
      if (row.disposition !== 'retained' || !row.documentId) continue
      addresses.set(row.documentId, {
        identity: row.identity,
        locator: row.locator,
        ...(row.url ? { url: row.url } : {}),
      })
    }
  }
  return addresses
}

function liveIndexAuditBatches(
  target: ReasonNode,
  live: LiveResult,
  searchedAt: number,
  attempted: boolean,
): CheckAuditRetrievalBatch[] {
  if (!attempted) return []
  return live.indexes.map((index) => {
    const rows: CheckAuditRetrievalRow[] = index.rows.map((row) => ({
      id: row.id,
      identity: row.identity,
      locator: row.locator,
      title: row.title,
      reach: row.text ? 'abstract-only' : 'unreadable',
      rank: row.rank,
      ...(row.url ? { url: row.url } : {}),
      ...(row.documentId ? { documentId: row.documentId } : {}),
      ...(row.snippet ? { snippet: row.snippet } : {}),
      ...(row.text ? { text: row.text } : {}),
      disposition: row.disposition,
    }))
    const inconsistentCount =
      index.reportedResultCount !== null && index.reportedResultCount < rows.length
    const completion: CheckAuditRetrievalBatch['completion'] = index.failure
      ? {
          kind: 'failed',
          code: `live-index-${index.failure.code}`,
          reason: liveFailureNote(index.failure),
        }
      : inconsistentCount
        ? {
            kind: 'partial',
            code: 'live-index-count-inconsistent',
            reason: `${index.source} reported fewer results than the response container returned.`,
          }
        : { kind: 'complete' }
    return {
      rows,
      queryText: liveQuery(target.label, target.claim),
      queryFamilyLabel: 'Claim wording and reasoning',
      retrievalSourceId: `scholarly-index:${index.sourceId}`,
      retrievalSourceLabel: index.source,
      retrievalSourceKind: 'scholarly-index',
      retrievalSourceMeta: {
        indexId: index.sourceId,
        returnedContainerRows: rows.length,
        sourceReportedResultCount: index.reportedResultCount,
      },
      required: true,
      boundary: {
        searchedAt,
        // v2: the label and the claim's chosen words (`liveQuery`), where v1 sent
        // the whole claim — the same index can return different rows for each.
        searchVersion: `live-source-adapter-v2:${index.sourceId}`,
        corpusVersion: null,
      },
      completion,
      reportedResultCount: inconsistentCount ? null : index.reportedResultCount,
    }
  })
}

function corpusAuditBatch(
  target: ReasonNode,
  retrieval: Retrieval | null,
  docs: readonly LoadedDoc[],
  searchedAt: number,
): CheckAuditRetrievalBatch | null {
  if (docs.length === 0) return null
  const rows = retrievalAuditRows(retrieval, docs, 'full-text')
  return {
    rows,
    queryText: retrievalQueryFor(target),
    queryFamilyLabel: 'Claim wording and reasoning',
    retrievalSourceLabel: 'User document corpus',
    retrievalSourceKind: 'local-corpus',
    boundary: {
      searchedAt,
      // v2: the label-and-claim query, over folded terms and the short forms the
      // documents define. A receipt says which search produced its rows, and a v1
      // row and a v2 row over the same corpus can differ.
      searchVersion: 'local-bm25-v2',
      corpusVersion: String(CHUNKER),
    },
    completion: { kind: 'complete' },
    reportedResultCount: rows.length,
  }
}

function liveAuditBatch(
  target: ReasonNode,
  retrieval: Retrieval | null,
  live: LiveResult,
  searchedAt: number,
  attempted: boolean,
): CheckAuditRetrievalBatch | null {
  if (!attempted) return null
  const rows = retrievalAuditRows(
    retrieval,
    live.docs,
    'abstract-only',
    true,
    liveAuditAddresses(live),
  )
  const failures = live.failures.map(liveFailureNote)
  const completion: CheckAuditRetrievalBatch['completion'] = failures.length
    ? live.docs.length
      ? {
          kind: 'partial',
          code: 'live-source-failure',
          reason: failures.join(' '),
        }
      : {
          kind: 'failed',
          code: 'live-source-failure',
          reason: failures.join(' '),
        }
    : { kind: 'complete' }
  return {
    rows,
    queryText: JSON.stringify({
      outboundIndexQuery: liveQuery(target.label, target.claim),
      localRankingQuery: retrievalQueryFor(target),
    }),
    queryFamilyLabel: 'Claim wording and reasoning',
    retrievalSourceLabel: 'Enabled public scholarly indexes',
    retrievalSourceKind: 'scholarly-index',
    boundary: {
      searchedAt,
      searchVersion: 'live-source-adapters-v2+local-bm25-v2',
      corpusVersion: null,
    },
    completion,
    reportedResultCount: rows.length,
  }
}

function sourceCheckPrompts(opId: string, system: string, user: string): ResearchPromptReceipt[] {
  return [
    { id: `${opId}:system`, role: 'system', content: system },
    { id: `${opId}:user`, role: 'user', content: user },
    {
      id: `${opId}:schema`,
      role: 'tool',
      content: JSON.stringify({ name: VERIFY_SCHEMA.name, schema: VERIFY_SCHEMA.schema }),
    },
  ]
}

async function persistSourceCheckResearch(input: {
  opId: string
  target: ReasonNode
  search: SearchOutcome | undefined
  citationDecisions: readonly CitationDecision[]
  verification: Verification | null
  prompts: readonly ResearchPromptReceipt[]
  spend: Spend
  startedAt: number
  completedAt: number
  searchedAt: number
  corpus: CheckAuditRetrievalBatch | null
  live: CheckAuditRetrievalBatch | null
  liveIndexes?: readonly CheckAuditRetrievalBatch[]
  outcome?: ResearchAuditOutcome
  interruptedRound?: CheckAuditInterruptedRound | null
}): Promise<void> {
  const audited = await buildCheckAudit({
    opId: input.opId,
    nodeId: input.target.id,
    searchOutcome: input.search,
    citationDecisions: input.citationDecisions,
    verification: input.verification,
    corpus: input.corpus,
    live: input.live,
    liveIndexes: input.liveIndexes,
    webBoundary: {
      searchVersion: 'provider-version-unavailable',
      corpusVersion: null,
      searchedAt: input.searchedAt,
    },
    prompts: input.prompts,
    interruptedRound: input.interruptedRound,
    response: {
      spend: input.spend,
      model: input.spend.model,
      startedAt: input.startedAt,
      completedAt: input.completedAt,
      outcome: input.outcome,
    },
  })
  const current = useStore.getState().session
  let research =
    current.research ??
    createResearchState({ id: `research_${current.id}`, now: input.completedAt })
  research = upsertResearchEntities(
    research,
    'coverageTraces',
    [audited.coverage],
    input.completedAt,
  )
  research = upsertResearchEntities(research, 'auditReceipts', [audited.receipt], input.completedAt)
  const evidence = projectSessionEvidence(
    current,
    research.evidenceRecords,
    research.genealogyOverrides,
    research.evidenceAnnotations,
  )
  research = upsertResearchEntities(
    research,
    'evidenceRecords',
    evidence.records,
    input.completedAt,
  )
  useStore.getState().setResearchState(research)
}

/**
 * Passage citations, resolved against the table the prompt actually printed.
 *
 * The document analogue of the URL gate, and a deliberately separate code path:
 * `matchSeen` must never be applied to a passage number, and this must never be
 * applied to a URL. A document-grounded pass declares no server tools, so the seen
 * set is empty — running passages through the URL gate would drop every citation
 * and make the feature look as though the model had refused to cite.
 *
 * The quote is sliced by the CLIENT from the text the client printed. Accepting a
 * quote from the model here would be an unchecked assertion about text we have in
 * hand — and it is what makes deleting a document non-destructive: removal costs
 * the link, not the record.
 *
 * `docs` is the live pool `runVerify` fetched, and it is here for ONE field: a
 * record's `notice`, copied onto the cite so a retraction rides on the record as
 * a value. The user's own documents never carry one and pass nothing.
 *
 * `refs` is how a prior-art entry reaches a passage: the call-local `p3` the
 * model can write, keyed to the `passageRef` the record stores. The call-local
 * number is only ever a key INTO this map, never a value out of it — it means
 * nothing once the call is over, and a stored `p3` was read back against the
 * document-level numbers and named a different passage, or none (D146).
 *
 * EXPORTED FOR THE SAME SEAM `reconcile` is: a replay of recorded output through
 * the admission path a real pass uses.
 */
export function resolveCites(
  r: Retrieval,
  raw: unknown,
  docs: readonly LoadedDoc[] = [],
): { evidence: CorpusEvidence; refs: Map<string, string> } {
  const o = (raw ?? {}) as Record<string, unknown>
  const cites: PassageCite[] = []
  const refs = new Map<string, string>()
  const kept = new Set<number>()
  let dropped = 0

  // By doc id, which is what the retrieval carries and what the cite records —
  // never by title, which is prose and which a retraction notice's own title
  // would match.
  const notices = new Map<string, DocNotice>()
  for (const d of docs) if (d.notice) notices.set(d.id, d.notice)

  for (const v of Array.isArray(o.passages) ? o.passages : []) {
    const n = Math.trunc(Number(v))
    if (!Number.isFinite(n)) continue
    const chunk = r.table.get(n)
    if (!chunk) {
      dropped++
      continue
    }
    if (kept.has(n)) continue
    kept.add(n)
    const notice = notices.get(chunk.docId)
    const retained = textRetention('admission')
    const cite: PassageCite = {
      docId: chunk.docId,
      docTitle: chunk.docTitle,
      // The DOCUMENT-level number, not the call-local one the prompt printed. The
      // call-local number means nothing once the call is over.
      passage: chunk.n,
      quote: retained.text('passage', chunk.text, RESEARCH_ITEM_CHARS),
      ...retained.fields(),
      ...(notice ? { notice } : {}),
    }
    cites.push(cite)
    refs.set(`p${n}`, passageRef(cite))
  }

  return {
    evidence: {
      cites,
      dropped,
      match: r.match,
      offered: r.offered,
      bestCoverage: r.bestCoverage,
      docsAtCheck: r.docsAtCheck,
    },
    refs,
  }
}

export interface Reconciled {
  verification: Verification | null
  /** Every model-proposed web citation, including the ones the honesty gate
   * rejected. This is an audit ledger; only `verification.sources` is evidence. */
  citationDecisions: CitationDecision[]
  /** Set when the pass has to be abandoned rather than recorded. The string is
   *  what the user is told. */
  abandon?: string
}

export interface CitationDecision {
  index: number
  url: string
  /** Search-owned for an admission; model-authored and explicitly claimed for a rejection. */
  title: string
  decision: 'admitted' | 'rejected'
  rule: string
  reason: string
  /** Canonical identity of the exact returned row, only when one matched. */
  matchedIdentity?: string
}

/**
 * Model output → a record we are willing to show. This function is the honesty
 * gate, and every rule in it exists because the alternative is a citation nobody
 * can check.
 */
export function reconcile(
  raw: unknown,
  node: ReasonNode,
  search: SearchOutcome | undefined,
  scope: VerifyScope,
  corpus: { evidence: CorpusEvidence; refs: Map<string, string> } | null,
  model: ModelEntry,
  /**
   * Notes from evidence gathering that happened OUTSIDE the model's own search —
   * today the live source rows, which fail on their own network and must be as
   * visible as a search failure and as non-fatal. Already reader-facing prose
   * (`liveFailureNote` writes it), so this function never learns a second failure
   * vocabulary and cannot start rewording one.
   */
  outsideFailures: readonly string[] = [],
): Reconciled {
  const seen = search?.sources ?? []
  const hadPassages = !!corpus && corpus.evidence.cites.length > 0
  const citationDecisions: CitationDecision[] = []

  // A pass with nothing to reconcile is a failed pass, not a result. An absence
  // only means something next to a search that returned something.
  if (!search && !corpus)
    return { verification: null, citationDecisions, abandon: 'Nothing was searched.' }
  if (search && search.sources.length === 0 && !hadPassages) {
    const why = search.failures.length
      ? failureNote(search.failures[0]!.code)
      : 'The search came back empty, so there is nothing to report yet.'
    return { verification: null, citationDecisions, abandon: why }
  }

  const r = (raw ?? {}) as Record<string, unknown>
  const claimed = str(r.reading, 40) as SourceReading
  const kept: WebSource[] = []
  const keptKeys = new Set<string>()
  let dropped = 0

  for (const [index, item] of (Array.isArray(r.sources) ? r.sources : []).entries()) {
    const o = (item ?? {}) as Record<string, unknown>
    const url = str(o.url, 2000)
    const claimedTitle = str(o.title, 300)
    if (!url || !isHttpUrl(url)) {
      citationDecisions.push({
        index,
        url,
        title: claimedTitle,
        decision: 'rejected',
        rule: 'citation.valid-http-url.v1',
        reason: 'The proposed citation was not a valid HTTP(S) URL.',
      })
      continue
    }
    const hit = matchSeen(url, seen)
    // THE gate. A URL the search never returned is not a citation, whatever the
    // model calls it — and the count is surfaced rather than swallowed, because a
    // silent drop is indistinguishable from a check that had nothing to say.
    if (!hit) {
      dropped++
      citationDecisions.push({
        index,
        url,
        title: claimedTitle,
        decision: 'rejected',
        rule: 'citation.returned-by-search.v1',
        reason: 'The search did not return this URL.',
      })
      continue
    }
    const key = canonicalUrl(url)
    if (keptKeys.has(key)) {
      citationDecisions.push({
        index,
        url,
        title: hit.title,
        decision: 'rejected',
        rule: 'citation.canonical-url-dedupe.v1',
        reason: 'This canonical URL was already admitted for the check.',
        matchedIdentity: canonicalUrl(hit.url),
      })
      continue
    }
    if (kept.length >= 6) {
      citationDecisions.push({
        index,
        url,
        title: hit.title,
        decision: 'rejected',
        rule: 'citation.display-bound.v1',
        reason: 'The check already retained its six inspectable web citations.',
        matchedIdentity: canonicalUrl(hit.url),
      })
      continue
    }
    keptKeys.add(key)

    const retained = textRetention('admission')
    const proposedQuote = typeof o.quote === 'string' ? o.quote.trim() : ''
    const quote = retained.text('quote', proposedQuote, SOURCE_QUOTE_CHARS)
    // Folded ONCE, and everything about this page's text is read off the same
    // string: the quote check below, and the two signatures the independence gate
    // collapses on. A second fold would be a second definition of what the text
    // IS, which is how the citation gate leaks in `url.ts`'s own words.
    const page = hit.via === 'fetch' && hit.text !== undefined ? foldText(hit.text) : undefined
    // Tri-state. `undefined` means the check was impossible: a snippet, or a PDF
    // that arrived as base64. `false` there would accuse the model of inventing a
    // quote it copied correctly.
    // Compare the entire proposed quotation. A retained prefix must never hide
    // a fabricated suffix and earn a match that the actual quotation failed.
    //
    // A page the fetch tool CUT is a third way the check can be impossible, and
    // it is the reason the truncation inference exists at all. The quote may sit
    // past the cut, so "not in the text this app received" is not "not on the
    // page" — and `false` would exclude a real refutation from `refutingDomains`
    // outright. `undefined` sends it to the three-domain-plus-DOI path instead,
    // which is the bar a text-less page already faces.
    //
    // Only where the quote was NOT found. A quote that WAS found is found: a cut
    // page proves nothing about text this app can see with its own eyes. One
    // definition, `quoteFoundOn`, which the prior-art search reads too.
    const quoteFound = quoteFoundOn(page, proposedQuote, hit.truncated)
    // Machine fields, never displayed. `refutingDomains` collapses copies of one
    // artefact before it counts domains, and the text they are taken over does not
    // survive the pass — so a stored source has to carry them, or the disk gate
    // cannot reach the live gate's verdict. See `core/similarity.ts`.
    const near = page !== undefined ? simHash(page) : undefined

    const given = STANCES.has(str(o.stance, 20))
    kept.push({
      url: hit.url,
      // Never the model's. The title is what carries a source's authority.
      title: hit.title,
      quote,
      stance: given ? (str(o.stance, 20) as WebSource['stance']) : 'context',
      // A side the model did not give is filled as context, which no gate counts
      // either way — and marked, so the stance count says "not labelled" for it
      // instead of reporting a label nobody gave (`stanceTally`).
      ...(given ? {} : { stanceUnlabelled: true as const }),
      via: hit.via,
      ...(quoteFound !== undefined ? { quoteFound } : {}),
      ...(page !== undefined ? { textComplete: hit.truncated !== true } : {}),
      ...(page !== undefined ? { textHash: textHash(page) } : {}),
      ...(near !== undefined ? { simHash: near } : {}),
      // What kind of evidence the model read the page as. Stored beside the
      // stance, read by no gate: `refutedIsEarned` below never sees it, and an
      // `unclear` is the absence it means rather than a label.
      ...(DESIGNS.has(o.design as SourceDesign) ? { design: o.design as SourceDesign } : {}),
      ...retained.fields(),
    })
    citationDecisions.push({
      index,
      url: hit.url,
      title: hit.title,
      decision: 'admitted',
      rule: 'citation.returned-by-search.v1',
      reason: 'The canonical URL matched a result returned by this search pass.',
      matchedIdentity: canonicalUrl(hit.url),
    })
  }

  // The reading, after the gate has had its say. A missing or unknown one is
  // filled as an absence, so the gates below have a reading to act on, but the
  // fill is the gate's word and not the pass's. `said` is the pass's, and it is
  // the only thing `claimedReading` is ever set from: kept from the fill, it had
  // the caveat say the pass reported "nothing found" of a pass that reported
  // nothing at all (D322).
  const said = READINGS.has(claimed) ? claimed : undefined
  let reading: SourceReading = said ?? 'nothing-found'
  let claimedReading: SourceReading | undefined
  // Set by the one branch below that writes `not-checkable` itself rather than
  // taking it from the pass. Without it, a pass that gave no reading there keeps no
  // claim (D322), and its record reads as a pass that itself said not-checkable
  // beside a failed search (D326).
  let demotedForFailure = false
  const retainedSummary = textRetention('admission')
  let summary: string | undefined = retainedSummary.text('summary', r.summary)

  // A documents-only pass that names nothing and says its passages do not address
  // the claim has given the answer its brief asks for, not lost its citations.
  // `CITE_BRIEF` tells it to leave `passages` empty when they do not address the
  // claim, and a thin match adds that this is "the correct answer". The gate below
  // made that answer `unsourced` and discarded its summary, while the same answer
  // citing a passage it called beside the point landed as nothing-found (D321).
  // What the absence stands next to is the passages offered, and the record keeps
  // their count, match and coverage. Only the reading the model GAVE counts: the
  // fill for a missing one is not an answer. And only a pass that named nothing:
  // a web address on a pass that searched no web, or a passage number the prompt
  // never printed, is a citation that failed the gate.
  const documentsSayNothing =
    !search &&
    !!corpus &&
    claimed === 'nothing-found' &&
    citationDecisions.length === 0 &&
    corpus.evidence.dropped === 0

  if (kept.length === 0 && !hadPassages && reading !== 'not-checkable' && !documentsSayNothing) {
    // Nothing it named survived the gate, or it named nothing. The reading goes,
    // the prose goes with it, and what it claimed, if it claimed anything, is kept
    // so the gap is legible.
    claimedReading = said
    reading = 'unsourced'
    summary = undefined
  } else if (reading === 'refuted' && !refutedIsEarned(kept)) {
    // The loud channel requires an OPENED page — "a paper with this title exists"
    // and "this paper says the opposite" are not the same claim — and now TWO of
    // them, carrying independent TEXT on independent domains. Three where nothing
    // was checked against the page, because there the quote predicate is vacuous
    // and no signature came back to collapse on either — and three that something
    // could TELL APART, since a page with neither a signature nor a DOI is a page
    // this app cannot distinguish from the copy beside it, and three of those are
    // three domains rather than three sources. Which bar applies is read off each
    // SOURCE and not off `search.pageText`: a fetched PDF arrives as base64 on a
    // pass where every HTML page beside it returned text.
    //
    // One was trivially satisfiable by whoever controls the page: they wrote the
    // quote, so the substring check passes by construction, and `via: 'fetch'` only
    // says the model opened the URL it was handed. Counting registrable domains
    // rather than URLs is what makes the second source cost something: two paths on
    // one host, or two subdomains of one domain, are one owner — and counting one
    // domain per ARTEFACT is what stops a syndicated copy being that second owner,
    // which needed no attacker at all.
    //
    // And a forged refutation is not local — `taintMap` carries it down the whole
    // subtree, so the blast radius of getting this wrong is an entire branch of
    // somebody's reasoning, marked as the app's most-defended finding.
    //
    // The cost is real and accepted: a genuine single authoritative refutation, a
    // retraction notice, one decisive paper, now lands as `mixed`. That failure is
    // LEGIBLE — `claimedReading` preserves what was claimed and the source is still
    // shown with its domain — while the failure it prevents is invisible.
    claimedReading = 'refuted'
    reading = 'mixed'
  } else if (reading === 'nothing-found' && search && search.failures.length > 0) {
    // Some searches errored, so an absence here is not evidence of anything. The
    // absence is kept as a claim only where the pass gave it: `said`, not the fill.
    // Why the reading is what it is goes on the record either way, since this is
    // the gate's `not-checkable`, not the pass's.
    claimedReading = said
    reading = 'not-checkable'
    demotedForFailure = true
  }

  // Prior art, then the one number it is allowed to move.
  const priorArt: PriorArt[] = []
  for (const item of Array.isArray(r.priorArt) ? r.priorArt : []) {
    if (priorArt.length >= 4) break
    const o = (item ?? {}) as Record<string, unknown>
    const refRaw = str(o.ref, 2000)
    if (!refRaw) continue
    // A passage arrives as the call-local `p3` the prompt printed and is stored
    // as the `passageRef` of the cite it resolved to — translated HERE, while the
    // table that gives `p3` a meaning still exists. Only a passage this pass cited
    // translates, and a ref written in the stored form is not one the prompt ever
    // offered, so it falls through to the URL arm and names nothing (D146).
    const ref = /^p\d+$/.test(refRaw)
      ? corpus?.refs.get(refRaw)
      : keptKeys.has(canonicalUrl(refRaw))
        ? canonicalUrl(refRaw)
        : undefined
    if (!ref) continue
    priorArt.push({
      ref,
      covers: str(o.covers, 400),
      closeness: CLOSENESS.has(str(o.closeness, 20))
        ? (str(o.closeness, 20) as PriorArt['closeness'])
        : 'adjacent',
    })
  }

  // One list, deduped once. A search failure and a source row failing are the
  // same thing to a reader — a piece of the evidence this check was supposed to
  // rest on did not arrive — so they print in one place under one heading.
  const failures = [...(search?.failures ?? []).map((f) => failureNote(f.code)), ...outsideFailures]

  const rawNovelty = typeof r.measuredNovelty === 'number' ? r.measuredNovelty : -1
  // Down only, and only with a surviving citation behind it. An absence of prior
  // art produces NO number: a search that found nothing is a search that found
  // nothing, and printing 0.75 for it under a field called "measured" is the tool
  // declaring it has found something.
  const measuredNovelty =
    priorArt.length > 0 &&
    rawNovelty >= 0 &&
    rawNovelty <= node.novelty &&
    reading !== 'unsourced' &&
    reading !== 'not-checkable'
      ? clamp(rawNovelty)
      : undefined

  return {
    citationDecisions,
    verification: {
      reading,
      ...(claimedReading ? { claimedReading } : {}),
      ...(demotedForFailure ? { demotedFor: 'failed-search' as const } : {}),
      ...(summary ? { summary } : {}),
      ...(summary ? retainedSummary.fields() : {}),
      sources: kept,
      ...(corpus ? { corpus: corpus.evidence } : {}),
      priorArt,
      ...(measuredNovelty !== undefined ? { measuredNovelty } : {}),
      ...(measuredNovelty !== undefined ? { noveltyNote: str(r.noveltyNote, 300) } : {}),
      scope,
      queries: search?.queries ?? [],
      seen: seen.length,
      dropped,
      ...(search?.exhausted ? { partial: true } : {}),
      ...(failures.length ? { failures: [...new Set(failures)] } : {}),
      checkedAt: Date.now(),
      model: stampOf(model),
      // From what the adapter RECEIVED, never from the registry, and absent
      // entirely on a pass with no web surface: a documents-only check has no page
      // to check a quote against, and `false` there would report a model limit that
      // was never reached.
      ...(search ? { quotesCheckable: search.pageText } : {}),
    },
  }
}
/* What used to be noted here is that the `refuted` gate was byte-identical on both
   providers: on one, `quoteFound !== false` is a real test; on the other it is
   vacuous, because nothing could ever set it. That was called deliberate, on the
   grounds that a stricter gate there would delete the tool's most valuable finding
   on every model that does not return page text — all three GPT‑5.6 tiers and any
   custom endpoint that can search at all — and that the difference in evidence
   strength was carried by `refutedBacking(v)` in prose, where a reader sees it.

   That trade has been RE-PRICED, and the gate is no longer byte-identical. The
   prose was doing two jobs and could only do one: it told a reader what the label
   rested on, and it was also the only thing standing between "the model opened two
   URLs" and the app's most destructive reading — on half the registry, under the
   same word. So the bar now carries the missing evidence: `REFUTED_MIN_DOMAINS`
   where a quote was FOUND in the page's own text, `REFUTED_MIN_DOMAINS_UNCHECKED`
   where nothing was checked against it.

   Read per SOURCE rather than off `search.pageText`, which was the first version
   of this and the wrong altitude: `pageText` is what the ADAPTER can do, and a
   fetched PDF arrives as base64 with no text on a pass where it is `true`. Two of
   those on two domains would have cleared the lower bar with no quote checked and
   no signature to collapse on — the combination the higher bar exists for.

   And the higher bar was a PRICE, not a proof, until the same review looked at
   what it counted. Three text-less copies of one wire story carry no signature,
   so nothing collapses them and three clusters clear a bar of three — reachable
   with no attacker at all, by the syndication D23 opens on. So the unchecked
   count admits only artefacts something could identify: a signature, or a DOI.
   `refutedBacking(v)` still prints the difference. `docs/decisions.md` D29 records
   the re-pricing, its costs and both corrections; D28 records the collapse that
   runs before the count. */

const READINGS = new Set<string>([
  'supported',
  'mixed',
  'refuted',
  'nothing-found',
  'not-checkable',
  'unsourced',
])
const STANCES = new Set<string>(['supports', 'disputes', 'context'])
const CLOSENESS = new Set<string>(['same', 'overlapping', 'adjacent'])

/**
 * One check's search budget.
 *
 * A bound on one provider and a request on the other: where the API takes
 * `max_uses` these are enforced, and where the tool loop is internal the same two
 * numbers reach the model as a sentence in the brief. Written once so the request
 * and the sentence cannot drift.
 *
 * Exported for a third reader: searches are billed per request and apart from
 * tokens, so a run's projected spend needs this sum, and the run control's
 * promise about live web requests is composed from it. Both used to carry a
 * hardcoded 9.
 */
export const VERIFY_BUDGET = { maxSearches: 6, maxFetches: 3 }

/** The PROVIDER tool budget one source check may spend: its searches plus its
 *  page fetches, bounded per check by `VERIFY_BUDGET` and billed per request at
 *  `searchPerK`. This is the figure the spend projection charges for, and it is
 *  no longer the whole of what a check sends over the network — the research rows
 *  add their own unpriced lookups, `liveLookupsPerCheck` in `liveSources.ts`
 *  bounds those, and the run control's promised figure is composed from both
 *  where that sentence is built. */
export const SEARCHES_PER_CHECK = VERIFY_BUDGET.maxSearches + VERIFY_BUDGET.maxFetches

/**
 * Check one node against sources.
 *
 * Never available in the demo, and that is permanent. A pre-authored citation
 * pointing at a real URL is the exact artefact this whole feature exists to make
 * impossible, so there is no demo source check and there never will be.
 */
export async function runVerify(
  targetId: string,
  opts: {
    mark?: boolean
    quiet?: boolean
    maxRetries?: number
    investigationPurpose?: string
    onStart?: (opId: string) => void
  } = {},
): Promise<boolean> {
  const s = useStore.getState()
  if (s.busy) {
    if (!opts.quiet) s.toast('One thought at a time — let the current pass finish.', 'warn')
    return false
  }
  const target = s.session.nodes[targetId]
  if (!target) return false
  // The seed is the question, not a claim, so V on it surveys the field around
  // it instead of checking it: a question has no truth value for a source to
  // bear on. It used to refuse here, which left the one sentence an exploration
  // exists for as the one sentence no step ever searched. Everything a reader
  // pays for is the check's — see `runSurvey`.
  if (target.parentId === null) {
    return runSurvey(targetId, {
      ...(opts.quiet !== undefined ? { quiet: opts.quiet } : {}),
      ...(opts.maxRetries !== undefined ? { maxRetries: opts.maxRetries } : {}),
      ...(opts.onStart ? { onStart: opts.onStart } : {}),
    })
  }
  const m = resolveFor(s, 'check')
  if (!m) {
    if (!opts.quiet) {
      s.toast(
        `A source check searches the live web, so it needs your ${providerFor(s, 'check').name} key.`,
        'warn',
      )
    }
    s.askForMissingKey('check')
    return false
  }

  // A model with no server-side search cannot run this pass at all. Refusing here
  // is the honest degradation: a check that declared a tool the endpoint does not
  // have would 400, and one that ran without any search would return nothing and
  // read as "nothing found" — which is the single confusion this whole feature was
  // built to prevent. Asked through `checkCanRead`, because a run asks it before
  // proposing a check and the two questions must be one.
  if (!checkCanRead(s)) {
    if (!opts.quiet) {
      s.toast(searchlessCheckSentence(m.entry.name), 'warn')
    }
    return false
  }

  const attemptedModel = stampOf(m.entry)

  // The op opens BEFORE the source rows are called, because calling them is an
  // await and `busy` is what makes "one thought at a time" true. Gathering
  // outside the op would leave a five-second window in which a second press
  // starts a second check.
  // A landed check is an append-only evidence event. Undo carries it, its op and
  // its spend forward, so taking an argument-history mark here would create a
  // first Cmd-Z that announces an undo while changing nothing.
  const opId = s.beginOp('verify', [targetId], { model: attemptedModel })
  opts.onStart?.(opId)
  const controller = new AbortController()
  inFlight = controller
  let thought = ''
  let first: unknown = null
  const operationStartedAt = Date.now()
  let responseStartedAt = operationStartedAt
  let prompts: ResearchPromptReceipt[] = []
  let corpusAudit: CheckAuditRetrievalBatch | null = null
  let liveAudit: CheckAuditRetrievalBatch | null = null
  let liveIndexAudit: CheckAuditRetrievalBatch[] = []
  let auditSearch: SearchOutcome | undefined
  let auditSpend: Spend | null = null
  let modelAttempted = false
  let webAttempted = false
  let liveAttempted = false
  const observedQueries: string[] = []
  const observedOpenedUrls: string[] = []
  const interruptedRound = (
    code: string,
    reason: string,
    searchedAt: number,
  ): CheckAuditInterruptedRound | null => {
    if (auditSearch) return null
    if (webAttempted) {
      return {
        surface: 'web',
        code,
        reason,
        searchedAt,
        searchVersion: 'provider-version-unavailable',
        corpusVersion: null,
        queries: observedQueries,
        openedUrls: observedOpenedUrls,
      }
    }
    if (liveAttempted && !liveAudit) {
      return {
        surface: 'live',
        code,
        reason,
        searchedAt,
        searchVersion: 'live-source-adapters-v2',
        corpusVersion: null,
        queryText: liveQuery(target.label, target.claim),
      }
    }
    return null
  }

  try {
    // Live prior art, fetched at CHECK TIME and discarded with this pass. Nothing
    // is stored, so nothing here can eat the corpus budget the user believes is
    // theirs — D23's warning, satisfied by construction rather than by accounting.
    //
    // Suppressed entirely under "Prefer my sources over the web": the toggle says
    // the web, and three public APIs are the web. And reached from HERE only —
    // the panel and the link scan retrieve from the user's documents alone.
    if (!s.preferCorpus) useStore.getState().setSearching('searching: prior art')
    const enabledRows = enabledLiveRows(s.sourcesOn, s.session.research?.mathematics?.subject)
    liveAttempted = !s.preferCorpus && enabledRows.length > 0
    const live = s.preferCorpus
      ? noLiveSources()
      : await fetchLiveSources({
          rows: enabledRows,
          query: liveQuery(target.label, target.claim),
          mailto: s.sourceMailto,
          // The SAME signal the model call below gets, so Stop reaches the part
          // of the pass that is actually running. Without it `abortInFlight`
          // aborted a request that had not been made yet and the user waited out
          // both gathering rounds under a pod claiming the app was working.
          signal: controller.signal,
        })
    // One pool, one ranker. The fetched records are offered in the same corpus
    // block, under the same `p<N>` numbering, and land as the same PassageCites.
    const corpusDocs = loadCorpus()
    const retrievalAttempt =
      corpusDocs.length || live.docs.length
        ? retrieve(retrievalQueryFor(target), [...corpusDocs, ...live.docs])
        : null
    const retrievedAt = Date.now()
    const retrieval =
      retrievalAttempt && retrievalAttempt.match !== 'none'
        ? { ...retrievalAttempt, docsAtCheck: corpusDocs.length }
        : null
    const corpusBlock = retrieval ? buildCorpusBlock(retrieval) : undefined
    // "Prefer my sources" only takes effect when the documents actually matched.
    // Off, or with no match, the pass searches the web — a preference that silently
    // turned a check into a no-op would be worse than no preference.
    const corpusOnly = s.preferCorpus && !!retrieval
    corpusAudit = corpusAuditBatch(target, retrievalAttempt, corpusDocs, retrievedAt)
    liveAudit = liveAuditBatch(target, retrievalAttempt, live, retrievedAt, liveAttempted)
    liveIndexAudit = liveIndexAuditBatches(target, live, retrievedAt, liveAttempted)
    if (controller.signal.aborted && liveAudit) {
      liveAudit = {
        ...liveAudit,
        completion: {
          kind: 'failed',
          code: 'operation-cancelled',
          reason: 'The source check was cancelled before live retrieval could complete.',
        },
      }
      liveIndexAudit = liveIndexAudit.map((batch) => ({
        ...batch,
        completion: {
          kind: 'failed',
          code: 'operation-cancelled',
          reason: 'The source check was cancelled before live retrieval could complete.',
        },
      }))
    }

    // The searchless refusal, at the far end of the pass this time. There is no
    // corpus block — the documents are empty, or none of them addresses this claim —
    // and this model has no web to fall back on. Falling through would declare
    // `web: VERIFY_BUDGET` at an endpoint that has no such tool, which is a rejected
    // request rather than a check; and a pass that somehow ran anyway would read
    // nothing and come back empty, which reads as "nothing found" — the single
    // confusion this whole feature was built to prevent, the same argument the guard
    // before the op makes.
    //
    // A model that CAN search is deliberately not caught here, and the comment three
    // lines up stays true of it: with no match it searches the web, because a
    // preference that silently turned a check into a no-op would be worse than no
    // preference. The asymmetry is the whole point — a searchless model has no web to
    // fall back TO, so the honest degradation there is a refusal, not a downgrade.
    //
    // Written on the model and on `corpusOnly` rather than on `!retrieval`, so it
    // does not lean on the guards above staying as they are: ANY path that reaches
    // the model call with no corpus block and nothing to search with has to stop
    // before the transport. This replaced `corpusOnly && retrieval.match === 'none'`,
    // which could not fire at all — `retrieveFor` returns null for every match-'none'
    // retrieval, so a truthy `retrieval` never carries that match. Do not put it back.
    if (m.entry.search === 'none' && !corpusOnly) {
      const refusalReason =
        `Your sources do not address this claim, and ${m.entry.name} has no web search ` +
        'to fall back to, so there was nothing to check it against.'
      if (!opts.quiet) {
        useStore.getState().toast(refusalReason, 'info')
      }
      useStore.getState().endOp(opId, [])
      const completedAt = Date.now()
      await persistSourceCheckResearch({
        opId,
        target,
        search: undefined,
        citationDecisions: [],
        verification: null,
        prompts: [],
        spend: {
          model: attemptedModel,
          inputTokens: 0,
          outputTokens: 0,
          searches: 0,
        },
        startedAt: operationStartedAt,
        completedAt,
        searchedAt: completedAt,
        corpus: corpusAudit,
        live: liveAudit,
        liveIndexes: liveIndexAudit,
        outcome: {
          kind: 'refused',
          code: 'no-available-evidence-surface',
          reason: refusalReason,
        },
      })
      return false
    }

    const systemPrompt = buildVerifySystem(
      m.entry.search,
      corpusOnly ? undefined : VERIFY_BUDGET,
      s.session.brief,
      // The same session and target `streamOpObjects` builds this pass's contract
      // from, so the theorem clauses travel exactly when its block prints the
      // mathematics paragraph.
      mathematicsInPlay(s.session, [targetId]),
    )
    const userPrompt = buildVerifyTurn(s.session, targetId, corpusBlock, corpusOnly)
    responseStartedAt = Date.now()
    modelAttempted = true
    webAttempted = !corpusOnly
    const res = await streamOpObjects(opId, {
      researchContext: {
        session: s.session,
        purpose: opts.investigationPurpose,
        onPrepared: (system, user) => {
          prompts = sourceCheckPrompts(opId, system, user)
        },
      },
      model: m,
      // Built for the search this model actually has. Three fragments differ and
      // nothing else does — see `buildVerifySystem`.
      system: systemPrompt,
      user: userPrompt,
      schema: VERIFY_SCHEMA,
      // A pinned ask, and the ask carries no temper because its type has no field
      // for one — the reason is in `effort.ts` beside the row.
      effort: resolvePassEffort({ pass: 'verify', levels: s.effortLevels }),
      maxTokens: 20000,
      ...(opts.maxRetries !== undefined ? { maxRetries: opts.maxRetries } : {}),
      // Omitted entirely on a corpus-only pass, so the tools are not in the
      // request at all — the prompt saying "you have no web search" and the request
      // declaring one would be a contradiction the model has to guess about.
      ...(corpusOnly ? {} : { web: VERIFY_BUDGET }),
      signal: controller.signal,
      onObject: (obj) => {
        if (first === null) first = obj
      },
      onThinking: (d) => {
        thought += d
        useStore.getState().setThought(thought)
      },
      onSearch: (ev) => {
        if (ev.kind === 'query') observedQueries.push(ev.text)
        else observedOpenedUrls.push(ev.url)
        useStore
          .getState()
          .setSearching(
            ev.kind === 'query' ? `searching: ${ev.text}` : `reading: ${hostOf(ev.url)}`,
          )
      },
    })
    const responseCompletedAt = Date.now()
    auditSearch = res.search
    auditSpend = res.spend

    // Spend is recorded BEFORE anything can back out. A pass that searched and
    // produced nothing still searched — and the searches are inside `res.spend`,
    // summed by the transport, because one provider cannot separate a search from
    // a page opened inside it.
    useStore.getState().addSpend(res.spend, opId)

    if (res.refused) {
      // The record keeps the provider's own words; only the toast adds which
      // filter it was and where to try again, which is advice rather than a fact
      // about the check (D312).
      const refusalReason = res.refusalNote || 'The model declined to check this claim.'
      if (!opts.quiet) {
        useStore
          .getState()
          .toast(
            declinedMessage(
              'The model declined to check this claim.',
              res,
              res.spend.model,
              'check',
            ),
            'warn',
          )
      }
      useStore.getState().endOp(opId, [], { thinking: thought, ...declinedOf(res) })
      await persistSourceCheckResearch({
        opId,
        target,
        search: res.search,
        citationDecisions: [],
        verification: null,
        prompts,
        spend: res.spend,
        startedAt: responseStartedAt,
        completedAt: responseCompletedAt,
        searchedAt: responseCompletedAt,
        corpus: corpusAudit,
        live: liveAudit,
        liveIndexes: liveIndexAudit,
        outcome: {
          kind: 'refused',
          code: 'provider-refusal',
          reason: refusalReason,
        },
        interruptedRound: interruptedRound(
          'provider-refusal',
          'The provider refused before the declared retrieval surface returned an outcome.',
          responseCompletedAt,
        ),
      })
      return false
    }

    const cites = retrieval ? resolveCites(retrieval, first, live.docs) : null
    const { verification, citationDecisions, abandon } = reconcile(
      first,
      target,
      res.search,
      resolveScope(res.search, cites),
      cites,
      m.entry,
      // Visible and non-fatal. A row that did not answer is recorded beside the
      // search failures, because "nothing else has been published on this" reads
      // very differently when OpenAlex was down while it was decided.
      live.failures.map(liveFailureNote),
    )

    // A second reading of every quoted source, by the judge, where a key is
    // stored. AFTER the gate and beside its output, never inside it: the sources
    // it reads are the ones `reconcile` admitted, the stance it gives is stored
    // next to the model's, and `refutedIsEarned` reads neither this nor anything
    // derived from it (D106). Inside the op, so `busy` still holds while it runs;
    // billed under the judge's own stamp on this op's row; and never fatal — a
    // check whose second reading did not arrive is a check with a note on it.
    if (verification) {
      const judge = resolveJudge(useStore.getState())
      const seen = res.search?.sources ?? []
      const inputs = judge
        ? stanceInputsFor(verification.sources, (url) => matchSeen(url, seen)?.text)
        : []
      if (judge && inputs.length) {
        try {
          const asked = stanceQuestions(
            target.claim,
            inputs.map((x) => x.source),
          )
          const jr = await askJudge(judge, asked, { signal: controller.signal })
          useStore.getState().addSpend(jr.spend, opId)
          verification.sources = withJudgedStances(
            verification.sources,
            inputs,
            jr.answers,
            jr.model,
          )
        } catch (err) {
          if (err instanceof ModelError && err.spent) useStore.getState().addSpend(err.spent, opId)
          verification.judgeNote =
            err instanceof ModelError
              ? `The judge did not read this check: ${err.message}`
              : 'The judge did not read this check.'
        }
      }
    }

    useStore.getState().endOp(opId, [], { thinking: thought, model: res.spend.model })

    if (!verification) {
      await persistSourceCheckResearch({
        opId,
        target,
        search: res.search,
        citationDecisions,
        verification: null,
        prompts,
        spend: res.spend,
        startedAt: responseStartedAt,
        completedAt: responseCompletedAt,
        searchedAt: responseCompletedAt,
        corpus: corpusAudit,
        live: liveAudit,
        liveIndexes: liveIndexAudit,
      })
      if (!opts.quiet) {
        useStore.getState().toast(abandon ?? 'That check produced nothing to record.', 'info')
      }
      return false
    }

    const liveSession = useStore.getState().session
    const current = liveSession.nodes[targetId]
    if (!current) return false
    const nextNodeOrdinal =
      liveSession.nextNodeOrdinal ??
      liveSession.order.reduce(
        (max, id, index) => Math.max(max, liveSession.nodes[id]?.createdOrdinal ?? index),
        -1,
      ) + 1
    // The user's own passages offered to this pass, counted apart from the fetched
    // records ranked beside them: "none of yours matched" is still true when a
    // stranger's abstract did.
    const ownIds = new Set(corpusDocs.map((d) => d.id))
    const ownOffered = retrieval?.passages.filter((p) => ownIds.has(p.docId)).length ?? 0
    const recorded: Verification = {
      ...verification,
      model: res.spend.model,
      opId,
      claimAtCheck: target.claim,
      claimHashAtCheck: textHash(foldText(target.claim)),
      labelAtCheck: target.label,
      nodeOrdinalAtCheck: Math.max(0, nextNodeOrdinal - 1),
      ...(corpusDocs.length > 0 && ownOffered === 0
        ? {
            corpusSearched: {
              docs: corpusDocs.length,
              offered: 0 as const,
              preferCorpus: s.preferCorpus,
            },
          }
        : {}),
    }
    useStore.getState().patchNode(targetId, {
      ...appendVerification(current, recorded),
      lastPulse: Date.now(),
    })
    // The reading the codec admitted — never the model's claimed one — and
    // nothing else. See `docs/metrics.md`.
    track('check_landed', { reading: recorded.reading })
    await persistSourceCheckResearch({
      opId,
      target,
      search: res.search,
      citationDecisions,
      verification: recorded,
      prompts,
      spend: res.spend,
      startedAt: responseStartedAt,
      completedAt: responseCompletedAt,
      searchedAt: recorded.checkedAt,
      corpus: corpusAudit,
      live: liveAudit,
      liveIndexes: liveIndexAudit,
    })
    if (!opts.quiet) {
      useStore
        .getState()
        .toast(READING_LABEL[recorded.reading] + '.', verificationTone(recorded.reading))
    }
    return true
  } catch (err) {
    // A cancelled or failed pass still billed whatever it had already run.
    if (err instanceof ModelError && err.spent) {
      useStore.getState().addSpend(err.spent, opId)
    }
    const msg = reportError(err)
    const errorKind = kindOf(err)
    const completedAt = Date.now()
    const receiptSpend = (err instanceof ModelError ? err.spent : undefined) ??
      auditSpend ?? {
        model: attemptedModel,
        inputTokens: 0,
        outputTokens: 0,
        searches: 0,
      }
    useStore.getState().endOp(opId, [], { error: msg, errorKind, thinking: thought })
    await persistSourceCheckResearch({
      opId,
      target,
      search: auditSearch,
      citationDecisions: [],
      verification: null,
      prompts,
      spend: receiptSpend,
      startedAt: modelAttempted ? responseStartedAt : operationStartedAt,
      completedAt,
      searchedAt: completedAt,
      corpus: corpusAudit,
      live: liveAudit,
      liveIndexes: liveIndexAudit,
      outcome: {
        kind: errorKind === 'aborted' ? 'cancelled' : 'failed',
        code: errorKind,
        reason: msg,
      },
      interruptedRound: interruptedRound(errorKind, msg, completedAt),
    })
    if (msg !== 'Stopped.' && !opts.quiet) useStore.getState().toast(msg, 'bad')
    if (err instanceof ModelError && err.kind === 'auth')
      useStore.getState().askForKey(m.entry.provider)
    return false
  } finally {
    if (inFlight === controller) inFlight = null
  }
}

/** A reading is not a grade, so the toast tone reports weight rather than mood:
 *  only a refutation is loud, and nothing here is ever 'good'. */
function verificationTone(r: SourceReading): 'info' | 'warn' {
  return r === 'refuted' || r === 'unsourced' ? 'warn' : 'info'
}

/**
 * Which evidence surface the pass actually had.
 *
 * Set here, from what was declared and what was inserted — never by the model,
 * which cannot know whether the user's documents were in its prompt.
 *
 * A pass that declared no search read the user's documents and nothing else, so
 * it is `corpus` whatever it cited. It used to be `corpus` only once a passage was
 * admitted and `web` otherwise, so a documents-only check that cited nothing was
 * stored as a web check with no query, and every surface that reads this field
 * described a search that never ran (D321). Where a search was declared, an
 * admitted passage still makes it `both`: nothing a reader is shown tells `both`
 * from `web`, so that half stays as it was.
 */
function resolveScope(
  search: SearchOutcome | undefined,
  cites: { evidence: CorpusEvidence } | null,
): VerifyScope {
  if (!search) return 'corpus'
  return cites && cites.evidence.cites.length > 0 ? 'both' : 'web'
}

/* ── The survey of the field ──────────────────────────────────────────────
   V on the seed. The one search of the question itself — see `core/survey.ts`
   for why it is a survey and not a check of the seed. */

const SURVEY_PART_OK = new Set<string>(SURVEY_PARTS)

/** What the contract says this action is for, in the words the user pressed. */
const SURVEY_PURPOSE =
  'Survey what a search returns about the seed question: the bodies of evidence and the kind of study behind each, landmark results, what has been tried, where results disagree, and what has not been tested.'

export interface ReconciledSurvey {
  /** Everything but the three fields only the pass knows: its op, its moment and
   *  the model that actually answered. */
  survey: Omit<FieldSurvey, 'opId' | 'surveyedAt' | 'model'> | null
  /** Set when the pass has to be abandoned rather than recorded. What the user
   *  is told. */
  abandon?: string
}

/**
 * Model output → a survey we are willing to show. `reconcile`'s gate, with
 * `matchSeen` doing exactly the job it does there and nothing else: a URL the
 * search never returned is not a citation, whatever the model calls it, and the
 * count is kept rather than swallowed (invariant 1).
 *
 * What differs is what a deletion takes with it. A check has one reading resting
 * on all of its sources; a survey has many sentences, each resting on its own —
 * so a fabricated URL takes down the ROW it was holding up, and the rows set
 * aside are counted beside the URLs. An `untested` row names what came back
 * without something, and an absence has no page, so it alone may stand on none.
 * When no source survives at all, the rest goes too: prose with nothing returned
 * behind it is the one part nobody should read.
 *
 * There is no reading, so there is nothing here for `refutedIsEarned` to guard,
 * and no stance, so there is nothing for the judge to read.
 *
 * EXPORTED FOR THE SAME SEAM `reconcile` is: a replay of recorded output through
 * the admission path a real pass uses.
 */
export function reconcileSurvey(
  raw: unknown,
  question: string,
  search: SearchOutcome | undefined,
): ReconciledSurvey {
  // A pass with nothing to reconcile is a failed pass, not a survey of a field
  // with nothing in it — the one confusion the check was built to prevent.
  if (!search) return { survey: null, abandon: 'Nothing was searched.' }
  if (search.sources.length === 0) {
    const why = search.failures.length
      ? failureNote(search.failures[0]!.code)
      : 'The search came back empty, so there is nothing to survey yet.'
    return { survey: null, abandon: why }
  }

  const seen = search.sources
  const r = (raw ?? {}) as Record<string, unknown>
  const sources: SurveySource[] = []
  const admitted = new Set<string>()
  const refused = new Set<string>()
  /**
   * One proposed URL through the gate. `key` when it may be cited, `bounded` when
   * the search DID return it but the survey already holds its bound of sources.
   * `countOnly` still runs the gate — a fabrication on a row the bound drops is
   * still a fabrication, and the count is a fact about the pass — but admits
   * nothing, so no source enters the list without a row that rests on it.
   */
  const gate = (value: unknown, countOnly: boolean): { key?: string; bounded?: true } => {
    const url = str(value, 2000)
    if (!url || !isHttpUrl(url)) return {}
    const hit = matchSeen(url, seen)
    if (!hit) {
      refused.add(canonicalUrl(url))
      return {}
    }
    const key = canonicalUrl(hit.url)
    if (countOnly) return {}
    if (admitted.has(key)) return { key }
    if (sources.length >= SURVEY_SOURCE_CAP) return { bounded: true }
    admitted.add(key)
    // Never the model's title: the search's own, for `WebSource.title`'s reason.
    sources.push({ url: hit.url, title: hit.title, via: hit.via })
    return { key }
  }

  const retained = textRetention('admission')
  const rows: SurveyRow[] = []
  const perPart = new Map<SurveyPart, number>()
  let rowsSetAside = 0
  for (const [i, item] of (Array.isArray(r.rows) ? r.rows : []).entries()) {
    const o = (item ?? {}) as Record<string, unknown>
    const part = str(o.part, 20)
    if (!SURVEY_PART_OK.has(part)) continue
    const kind = part as SurveyPart
    const said = retained.text(`rows[${i}].text`, o.text, SURVEY_TEXT_CHARS.row)
    if (!said) continue
    const overBound = (perPart.get(kind) ?? 0) >= SURVEY_PART_CAP[kind]
    const refs: string[] = []
    let bounded = false
    for (const value of Array.isArray(o.sources) ? o.sources : []) {
      const out = gate(value, overBound || refs.length >= SURVEY_ROW_REFS)
      if (out.bounded) bounded = true
      if (out.key && !refs.includes(out.key)) refs.push(out.key)
    }
    // Past a bound the prompt stated, the row was never going to be shown —
    // its part's, or the survey's bound on distinct sources. Dropping it is not
    // setting evidence aside, so it is not counted as that.
    if (overBound || (bounded && refs.length === 0 && kind !== 'untested')) continue
    // Nothing the search returned stands behind it: set aside, and counted.
    if (kind !== 'untested' && refs.length === 0) {
      rowsSetAside++
      continue
    }
    const design = kind === 'untested' ? '' : str(o.design, SURVEY_TEXT_CHARS.design)
    perPart.set(kind, (perPart.get(kind) ?? 0) + 1)
    rows.push({ part: kind, text: said, ...(design ? { design } : {}), refs })
  }

  let summary: string | undefined =
    retained.text('summary', r.summary, SURVEY_TEXT_CHARS.summary) || undefined
  if (sources.length === 0) {
    rowsSetAside += rows.length
    rows.length = 0
    summary = undefined
  }
  const failures = [...new Set(search.failures.map((f) => failureNote(f.code)))]

  return {
    survey: {
      questionAtSurvey: question,
      questionHashAtSurvey: questionHash(question),
      ...(summary ? { summary } : {}),
      rows,
      sources,
      queries: search.queries.slice(0, SURVEY_QUERY_CAP),
      searches: search.queries.length,
      seen: seen.length,
      dropped: refused.size,
      rowsSetAside,
      ...(search.exhausted ? { partial: true } : {}),
      ...(failures.length ? { failures } : {}),
      ...retained.fields(),
    },
  }
}

/** What the pass says when it lands. Counts, never a finding: the survey is on
 *  the card, and a toast summarising it would be a second, shorter survey. */
function surveyToast(s: FieldSurvey): { text: string; tone: 'info' | 'warn' } {
  if (!s.sources.length) {
    return {
      text: s.dropped
        ? `Every source the survey named was set aside — the search never returned ${s.dropped === 1 ? 'it' : 'them'} — so nothing was kept.`
        : 'The survey named no source the search returned, so nothing was kept.',
      tone: 'warn',
    }
  }
  const set = s.dropped ? `, ${s.dropped} set aside` : ''
  return {
    text: `${STATUS_WORD.fieldSurveyed} — ${s.sources.length} returned source${s.sources.length === 1 ? '' : 's'}${set}. It is on the seed.`,
    tone: 'info',
  }
}

/**
 * Survey the field around the seed question. What V does on the seed.
 *
 * Priced, routed and refused exactly as a source check is, because in every
 * respect a reader pays for it IS one: the CHECK role's model and key, the
 * check's search budget, its pinned effort and its output ceiling. So the
 * estimate `VerbPrice` shows for `verify` before the press is this pass's
 * estimate too, and the run reserve's bound on one check bounds it. A routed role
 * with no key refuses (invariant 4), and a model with no web search refuses —
 * a survey that searched nothing would read as a field with nothing in it.
 *
 * Web only. The research rows and the user's documents feed a check's passage
 * pool, which is ranked against ONE claim; a question is not a claim to rank
 * passages against, and a survey that quietly narrowed to the user's own
 * documents would be a survey of their reading list, not of the field.
 *
 * Never in the demo, for `runVerify`'s reason. Never in a run: `checkableClaim`
 * and the agenda both refuse the seed, and a run spending a check here is a
 * design of its own — it would put the seed among the candidates the judge
 * orders (D106). A press, every time (invariant 9).
 *
 * Like a check, it takes no undo mark: a landed survey is an evidence event, and
 * undo carries it forward rather than rolling it back.
 */
export async function runSurvey(
  rootId: string,
  opts: { quiet?: boolean; maxRetries?: number; onStart?: (opId: string) => void } = {},
): Promise<boolean> {
  const s = useStore.getState()
  if (s.busy) {
    if (!opts.quiet) s.toast('One thought at a time — let the current pass finish.', 'warn')
    return false
  }
  const root = s.session.nodes[rootId]
  if (!root || root.parentId !== null) return false
  const m = resolveFor(s, 'check')
  if (!m) {
    if (!opts.quiet) {
      s.toast(
        `A survey of the field searches the live web, so it needs your ${providerFor(s, 'check').name} key.`,
        'warn',
      )
    }
    s.askForMissingKey('check', SURVEY_NEED)
    return false
  }
  if (m.entry.search === 'none') {
    if (!opts.quiet) {
      s.toast(
        `${m.entry.name} has no web search, so there is nothing for a survey of the field to read.`,
        'warn',
      )
    }
    return false
  }

  const question = root.claim
  const opId = s.beginOp('survey', [rootId], { model: stampOf(m.entry) })
  opts.onStart?.(opId)
  const controller = new AbortController()
  inFlight = controller
  let thought = ''
  let first: unknown = null
  try {
    useStore.getState().setSearching('searching: the field')
    const res = await streamOpObjects(opId, {
      researchContext: { session: s.session, purpose: SURVEY_PURPOSE },
      model: m,
      system: buildSurveySystem(m.entry.search, VERIFY_BUDGET),
      user: buildSurveyTurn(s.session, rootId),
      schema: SURVEY_SCHEMA,
      // Its own pinned row, held at the check's level — see `effort.ts`.
      effort: resolvePassEffort({ pass: 'survey', levels: s.effortLevels }),
      maxTokens: 20000,
      ...(opts.maxRetries !== undefined ? { maxRetries: opts.maxRetries } : {}),
      web: VERIFY_BUDGET,
      signal: controller.signal,
      onObject: (obj) => {
        if (first === null) first = obj
      },
      onThinking: (d) => {
        thought += d
        useStore.getState().setThought(thought)
      },
      onSearch: (ev) => {
        useStore
          .getState()
          .setSearching(
            ev.kind === 'query' ? `searching: ${ev.text}` : `reading: ${hostOf(ev.url)}`,
          )
      },
    })
    // Before anything can back out: a pass that searched and kept nothing still
    // searched.
    useStore.getState().addSpend(res.spend, opId)

    if (res.refused) {
      if (!opts.quiet) {
        useStore
          .getState()
          .toast(
            declinedMessage(
              'The model declined to survey this question.',
              res,
              res.spend.model,
              'check',
            ),
            'warn',
          )
      }
      useStore.getState().endOp(opId, [], { thinking: thought, ...declinedOf(res) })
      return false
    }

    const { survey, abandon } = reconcileSurvey(first, question, res.search)
    const current = useStore.getState().session.nodes[rootId]
    if (!survey || !current) {
      useStore.getState().endOp(opId, [], { thinking: thought, model: res.spend.model })
      if (!opts.quiet) {
        useStore.getState().toast(abandon ?? 'That survey produced nothing to record.', 'info')
      }
      return false
    }
    // The model that ANSWERED, never the one asked for (invariant 3).
    const recorded: FieldSurvey = {
      ...survey,
      opId,
      surveyedAt: Date.now(),
      model: res.spend.model,
    }
    // Patched BEFORE the op ends, so the dated board `endOp` settles is the one
    // that holds it — the knowledge replay reads a survey where it reads a check.
    useStore.getState().patchNode(rootId, {
      ...appendSurvey(current, recorded),
      lastPulse: Date.now(),
    })
    useStore.getState().endOp(opId, [], { thinking: thought, model: res.spend.model })
    if (!opts.quiet) {
      const said = surveyToast(recorded)
      useStore.getState().toast(said.text, said.tone)
    }
    return true
  } catch (err) {
    // A cancelled or failed pass still billed whatever it had already run.
    if (err instanceof ModelError && err.spent) useStore.getState().addSpend(err.spent, opId)
    const msg = reportError(err)
    useStore.getState().endOp(opId, [], { error: msg, errorKind: kindOf(err), thinking: thought })
    if (msg !== 'Stopped.' && !opts.quiet) useStore.getState().toast(msg, 'bad')
    if (err instanceof ModelError && err.kind === 'auth')
      useStore.getState().askForKey(m.entry.provider)
    return false
  } finally {
    if (inFlight === controller) inFlight = null
  }
}

/* ── Closing the experiment loop ──────────────────────────────────────────
   The one place in this app where a fact enters from outside the model. */

export interface OutcomeDraft {
  observed: string
  bearing: Outcome['bearing']
  /** Local midnight of the day chosen, or now when nothing was chosen. */
  at: number
  source?: string
  /** Where the user left the confidence dial. Not suggested by the tool. */
  confidence: number
}

function preparedFutureFor(
  session: Session,
  targetId: string,
  recordedAt: number,
): FalsificationFuture | undefined {
  return session.research?.falsificationPortfolios
    .filter(
      (portfolio) =>
        portfolio.targetId === targetId && portfolio.resolved?.outcomeRecordedAt === recordedAt,
    )
    .sort((a, b) => b.createdAt - a.createdAt)[0]?.resolved?.activeFuture
}

/**
 * Record what actually happened, and optionally re-derive from it.
 *
 * Two acts, deliberately separable. Recording spends nothing and calls no model;
 * re-deriving destroys the subtree and pays for a pass. A user who has just come
 * back from the lab wants the first and very often not yet the second, and a single
 * button that did both would make writing down a result a destructive action.
 *
 * Never writes `label`, `claim`, `reasoning` or `editedAt`, and never touches
 * `terminal`. An outcome is not a claim replacement: the claim stands exactly as
 * written and a fact is placed beside it.
 */
export async function recordOutcome(
  targetId: string,
  draft: OutcomeDraft,
  opts: { rederive: boolean },
): Promise<void> {
  const s = useStore.getState()
  if (s.busy) {
    s.toast('One thought at a time — let the current pass finish.', 'warn')
    return
  }
  const node = s.session.nodes[targetId]
  if (!node) return
  const observed = draft.observed.trim()
  if (observed.length < 8) {
    s.toast('Write down what you observed first — a sentence is enough.', 'warn')
    return
  }

  const now = Date.now()
  const outcome: Outcome = {
    observed: observed.slice(0, 4000),
    bearing: draft.bearing,
    // Never forward-dated. `recordedAt` is what a descendant is compared against
    // to decide whether it predates the result.
    at: Math.min(draft.at || now, now),
    recordedAt: now,
    claimAtRecord: node.claim,
    // Written once, on the FIRST record. An edit that refreshed it would print
    // "0.05 → 0.05" and delete the only number in the sentence that came from the
    // model.
    confidenceBefore: node.outcome?.confidenceBefore ?? node.confidence,
    ...(draft.source?.trim() ? { source: draft.source.trim().slice(0, 200) } : {}),
  }
  // The bearing and nothing else — the one measurement in the app, counted.
  track('outcome_recorded', { bearing: outcome.bearing })

  // Re-deriving needs a key; recording never does. The guard refuses only the
  // second half, so a result is never lost to a missing key. The reasoning role's
  // key, which is what `runOp` resolves, so a routed role with none keeps the
  // moves below rather than dropping them for a pass that cannot run.
  const canRederive = opts.rederive && resolveFor(s, 'reason') !== null

  // One step of history covers the patch and the optional subtree drop. NEVER
  // withdrawn: the patch alone is a real change, and a step back has to be able
  // to take a recorded result off the node.
  s.mark(OP_LABEL.outcome)

  if (canRederive) {
    const kids = s.session.order.filter((id) => s.session.nodes[id]?.parentId === targetId)
    for (const k of kids) useStore.getState().dropSubtree(k)
  }

  useStore.getState().patchNode(targetId, {
    outcome,
    confidence: clamp(draft.confidence),
    lastPulse: now,
  })

  // A prepared falsification portfolio is an executable branch plan, not a
  // static suggestion. Recording the observation deterministically activates
  // the matching future while retaining the other two in the audit record.
  // This reads only the person's bearing and never asks a model to reinterpret
  // what happened.
  const research = useStore.getState().session.research
  const matchingPortfolios = research?.falsificationPortfolios.filter(
    (portfolio) => portfolio.targetId === targetId,
  )
  if (research && matchingPortfolios?.length) {
    useStore.getState().setResearchState(
      upsertResearchEntities(
        research,
        'falsificationPortfolios',
        matchingPortfolios.map((portfolio) =>
          resolveFalsificationPortfolio(portfolio, outcome.bearing, outcome.recordedAt),
        ),
        now,
      ),
    )
  }

  if (!opts.rederive) {
    // Where this result stands in the one table that grades the model, read off
    // that table (`resultPointer`), with the press that opens it. The way back
    // is the card's Withdraw, directly under the result, and ⌘Z as ever.
    const st = useStore.getState()
    const node = st.session.nodes[targetId]
    const pointer = node
      ? resultPointer(
          calibrate(loadOutcomeRecords(st.session)),
          node,
          st.session.id === DEMO_SESSION_ID,
        )
      : null
    st.toast(
      `${STATUS_WORD.resultRecorded}${pointer ? ` · ${resultPointerLine(pointer)}` : ''}.`,
      'info',
      pointer && pointer.kind !== 'none'
        ? { label: 'See the ledger', run: () => useStore.getState().pokeLedger() }
        : undefined,
    )
    return
  }
  if (!canRederive) {
    useStore
      .getState()
      .toast(
        `${STATUS_WORD.resultRecorded}. Re-deriving needs your ${providerFor(s, 'reason').name} key — the moves below it are marked as predating it.`,
        'warn',
      )
    useStore.getState().askForMissingKey('reason', 'Re-deriving')
    return
  }

  const patched = useStore.getState().session.nodes[targetId]
  if (!patched) return
  const latestSession = useStore.getState().session
  await runOp(
    'outcome',
    targetId,
    buildOutcomeExtra(patched, preparedFutureFor(latestSession, targetId, outcome.recordedAt)),
    { mark: false },
  )
}

/** Re-derive from a result already on the node. The second half, on its own. */
export async function rederiveFromOutcome(targetId: string): Promise<void> {
  const s = useStore.getState()
  const node = s.session.nodes[targetId]
  if (!node?.outcome) return
  if (s.busy) {
    s.toast('One thought at a time — let the current pass finish.', 'warn')
    return
  }
  if (!resolveFor(s, 'reason')) {
    s.toast(
      `Re-deriving reasons the branch again, so it needs your ${providerFor(s, 'reason').name} key.`,
      'warn',
    )
    s.askForMissingKey('reason', 'Re-deriving')
    return
  }
  s.mark(OP_LABEL.outcome)
  const kids = s.session.order.filter((id) => s.session.nodes[id]?.parentId === targetId)
  for (const k of kids) useStore.getState().dropSubtree(k)
  await runOp(
    'outcome',
    targetId,
    buildOutcomeExtra(node, preparedFutureFor(s.session, targetId, node.outcome.recordedAt)),
    { mark: false },
  )
}

/**
 * Author a new branch head against a material source change.
 *
 * The old branch is retained as the before-side of the comparison. Deleting it
 * would erase the exact reasoning the time machine exists to preserve, and it
 * could also erase recorded-outcome boundaries or a two-parent synthesis that a
 * single pass from this node cannot recreate.
 */
export async function rederiveFromVerification(targetId: string): Promise<void> {
  const s = useStore.getState()
  const node = s.session.nodes[targetId]
  if (!node?.verification) return
  if (s.busy) {
    s.toast('One thought at a time — let the current pass finish.', 'warn')
    return
  }

  const affected = pendingEvidenceStaleForSource(s.session, targetId)
  if (!affected.length) {
    s.toast('Nothing below this claim predates a material source-record change.', 'info')
    return
  }
  const queue = evidenceRebaseQueue(s.session, targetId)
  const queuedBranch = queue.find((branch) => branch.action === 'rederive')
  if (!queuedBranch) {
    s.toast(
      `The ${affected.length} affected claim${affected.length === 1 ? '' : 's'} cross a synthesis or another branch. They remain queued for explicit review; a one-source pass cannot safely recreate them.`,
      'warn',
    )
    return
  }
  const covered = queuedBranch.affectedIds
  if (!resolveFor(s, 'reason')) {
    s.toast(
      `Rethinking needs your ${providerFor(s, 'reason').name} key. The affected claims remain marked.`,
      'warn',
    )
    s.askForMissingKey('reason', 'Rethinking')
    return
  }

  s.mark(OP_LABEL.rebase)
  useStore
    .getState()
    .toast(
      `Rethinking “${queuedBranch.root.label}” against the newer record. Its ${covered.length} earlier claim${covered.length === 1 ? ' stays' : 's stay'} on the board, dimmed, as the version to compare.`,
      'info',
    )
  let opId = ''
  const produced = await runOp(
    'rebase',
    targetId,
    buildEvidenceRebaseExtra(node, [queuedBranch.root]),
    {
      mark: false,
      // The old children are the prior version, not a list of territory this pass
      // must avoid. Hiding them prevents the ordinary "do not repeat" block from
      // forbidding the new head from restating a consequence that still follows.
      blindTo: 'children',
      onStart: (id) => {
        opId = id
      },
    },
  )
  if (!produced.length || !opId) {
    useStore.getState().dropMark(OP_LABEL.rebase)
    return
  }

  const completed = useStore.getState().session.ops.find((row) => row.id === opId)
  // `runOp` intentionally keeps nodes that streamed before a transport error.
  // They remain useful, but they are not a completed evidence rebase and must
  // not clear the pending state or suppress a retry.
  if (!completed?.finishedAt || completed.error || completed.refused) return

  const rebasedAt = Date.now()
  const current = useStore.getState().session.nodes[targetId]
  if (!current?.verification) return
  const verificationEventsNow = verificationEvents(current)
  const verificationEventIndex = Math.max(0, verificationEventsNow.length - 1)
  useStore.getState().patchNode(targetId, {
    evidenceRebases: [
      ...(current.evidenceRebases ?? []),
      {
        verificationAt: current.verification.checkedAt,
        ...(current.verification.opId ? { verificationOpId: current.verification.opId } : {}),
        verificationEventIndex,
        opId,
        rebasedAt,
        affected: covered,
        produced,
      },
    ],
    lastPulse: rebasedAt,
  })
  const remaining = pendingEvidenceStaleForSource(useStore.getState().session, targetId).length
  useStore
    .getState()
    .toast(
      `Rethought “${queuedBranch.root.label}”: ${produced.length} new claim${produced.length === 1 ? '' : 's'}. The earlier version stays on the board, dimmed, for comparison.${remaining ? ` ${remaining} more claim${remaining === 1 ? '' : 's'} still ${remaining === 1 ? 'needs' : 'need'} a second look.` : ''}`,
      remaining ? 'warn' : 'info',
    )
}

/**
 * Take a recorded result back off a node.
 *
 * Separate from undo because undo is a stack and this is a decision: a result
 * entered against the wrong node is a mistake the user notices later, by which
 * time it is twenty steps down. The reasoning derived from it is left alone —
 * deleting it would be a second destructive act attached to a correction.
 */
export function withdrawOutcome(targetId: string): void {
  const s = useStore.getState()
  const node = s.session.nodes[targetId]
  if (!node?.outcome) return
  s.mark('Withdraw result')
  useStore.getState().patchNode(targetId, {
    outcome: undefined,
    confidence: node.outcome.confidenceBefore,
    lastPulse: Date.now(),
  })
  useStore
    .getState()
    .toast('Result withdrawn, and the confidence put back where the model had it.', 'info')
}

/**
 * A bracketed reference from the scan, resolved to an end.
 *
 * `E2/n_abc` is foreign, matched ONLY against the digests this pass actually
 * printed — never against everything on disk, or a model that invented an
 * E-number would resolve to a real node in an exploration it was never shown.
 * A bare id is local. Anything that does not resolve returns null and the
 * candidate is dropped.
 */
function resolveEnd(ref: string, session: Session, used: Digest[]): BridgeEnd | null {
  const m = /^E(\d+)\/(.+)$/.exec(ref)
  if (m) {
    const d = used[Number(m[1]) - 1]
    if (!d) return null
    const n = d.nodes.find((x) => x.id === m[2])
    if (!n) return null
    return {
      where: 'foreign',
      ref: {
        sessionId: d.id,
        seed: d.seed,
        nodeId: n.id,
        label: n.label,
        claim: n.claim,
        at: Date.now(),
      },
    }
  }
  return session.nodes[ref] ? { where: 'local', nodeId: ref } : null
}

/* ── The adversarial panel ────────────────────────────────────────────────
   Three passes over one node, each under a different lens and blind to the others.
   Seat one goes first; the other two start together on its first stream event,
   when the prefix all three share is cached. The agreement between them is counted
   here, from the votes, by exact equality — never asked of a model. */

/**
 * Reason one node again under three lenses and record where they landed.
 *
 * Deliberately NOT routed through `runOp`. Three things make it a different shape:
 * it is one op spanning three requests, it produces at most one node from three
 * passes, and a panel that recorded three verdicts and produced no node DID change
 * the graph — so it must not take `runOp`'s "produced nothing, withdraw the step"
 * path.
 *
 * `opts` carries the same three fields `runOp` and `runVerify` take, and for the
 * same reasons: a run takes ONE step of history for its whole loop, its toasts are
 * noise to whoever is watching and invisible to whoever walked away, and a caller
 * cannot learn the op id any other way — the busy guard above returns before
 * `beginOp`, so a caller that indexed `session.ops` afterwards would read
 * `undefined` exactly when a user pressed a verb key in the gap between passes.
 */
export async function runPanel(
  targetId: string,
  opts: {
    mark?: boolean
    quiet?: boolean
    onStart?: (opId: string) => void
    /** Actual provider requests begun, including a seat that later fails. */
    onAttempt?: (attempts: number) => void
  } = {},
): Promise<boolean> {
  const s = useStore.getState()
  if (s.busy) {
    if (!opts.quiet) s.toast('One thought at a time — let the current pass finish.', 'warn')
    return false
  }
  const target = s.session.nodes[targetId]
  if (!target) return false
  if (target.parentId === null) {
    if (!opts.quiet) {
      s.toast('A panel rules on a claim derived from the seed, not on the seed itself.', 'info')
    }
    return false
  }
  const m = resolveFor(s, 'panel')
  if (!m) {
    if (!opts.quiet) {
      s.toast(
        `A panel runs three separate passes, so it needs your ${providerFor(s, 'panel').name} key.`,
        'warn',
      )
    }
    s.askForMissingKey('panel')
    return false
  }

  const lenses = panelLenses(s.lens)
  const temper = s.temper
  const retrieval = retrieveFor(target)
  // Built once and sent byte-identically to all three seats. This is the blindness
  // invariant, and it is also what makes the cache hit on passes two and three.
  const system = buildPanelSystem(
    s.session,
    targetId,
    retrieval ? buildCorpusBlock(retrieval) : undefined,
  )

  if (opts.mark !== false) s.mark(OP_LABEL.panel)
  const opId = s.beginOp('panel', [targetId], { model: stampOf(m.entry) })
  opts.onStart?.(opId)
  const controller = new AbortController()
  inFlight = controller

  // Per seat, indexed in lens order and read back in lens order below — whatever
  // order the seats FINISH in. The record a panel leaves must not depend on which
  // request the network happened to answer first.
  const seatVotes: (PanelVote | undefined)[] = lenses.map(() => undefined)
  // The raw dissent, kept beside the vote rather than inside it. `PanelVote` stores
  // no dissent on purpose: at most one objection becomes a node, and the other two
  // would then be a second copy of a reasoning move that is not on the graph.
  const dissents = new Map<Lens, unknown>()
  const traces: (string | undefined)[] = lenses.map(() => undefined)
  const thoughts = lenses.map(() => '')
  const started = lenses.map(() => false)
  const settled = lenses.map(() => false)
  const receipts = seatReceipts(opId)
  let attempts = 0
  /** A failure no other seat would get past — see `endsThePanel`: no seat starts
   *  after one. */
  let ended = false
  /** Why each seat did not return, where it failed on the provider's terms. Read
   *  when no seat returned at all, so the op records the failure it was rather
   *  than an empty panel. */
  const failed: (ModelError | undefined)[] = lenses.map(() => undefined)

  // The thought line follows ONE seat — the first still running, in lens order —
  // and moves on to the next when it finishes, carrying what that seat has
  // thought so far. Three seats writing one line by turns would interleave three
  // arguments a clause at a time.
  const shown = () => settled.findIndex((done, k) => !done && started[k])
  const showThought = (i: number) => {
    if (i < 0 || i !== shown()) return
    useStore
      .getState()
      .setThought(
        `Seat ${i + 1} of ${lenses.length} — ${LENS_LABEL[lenses[i]!]}. ${thoughts[i]!.slice(-560)}`,
      )
  }

  // How long each seat has heard nothing, once its quiet watch has spoken — said
  // on the line a lone pass's quiet uses, naming the seats, because the thought
  // above it follows one seat and the one that stalled is often another: seat two
  // can have sent nothing since it opened while seat one streams beside it. The
  // minutes are the fewest among them, so the line is true of every seat it names.
  const quietFor: (number | null)[] = lenses.map(() => null)
  let quietSaid: string | null = null
  const seatQuiet = (i: number, ms: number | null) => {
    quietFor[i] = ms
    const seats = quietFor.flatMap((gap, k) => (gap === null ? [] : [k + 1]))
    const line = seats.length
      ? seatsQuietLine(seats, Math.min(...seats.map((k) => quietFor[k - 1]!)))
      : null
    if (line === quietSaid) return
    quietSaid = line
    useStore.getState().setWaiting(line)
  }

  let releaseSeats = () => {}
  const seatOneOpen = new Promise<void>((resolve) => (releaseSeats = resolve))

  const runSeat = async (i: number): Promise<void> => {
    const lens = lenses[i]!
    let first: unknown = null
    started[i] = true
    try {
      attempts++
      opts.onAttempt?.(attempts)
      const res = await streamOpObjects(opId, {
        // Resolved ONCE, before seat one, and the same model for all three: the
        // feature's whole claim is that a disagreement is attributable to the
        // lens, and three seats on three models cannot support it.
        model: m,
        system,
        user: buildPanelTurn(s.session, targetId, lens, temper),
        schema: PANEL_SCHEMA,
        // The one call site that sets this. The three seats send a byte-identical
        // system block by design, which is app structure an adapter cannot see.
        cacheKey: `panel:${targetId}`,
        // Fixed for every seat, for the same reason the temper is held fixed —
        // see the `panel` row in `effort.ts`.
        effort: resolvePassEffort({ pass: 'panel', levels: s.effortLevels }),
        maxTokens: 9000,
        signal: controller.signal,
        // Seat one's response has begun, so the prefix all three share is cached:
        // the other two go now, rather than after seat one has finished.
        ...(i === 0 ? { onOpen: () => releaseSeats() } : {}),
        overlap: { ...receipts.seat(i), quiet: (ms) => seatQuiet(i, ms) },
        onObject: (obj) => {
          if (first === null) first = obj
        },
        onThinking: (d) => {
          thoughts[i] += d
          showThought(i)
        },
      })
      useStore.getState().addSpend(res.spend, opId)
      const vote = toVote(first, lens)
      if (vote) {
        seatVotes[i] = vote
        dissents.set(lens, (first as Record<string, unknown> | null)?.dissent)
      }
      // Clipped per seat: three full traces would blow the whole trace cap on one
      // op and leave nothing for the seats that ran last.
      const thought = thoughts[i]!
      if (thought.trim()) {
        traces[i] =
          `Seat ${i + 1} of ${lenses.length} — ${LENS_LABEL[lens]}.\n${thought.trim().slice(0, 1900)}`
      }
    } catch (err) {
      if (err instanceof ModelError && err.spent) {
        useStore.getState().addSpend(err.spent, opId)
      }
      if (err instanceof ModelError) failed[i] = err
      // A failure the other seats would meet too ends the panel — see
      // `endsThePanel`; one seat failing on its own is recorded as a seat that did
      // not return, which the display says out loud. Ending the panel means no
      // seat STARTS after this one: a seat already in flight lands or fails on its
      // own terms, and a Stop has aborted every one of them already, through the
      // controller they share.
      if (err instanceof ModelError && endsThePanel(err.kind)) {
        if (err.kind === 'auth') useStore.getState().askForKey(m.entry.provider)
        traces[i] =
          err.kind === 'auth' || err.kind === 'aborted'
            ? `Seat ${i + 1} of ${lenses.length} — ${LENS_LABEL[lens]}. Stopped: ${err.message}`
            : `Seat ${i + 1} of ${lenses.length} — ${LENS_LABEL[lens]}. Did not return: ${reportError(err)}`
        ended = true
        return
      }
      traces[i] =
        `Seat ${i + 1} of ${lenses.length} — ${LENS_LABEL[lens]}. Did not return: ${reportError(err)}`
    } finally {
      settled[i] = true
      receipts.settle(i)
      showThought(shown())
    }
  }

  try {
    // Seat one first, alone. Seats two and three start on its first stream event —
    // the moment its prompt's cache entry is readable — and together, because they
    // are blind to each other and read only as a set. A seat one that settles
    // without ever opening releases them then instead, which is where the old
    // sequence started seat two — unless it ended the panel.
    const seatOne = runSeat(0).finally(() => releaseSeats())
    await seatOneOpen
    const rest =
      ended || controller.signal.aborted ? [] : lenses.slice(1).map((_, k) => runSeat(k + 1))
    await Promise.all([seatOne, ...rest])

    const votes = seatVotes.filter((vote): vote is PanelVote => vote !== undefined)
    const thinking = traces.filter((line) => line !== undefined).join('\n\n')

    if (!votes.length) {
      // Why, where the seats failed on the provider's terms: the first in lens
      // order. It used to end as an empty panel, which a run counted as a move
      // that came back with "nothing new" — a claim about the claim, made by a
      // rate limit — and which `endsTheRun` never saw, so three seats refused for
      // an empty account did not stop the run. A Stop is still just a stop.
      const failure = failed.find((f) => f !== undefined && f.kind !== 'aborted')
      const why = failure ? reportError(failure) : null
      useStore.getState().endOp(opId, [], {
        thinking,
        ...(failure
          ? { error: `No seat returned a verdict. ${why}`, errorKind: failure.kind }
          : {}),
      })
      if (opts.mark !== false) useStore.getState().dropMark(OP_LABEL.panel)
      if (!opts.quiet) {
        useStore
          .getState()
          .toast(
            why
              ? `No seat returned a verdict, so nothing was recorded. ${why}`
              : 'No seat returned a verdict, so nothing was recorded.',
            why ? 'bad' : 'warn',
          )
      }
      return false
    }

    const panel: PanelCheck = {
      lenses,
      votes,
      temper,
      checkedAt: Date.now(),
      model: stampOf(m.entry),
    }
    useStore.getState().patchNode(targetId, { panel, lastPulse: Date.now() })

    // At most one objection lands as a node: the sharpest dissent when the panel
    // split on `fails`, or the strongest of the agreeing objections when it
    // converged on `fails`. A `holds-weaker` dissent is a condition, and a
    // condition is an annotation rather than a move in the argument.
    const land = objectionToLand(panel)
    const produced: string[] = []
    if (land) {
      const node = toDissentNode(dissents.get(land.lens), land, targetId, stampOf(m.entry))
      if (node) {
        useStore.getState().addNodes([node])
        produced.push(node.id)
      }
    }

    useStore.getState().endOp(opId, produced, { thinking })
    if (!opts.quiet) {
      useStore.getState().toast(panelSummary(panel), panelSplit(panel) ? 'warn' : 'info')
    }
    return true
  } catch (err) {
    // The catch this function never had. Every seat is wrapped individually, but
    // everything after the seat loop — `toVote`, `objectionToLand`, `patchNode`,
    // `addNodes` — ran naked inside a `try/finally`, so a throw there escaped with
    // `busy` still true and no writer left to clear it: the app wedged, with no
    // recovery short of restoring a session. Reached only by a bug, which is
    // exactly when the app must not become unusable.
    if (err instanceof ModelError && err.spent) useStore.getState().addSpend(err.spent, opId)
    const msg = reportError(err)
    useStore.getState().endOp(opId, [], {
      error: msg,
      errorKind: kindOf(err),
      thinking: traces.filter((line) => line !== undefined).join('\n\n'),
    })
    if (opts.mark !== false) useStore.getState().dropMark(OP_LABEL.panel)
    if (msg !== 'Stopped.' && !opts.quiet) useStore.getState().toast(msg, 'bad')
    return false
  } finally {
    if (inFlight === controller) inFlight = null
  }
}

/**
 * Whether one seat's failure means no seat should start after it.
 *
 * The panel's seats share one key, one account and one model, so some failures
 * are the panel's rather than the seat's: a rejected key, an empty account, a
 * save that did not land, and a rate limit the transport has already waited out
 * on its own terms — seats two and three sent into the same limit a second later
 * made six more doomed requests, and on a limit measured per minute, drew it
 * down. A server fault or a dropped connection can be one seat's bad luck, so
 * the others still go (D192). And a Stop is the reader's.
 *
 * Exhaustive over the kind with no `default`, for invariant 7's reason: a new
 * kind stops the build here until somebody decides.
 */
export function endsThePanel(kind: ModelError['kind']): boolean {
  switch (kind) {
    case 'auth':
    case 'quota':
    case 'storage':
    case 'rate':
    case 'aborted':
      return true
    case 'network':
    case 'server':
    case 'request':
    case 'timeout':
    case 'unknown':
      return false
  }
}

const VERDICTS = new Set<string>(['holds', 'holds-weaker', 'fails', 'undecidable'])

function toVote(raw: unknown, lens: Lens): PanelVote | null {
  if (!raw || typeof raw !== 'object') return null
  const r = raw as Record<string, unknown>
  const verdict = str(r.verdict, 20)
  if (!VERDICTS.has(verdict)) return null
  return {
    lens,
    verdict: verdict as PanelVerdict,
    because: str(r.because, 700),
    confidence: num(r.confidence, 0.5),
  }
}

/** The dissent, as a node. `origin: 'challenge'` because that is what it is — an
 *  objection — and a reader tracing provenance should find the honest verb. */
function toDissentNode(
  rawDissent: unknown,
  vote: PanelVote,
  parentId: string,
  model: ModelStamp,
): ReasonNode | null {
  if (!rawDissent || typeof rawDissent !== 'object') return null
  const raw = rawDissent as Record<string, unknown>
  const retained = textRetention('admission')
  const label = retained.text('label', raw.label, 90)
  const claim = retained.text('claim', raw.claim)
  if (!label || !claim) return null
  return makeNode({
    parentId,
    label,
    claim,
    reasoning: retained.text('reasoning', raw.reasoning),
    kind: 'contradiction',
    confidence: vote.confidence,
    novelty: num(raw.novelty, 0.4),
    tension: num(raw.tension, 0.8),
    evidence: retained.list('evidence', raw.evidence, 2),
    nextSteps: [],
    terminal: false,
    origin: 'challenge',
    model,
    ...retained.fields(),
  })
}

/* ── Synthesis ────────────────────────────────────────────────────────── */

/** How many other explorations one scan reads, and how much of each. Both are
 *  prompt-size limits: the corpus is what can blow this pass up. Both exported,
 *  because the pick list enforces the first with its own hands and prints the
 *  second in its explainer — a control that let seven be ticked over a scan
 *  that reads six would be the selection lying by one, and a sentence carrying
 *  its own copy of a threshold is how the two drift. */
export const SCAN_EXPLORATIONS = 6
export const SCAN_PER_EXPLORATION = 14

/** How many claims (the seed not counted) a scan needs before it will read the
 *  graph: within this graph, and across explorations, where most of the pool
 *  comes from elsewhere but a candidate still needs somewhere to land. Exported
 *  for the Overview's guard, which prints the number beside a press it disables
 *  — the same reason as the two above. */
export const SCAN_MIN_CLAIMS = 6
export const SCAN_MIN_CLAIMS_ACROSS = 3

/** Characters of a candidate's rationale kept, ending where a sentence does
 *  (`clipToSentence`). The prompt asks for sixty words, which fits well inside. */
const BRIDGE_WHY_CHARS = 700

export type ScanScope = 'within' | 'across'

/** Output tokens a scan may draw, reasoning included. Its price (`projectScanUsd`
 *  in `run.ts`) charges all of them, because a cap is the one output figure that
 *  cannot be exceeded. */
export const SCAN_MAX_TOKENS = 12_000

/** The explorations a scan would read beside this one, and how many it could
 *  have: one reader for the pass and for its price. */
function scanPoolOf(sessionId: string, among?: readonly string[]) {
  return {
    candidates: listScanCandidates(sessionId, SCAN_EXPLORATIONS, among),
    others: listRecent().filter((e) => e.id !== sessionId && e.nodes > 0).length,
  }
}

function acrossCorpus(
  used: Digest[],
  pool: ReturnType<typeof scanPoolOf>,
  unreadable: number,
  picked: boolean,
): string {
  return buildScanCorpus(used, {
    skipped: Math.max(0, pool.others - pool.candidates.length),
    unreadable,
    picked,
  })
}

/**
 * What a scan across explorations would print about them, read now — or
 * undefined where nothing would be read, which is where the pass refuses.
 *
 * The same pool and the same digests the pass reads, without the pass's yields:
 * this is for the scan's PRICE, which a panel computes once per pick rather than
 * per frame, and a figure built from a different reading would price a prompt
 * nobody sends.
 */
export function scanAcrossBlock(sessionId: string, among?: readonly string[]): string | undefined {
  const pool = scanPoolOf(sessionId, among)
  const used: Digest[] = []
  let unreadable = 0
  for (const c of pool.candidates) {
    const d = loadDigest(c.id, SCAN_PER_EXPLORATION)
    if (!d || !d.nodes.length) unreadable++
    else used.push(d)
  }
  return used.length ? acrossCorpus(used, pool, unreadable, !!among) : undefined
}

/** What a scan is built from: the graph, and the reading it is scanned under. */
type ScanReading = { lens: Lens; temper: Temper; session: Session }

/** The two messages a scan sends, from the ONE builder the pass and its price
 *  both call — so the figure on the press is the size of what the press sends. */
export function scanPrompt(s: ScanReading, across?: string): { system: string; user: string } {
  // The pairs the GRAPH nominates, before the model sees anything. Structure and
  // vocabulary only (`core/pairs.ts`), and pairs already proposed are excluded so a
  // scan does not re-offer what is in the list — the candidates are a suggestion the
  // model still has to judge, not a shortcut past it.
  //
  // Computed HERE rather than at the call site, because this is the ONE builder the
  // pass and its price both read: a block added to the pass alone would make the
  // figure on the press smaller than what the press sends.
  const structural = candidatePairs(s.session, { exclude: s.session.bridges.map(pairKey) })
  return {
    system: buildSystem(s.lens, s.temper, s.session.frame, s.session.brief),
    user: buildBridgeTurn(s.session, across, structural),
  }
}

/** The text a scan sends, whole — both messages as they leave, the research
 *  contract's block included, and the schema it asks for — for `projectScanUsd`
 *  to price. A scan's op has no targets, so its contract names none. */
export function scanRequestText(s: ScanReading, across?: string): string {
  const { system, user } = scanPrompt(s, across)
  const sent = preparedTurn(researchContractFor(s.session, 'bridge', []), user)
  return `${system}\n${sent}\n${JSON.stringify(BRIDGES_SCHEMA.schema)}`
}

/**
 * The charge a finished pass drew, as a toast suffix — ` · est. +$0.12 · $0.40 so
 * far`, in `endOp`'s own words (`chargeLine`) — or nothing when the pass has no
 * priced row to speak from, with whether the exploration is now past the cap, for
 * the toast's tone. The scan, the link search, the prior-art search and the field
 * sweep carry their charge inside their own ending sentence (their result IS a
 * sentence), where every other verb's charge is toasted centrally by `endOp`.
 */
function chargeSuffix(opId: string): { text: string; over: boolean } {
  const s = useStore.getState()
  const op = s.session.ops.find((o) => o.id === opId)
  const p = op?.spend ? priceOf(op.spend.model.id) : undefined
  if (!op?.spend || !p) return { text: '', over: false }
  const { line, over } = chargeLine(
    estimateUsd(op.spend, p),
    estimateSpendUsd(s.session.spend).usd,
    parseCap(s.runCeiling),
  )
  return { text: ` · ${line}`, over }
}

export async function scanForBridges(
  opts: {
    scope?: ScanScope
    /**
     * The explorations an `across` scan may read, by id — the user's own pick.
     * Absent, recency decides, which is what absent has always meant; ignored on
     * a within scan. An EMPTY pick is refused loudly rather than silently
     * widened: a scan that read six explorations somebody had just unticked
     * would be the selection control lying.
     */
    among?: readonly string[]
    /**
     * Suppress this pass's toasts AND its rail steal.
     *
     * Both, because for an autonomous run they are one gesture: a run whose final
     * act is to force open a descending ranking of its own candidates has composed
     * a discovery claim, whatever the caption under the list says. A run states
     * the count and leaves opening the tab to the person.
     */
    quiet?: boolean
    /** Same contract as `runOp`'s: fires with the op id if and only if this pass
     *  got past every guard. A caller that instead read the last op off the ledger
     *  would attribute someone else's pass to itself. */
    onStart?: (opId: string) => void
  } = {},
): Promise<number> {
  const s = useStore.getState()
  if (s.busy) return 0
  const m = resolveFor(s, 'scan')
  if (!m) {
    if (!opts.quiet) {
      s.toast(
        `Synthesis needs a live model. Add your ${providerFor(s, 'scan').name} key to run it.`,
        'warn',
      )
      s.askForMissingKey('scan')
    }
    return 0
  }
  const scope = opts.scope ?? 'within'
  const live = s.session.order.filter((id) => s.session.nodes[id]?.parentId !== null)
  // A cross-exploration scan needs less local material, because most of the pool
  // comes from elsewhere — but it still needs somewhere to land.
  const floor = scope === 'across' ? SCAN_MIN_CLAIMS_ACROSS : SCAN_MIN_CLAIMS
  if (live.length < floor) {
    if (!opts.quiet)
      s.toast('Grow the graph a little more first — synthesis needs material.', 'info')
    return 0
  }

  // Read the other explorations before spending anything, so a scope with nothing
  // to read costs nothing and says so.
  let across: string | undefined
  let used: Digest[] = []
  let readExplorations = 0
  if (scope === 'across') {
    if (opts.among && opts.among.length === 0) {
      s.toast('Every exploration is unticked — tick at least one for the scan to read.', 'info')
      return 0
    }
    const pool = scanPoolOf(s.session.id, opts.among)
    let unreadable = 0
    for (const c of pool.candidates) {
      const d = loadDigest(c.id, SCAN_PER_EXPLORATION)
      if (!d || !d.nodes.length) {
        unreadable++
        continue
      }
      used.push(d)
      // Yield between documents: parsing six stored explorations on the main
      // thread is enough work to drop frames while the canvas is still animating.
      await sleep(0)
    }
    if (!used.length) {
      // Two different facts, told apart: an empty library, and a pick whose
      // documents all failed to read back — "start a second exploration" is
      // advice that insults the person who has six and ticked three.
      s.toast(
        opts.among
          ? 'None of the ticked explorations could be read back, so there is nothing to scan against.'
          : 'There is nothing else in the library to scan against yet. Start a second exploration and try again.',
        'info',
      )
      return 0
    }
    readExplorations = used.length
    across = acrossCorpus(used, pool, unreadable, !!opts.among)
  }

  const opId = s.beginOp('bridge', [], { model: stampOf(m.entry) })
  opts.onStart?.(opId)
  const controller = new AbortController()
  inFlight = controller
  let thought = ''

  try {
    const found: Bridge[] = []
    const res = await streamOpObjects(opId, {
      model: m,
      ...scanPrompt(s, across),
      schema: BRIDGES_SCHEMA,
      // Asked for, not guaranteed: on a model whose ladder stops below this, the
      // dispatcher lowers it and the picker says so in a sentence.
      effort: resolvePassEffort({ pass: 'scan', levels: s.effortLevels }),
      maxTokens: SCAN_MAX_TOKENS,
      signal: controller.signal,
      onObject: (obj) => {
        const r = obj as Record<string, unknown>
        const cur = useStore.getState().session
        const a = resolveEnd(str(r.aRef, 80), cur, used)
        const b = resolveEnd(str(r.bRef, 80), cur, used)
        // A reference that does not resolve is dropped rather than guessed at: a
        // guess would attach a synthesis to a claim it was not derived from.
        if (!a || !b) return
        if (endKey(a) === endKey(b)) return
        // Both ends foreign has nowhere to land in this graph.
        if (a.where === 'foreign' && b.where === 'foreign') return
        if (a.where === 'local' && b.where === 'local') {
          // A parent/child pair is a continuation, not a synthesis.
          if (cur.nodes[a.nodeId]!.parentId === b.nodeId) return
          if (cur.nodes[b.nodeId]!.parentId === a.nodeId) return
        }
        const title = str(r.title, 140)
        if (!title) return
        // The arithmetic that would have chosen this pair, recomputed for
        // whichever pair the model actually returned — including one it
        // nominated itself. A card can then say why a pair is here in numbers
        // nobody has to take on trust. Local ends only: a foreign end has no
        // position in this tree to measure a distance from.
        const basis =
          a.where === 'local' && b.where === 'local' ? basisOf(cur, a.nodeId, b.nodeId) : null
        found.push({
          id: uid('b'),
          a,
          b,
          title,
          // At the cap, back to the last sentence that fits: a count-cut rationale
          // ended mid-word on five of eleven candidates a study read.
          why: clipToSentence(typeof r.why === 'string' ? r.why : '', BRIDGE_WHY_CHARS),
          score: num(r.score, 0.5),
          ...(basis ? { basis } : {}),
          createdAt: Date.now(),
        })
      },
      onThinking: (d) => {
        thought += d
        useStore.getState().setThought(thought)
      },
    })

    useStore.getState().addSpend(res.spend, opId)
    useStore.getState().addBridges(found)
    useStore.getState().endOp(opId, [], {
      thinking: thought,
      // Nothing in `produced` or `targetIds` distinguishes a within-graph scan from
      // an across one, so the ledger would otherwise claim the wrong provenance.
      ...(readExplorations ? { readExplorations } : {}),
    })

    // What this scan read, noted AFTER endOp so a demo id re-minted by beginOp is
    // the id the note lands under. The scan control compares against this to say
    // "same graph, same reading" before anything is spent again.
    {
      const after = useStore.getState()
      after.noteScan(after.session.id, {
        nodes: after.session.order.length,
        temper: after.temper,
        modelId: m.entry.id,
        // Keyed from the digests this pass actually printed, never from the ask:
        // a candidate that could not be read back was not part of the reading.
        pool: scanPoolKey(used),
      })
    }

    if (!opts.quiet) {
      const charge = chargeSuffix(opId)
      if (found.length) {
        useStore.getState().setRightTab('breakthroughs')
        useStore.getState().setRail(true)
        useStore
          .getState()
          .toast(
            `${found.length} synthesis candidate${found.length > 1 ? 's' : ''} found${charge.text}.`,
            charge.over ? 'warn' : 'good',
          )
      } else {
        useStore
          .getState()
          .toast(
            `No pair fused cleanly${charge.text}. Grow more branches and try again.`,
            charge.over ? 'warn' : 'info',
          )
      }
    }
    return found.length
  } catch (err) {
    if (err instanceof ModelError && err.spent) {
      useStore.getState().addSpend(err.spent, opId)
    }
    const msg = reportError(err)
    useStore.getState().endOp(opId, [], { error: msg, errorKind: kindOf(err), thinking: thought })
    if (msg !== 'Stopped.' && !opts.quiet) useStore.getState().toast(msg, 'bad')
    return 0
  } finally {
    if (inFlight === controller) inFlight = null
  }
}

/* ── sharpening a seed ───────────────────────────────────────────────────
   The cheapest pass in the app, run before a graph exists. */

/** One sharper formulation of the person's own idea, as data. */
export interface SharpSeed {
  claim: string
  frame: Frame
  why: string
}

/**
 * Sharpen a seed into up to three versions that could be wrong.
 *
 * Runs on words the person typed, before any graph exists, and lands NOTHING:
 * it returns the candidates and the composer prints them as chips. Adopting one
 * is the person's press and it only fills the box — the seed that gets explored
 * is still the text in front of them, which is the same rule `core/promote.ts`
 * keeps for an answer: a model's sentence is an input a person may take, never
 * an output that arrives on its own.
 *
 * Cheap by construction — `effort.ts` pins this pass at `low` and the ceiling is
 * 900 tokens — because a sharpener that costs a reasoning pass is one nobody
 * runs before deciding what to explore.
 */
export async function sharpenSeed(
  seed: string,
  frame: Frame = 'claim',
  opts: { quiet?: boolean } = {},
): Promise<SharpSeed[]> {
  const s = useStore.getState()
  if (s.busy) {
    if (!opts.quiet) s.toast('One thought at a time — let the current pass finish.', 'warn')
    return []
  }
  const words = seed.trim()
  if (words.length < 12) {
    if (!opts.quiet)
      s.toast('Write a little more first — there is nothing here to sharpen yet.', 'info')
    return []
  }
  const m = resolveFor(s, 'reason')
  if (!m) {
    if (!opts.quiet) {
      s.toast(
        `Sharpening is one small pass, so it needs your ${providerFor(s, 'reason').name} key.`,
        'warn',
      )
      s.setSetup('settings')
    }
    return []
  }

  // An op, so the pass appears in the ledger like every other one that spends —
  // even though it produces no node. `beginOp` takes the lock, which is what
  // stops a second press while this one is in flight.
  const opId = s.beginOp('sharpen', [])
  const controller = new AbortController()
  inFlight = controller
  const out: SharpSeed[] = []
  try {
    const res = await streamOpObjects(opId, {
      model: m,
      system: buildSharpenSystem(),
      user: buildSharpenTurn(words, frame),
      schema: SHARPEN_SCHEMA,
      rootArrayKey: 'candidates',
      effort: resolvePassEffort({ pass: 'sharpen', levels: s.effortLevels }),
      maxTokens: 900,
      signal: controller.signal,
      onObject: (obj) => {
        const r = obj as Record<string, unknown>
        const claim = str(r.claim, 400)
        const f = str(r.frame, 20)
        // A candidate with no claim is nothing to adopt; an unknown frame would
        // set a reading the app has no rules for, so it reads as `claim` — the
        // same default absence has meant since frames existed.
        if (!claim || out.length >= SHARPEN_MAX) return
        out.push({
          claim,
          frame: f === 'question' || f === 'goal' ? f : 'claim',
          why: str(r.why, 240),
        })
      },
      onThinking: () => {},
    })
    useStore.getState().addSpend(res.spend, opId)
    useStore.getState().endOp(opId, [])
    if (!out.length && !opts.quiet) {
      useStore
        .getState()
        .toast(
          `Nothing sharper came back${chargeSuffix(opId)} — your own wording may already be it.`,
          'info',
        )
    }
    return out
  } catch (err) {
    if (err instanceof ModelError && err.spent) useStore.getState().addSpend(err.spent, opId)
    const msg = reportError(err)
    useStore.getState().endOp(opId, [], { error: msg, errorKind: kindOf(err) })
    if (msg !== 'Stopped.' && !opts.quiet) useStore.getState().toast(msg, 'bad')
    return []
  } finally {
    if (inFlight === controller) inFlight = null
  }
}

/* ── The programme ───────────────────────────────────────────────────────
   One pass proposes a list of moves; a person approves it; a run walks it. */

/**
 * Propose the move list a programme run would walk, for a person to approve.
 *
 * One paid pass, and it lands NOTHING on the graph and starts no run: the list
 * goes into `programmeDraft`, the sheet prints it beside the budget it implies,
 * and only the person's press hands it to `startRun` — where the loop walks it
 * in order and chooses nothing (invariant 6 holds over that run: the list is an
 * approved order, not a rating; invariant 9 holds here: the spend the list
 * implies sits behind one explicit press). Confined to a candidate's cones when a
 * bridge id is given, so the proposal is a list over the branches under it.
 *
 * Every step is validated as it streams — the id must resolve, the verb must be
 * one the loop can dispatch, a check must not name the seed — and a step that
 * fails is dropped rather than repaired, for the reason `scanForBridges` drops an
 * unresolvable end: a repaired step is a step nobody proposed.
 */
export async function planProgramme(
  opts: { maxSteps?: number; bridgeId?: string; quiet?: boolean } = {},
): Promise<Programme | null> {
  const s = useStore.getState()
  if (s.busy) {
    if (!opts.quiet) s.toast('One thought at a time — let the current pass finish.', 'warn')
    return null
  }
  const m = resolveFor(s, 'reason')
  if (!m) {
    if (!opts.quiet) {
      s.toast(
        `A programme is one reasoning pass, so it needs your ${providerFor(s, 'reason').name} key.`,
        'warn',
      )
      s.setSetup('settings')
    }
    return null
  }
  if (s.session.order.length < 2) {
    if (!opts.quiet)
      s.toast('A programme is a list of moves over a graph. Explore an idea first.', 'info')
    return null
  }
  let within: Set<string> | undefined
  if (opts.bridgeId !== undefined) {
    const b = s.session.bridges.find((x) => x.id === opts.bridgeId)
    const st = b ? bridgeStanding(b, s.session.nodes) : null
    if (!b || !st?.nodeId) {
      if (!opts.quiet) {
        s.toast(
          'Realise the synthesis first — a programme about a candidate is a list over the branches under it.',
          'warn',
        )
      }
      return null
    }
    const roots = conesOf(b, st.nodeId)
    within = new Set(s.session.order.filter((id) => underAny(s.session, id, roots)))
  }
  const max = Math.max(1, Math.min(PLAN_STEPS_MAX, Math.trunc(opts.maxSteps ?? PLAN_STEPS_MAX)))

  const opId = s.beginOp('plan', [])
  const controller = new AbortController()
  inFlight = controller
  let thought = ''
  const steps: ProgrammeStep[] = []

  try {
    const res = await streamOpObjects(opId, {
      model: m,
      system: buildSystem(s.lens, s.temper, s.session.frame, s.session.brief),
      user: buildPlanTurn(s.session, max, within),
      schema: PLAN_SCHEMA,
      rootArrayKey: 'steps',
      effort: resolvePassEffort({ pass: 'plan', levels: s.effortLevels }),
      maxTokens: 6000,
      signal: controller.signal,
      onObject: (obj) => {
        const r = obj as Record<string, unknown>
        const cur = useStore.getState().session
        const id = normalizeNodeRef(str(r.ref, 80))
        const n = cur.nodes[id]
        const verb = str(r.verb, 20)
        if (!n || !(PLAN_VERBS as readonly string[]).includes(verb)) return
        if (verb === 'verify' && n.parentId === null) return
        if (within && !within.has(id)) return
        if (steps.length >= max) return
        if (steps.some((x) => x.nodeId === id && x.verb === verb)) return
        steps.push({ nodeId: id, verb: verb as ProgrammeStep['verb'], why: str(r.why, 300) })
      },
      onThinking: (d) => {
        thought += d
        useStore.getState().setThought(thought)
      },
    })

    useStore.getState().addSpend(res.spend, opId)
    useStore.getState().endOp(opId, [], { thinking: thought })
    if (!steps.length) {
      if (!opts.quiet) {
        useStore
          .getState()
          .toast(
            `The pass proposed no step it could name${chargeSuffix(opId)}. Nothing to approve.`,
            'info',
          )
      }
      return null
    }
    const programme: Programme = { steps, model: stampOf(m.entry), proposedAt: Date.now() }
    useStore.getState().setProgrammeDraft(programme)
    if (!opts.quiet) {
      useStore
        .getState()
        .toast(
          `${steps.length} step${steps.length === 1 ? '' : 's'} proposed${chargeSuffix(opId)}. Nothing runs until you approve the list.`,
          'good',
        )
    }
    return programme
  } catch (err) {
    if (err instanceof ModelError && err.spent) {
      useStore.getState().addSpend(err.spent, opId)
    }
    const msg = reportError(err)
    useStore.getState().endOp(opId, [], { error: msg, errorKind: kindOf(err), thinking: thought })
    if (msg !== 'Stopped.' && !opts.quiet) useStore.getState().toast(msg, 'bad')
    return null
  } finally {
    if (inFlight === controller) inFlight = null
  }
}

/**
 * The `bridge_rung` event, after one of the two passes that move a candidate.
 * Carries the rung it now stands on and whether it stopped — closed sets both —
 * and never the pair. See `docs/metrics.md`.
 */
function trackRung(bridgeId: string): void {
  const s = useStore.getState().session
  const b = s.bridges.find((x) => x.id === bridgeId)
  if (!b) return
  const st = bridgeStanding(b, s.nodes)
  track('bridge_rung', { rung: st.rung, stop: st.stop ?? 'none' })
}

/* ── The link scan ───────────────────────────────────────────────────────
   Three searches over one candidate pair, and arithmetic over what came back.

   The one pass in this app whose headline finding is a COUNT: `core/link.ts`
   decides the reading, this file only fetches the piles and records the result.
   No model is asked whether the pair is connected, novel, or worth pursuing —
   for the same reason no model is asked how much a panel agreed. */

/** One side gather. Smaller than a source check's: this pass is mapping a
 *  literature, not adjudicating a claim, and breadth beats depth for it. */
export const LINK_GATHER_BUDGET = { maxSearches: 4, maxFetches: 2 }

/** The conjunction gather, which is the one that has to fail. It gets the
 *  largest allowance in the app, because an under-searched null is the error
 *  this whole feature is built to avoid. */
export const LINK_CONJUNCTION_BUDGET = { maxSearches: 6, maxFetches: 3 }

/**
 * Live web requests one link scan can make, worst case.
 *
 * The figure any spend projection has to charge for, and it is a worst case in
 * one direction only: a scan that reads `already-made` off the conjunction
 * returns before the two side gathers run, and bills roughly a third of this.
 */
export const SEARCHES_PER_LINK_SCAN =
  LINK_CONJUNCTION_BUDGET.maxSearches +
  LINK_CONJUNCTION_BUDGET.maxFetches +
  2 * (LINK_GATHER_BUDGET.maxSearches + LINK_GATHER_BUDGET.maxFetches)

/**
 * The three searches of a link scan, by the names a reader is told when one of
 * them does not finish. A side is named by its claim's label, clipped, because
 * the sentence it heads is stored under the codec's 200-character line cap.
 */
export const LINK_STEP = {
  both: 'The search for both claims together',
  side: (label: string) =>
    `The search around “${label.length > 48 ? `${label.slice(0, 47).trimEnd()}…` : label}”`,
}

/** The codec's cap on one stored failure line (`asStoredLinkage`). */
const LINK_FAILURE_CHARS = 200

/**
 * One sentence for a link-scan search that did not finish, or null for one that did.
 *
 * The scan used to file every failure as its own line under a note that blamed
 * "the search" — so a scan whose fourteen searches all came back and whose one
 * gather then ran out of budget read, three times over, as a search that had not
 * completed. What a reader can act on is WHICH of the three searches stopped, how
 * much of it came back first, and what stopped it — once. So each unfinished step
 * is one sentence that says exactly that, with a repeated failure counted rather
 * than repeated; the reading's own rule (`assess`, `core/link.ts`) is unchanged,
 * and any failure still makes the null unsound.
 *
 * "Came back" counts the step's search actions that completed, where the adapter
 * reports them (`rounds`); elsewhere, its queries less its failed searches.
 */
export function linkStepFailure(step: string, search: SearchOutcome): string | null {
  const what = new Map<string, number>()
  for (const f of search.failures) {
    const note = failureNote(f.code)
    what.set(note, (what.get(note) ?? 0) + 1)
  }
  if (search.exhausted) what.set('The search was cut off before it finished.', 1)
  if (!what.size) return null
  const back = search.rounds
    ? search.rounds.filter((r) => r.completion.kind === 'complete').length
    : Math.max(0, search.queries.length - search.failures.filter((f) => f.tool === 'search').length)
  const ran = back ? `${back} search${back === 1 ? '' : 'es'} came back` : 'no search came back'
  const phrases = [...what].map(
    ([note, n]) =>
      `${note.charAt(0).toLowerCase()}${note.slice(1).replace(/\.$/, '')}${n > 1 ? ` (${n} times)` : ''}`,
  )
  const said = `${step}: ${ran}, and ${phrases.join('; ')}.`
  if (said.length <= LINK_FAILURE_CHARS) return said
  const steps = [...what.values()].reduce((a, b) => a + b, 0)
  return `${step}: ${ran}, and ${steps} step${steps === 1 ? '' : 's'} did not finish.`
}

/** What a bridge end says, whichever side of the machine it lives on. A foreign
 *  end carries its own copy precisely so this needs no graph to resolve it. */
function endText(
  e: BridgeEnd,
  nodes: Record<string, ReasonNode>,
): { label: string; claim: string; reasoning?: string } | null {
  if (e.where === 'foreign') return { label: e.ref.label, claim: e.ref.claim }
  const n = nodes[e.nodeId]
  return n ? { label: n.label, claim: n.claim, reasoning: reasoningOf(n) } : null
}

/** A harvested search, reduced to what the arithmetic reads. `via: 'fetch'`
 *  carries text on one provider and not the other; `LinkDoc.text` being absent
 *  is exactly what makes a document score against the titles floor. */
const toPile = (out: SearchOutcome | undefined): LinkPile => ({
  docs: (out?.sources ?? []).map((s) => ({
    url: s.url,
    title: s.title,
    ...(s.text ? { text: s.text } : {}),
  })),
  // The same soundness condition `Verification.partial` and `failures` carry: a
  // null read off a search that errored or was cut off is not a null.
  sound: !!out && !out.failures.length && !out.exhausted,
})

const toJoining = (d: LinkDoc, out: SearchOutcome | undefined): JoiningSource => ({
  url: d.url,
  title: d.title,
  via: out?.sources.find((s) => canonicalUrl(s.url) === canonicalUrl(d.url))?.via ?? 'search',
})

/** What needed the key, when a link scan or a synthesis is refused for one: the
 *  key card's sentence after the refusal, and the Overview press's title before
 *  it (`SURVEY_NEED`'s pairing), so the two name one route in one sentence. */
export const LINK_SCAN_NEED = 'A link scan'
export const SYNTHESIS_NEED = 'Realising a synthesis'
/** The conjecture line's passes that name themselves on the key card (D286). A
 *  conjecture pass is a reasoning pass and says "Reasoning", as a move does. */
export const PRIOR_ART_NEED = 'A prior-art search'
export const SWEEP_NEED = 'A field sweep'
export const EXECUTABLE_NEED = 'An executable test'

/**
 * Search whether anybody has already joined the two ends of a candidate.
 *
 * Three gathers rather than one, because `SearchOutcome` reports the queries a
 * pass ran and the sources it returned but never which source came from which
 * query — so one pass searching all three things would hand the arithmetic a
 * single undifferentiated pile, and the whole test is a comparison BETWEEN
 * piles. Three passes is the cost of attributable results.
 */
export async function runLinkScan(
  bridgeId: string,
  opts: { quiet?: boolean } = {},
): Promise<boolean> {
  const s = useStore.getState()
  if (s.busy) {
    if (!opts.quiet) s.toast('One thought at a time — let the current pass finish.', 'warn')
    return false
  }
  const bridge = s.session.bridges.find((b) => b.id === bridgeId)
  if (!bridge) return false
  if (!bridgeReadable(bridge, s.session.nodes)) {
    if (!opts.quiet)
      s.toast('One side of that pair has been pruned, so there is nothing to search.', 'warn')
    return false
  }
  const m = resolveFor(s, 'check')
  if (!m) {
    if (!opts.quiet) {
      s.toast(
        `A link scan searches the live web, so it needs your ${providerFor(s, 'check').name} key.`,
        'warn',
      )
      s.askForMissingKey('check', LINK_SCAN_NEED)
    }
    return false
  }
  // No search, no scan — and unlike a source check there is no corpus fallback.
  // The test is over what a WEB search did not return; running it against the
  // user's own documents would report that their notes do not join two claims,
  // which is true of almost any two claims and means nothing.
  if (m.entry.search === 'none') {
    if (!opts.quiet) {
      s.toast(
        `${m.entry.name} has no web search, so there is no record for a link scan to come back empty from.`,
        'warn',
      )
    }
    return false
  }

  const a = endText(bridge.a, s.session.nodes)
  const c = endText(bridge.b, s.session.nodes)
  if (!a || !c) return false

  const opId = s.beginOp('link', localEnds(bridge), { model: stampOf(m.entry) })
  const controller = new AbortController()
  inFlight = controller
  let thought = ''
  const queries: string[] = []
  const failures: string[] = []
  const stamp: ModelStamp = stampOf(m.entry)

  /** One gather. Its JSON is discarded — what is wanted is `res.search`. `step`
   *  names it in the one sentence said if it does not finish. */
  const gather = async (
    system: string,
    user: string,
    budget: typeof LINK_GATHER_BUDGET,
    step: string,
  ) => {
    const res = await streamOpObjects(opId, {
      model: m,
      system,
      user,
      schema: GATHER_SCHEMA,
      // Pinned, like the source check's. A temper chosen for reasoning must not
      // change how hard this app looks before it reports having found nothing.
      effort: resolvePassEffort({ pass: 'gather', levels: s.effortLevels }),
      maxTokens: 4000,
      web: budget,
      signal: controller.signal,
      onObject: () => {},
      onThinking: (d) => {
        thought += d
        useStore.getState().setThought(thought)
      },
      onSearch: (ev) => {
        useStore
          .getState()
          .setSearching(
            ev.kind === 'query' ? `searching: ${ev.text}` : `reading: ${hostOf(ev.url)}`,
          )
      },
    })
    // Billed before anything can back out. A gather that returned nothing still
    // searched, and the searches are inside `res.spend`.
    useStore.getState().addSpend(res.spend, opId)
    if (res.search) {
      queries.push(...res.search.queries)
      const unfinished = linkStepFailure(step, res.search)
      if (unfinished) failures.push(unfinished)
    }
    return res.search
  }

  try {
    // The conjunction runs FIRST, alone. It is the only search that can produce
    // a positive finding, and `assess` tests `already-made` ahead of everything
    // else — so a pair somebody has already joined costs one gather instead of
    // three. Sound because the join test scores a document against the two
    // NODES' terms and against a floor that belongs to the document, never
    // against the side piles or the pass: empty piles cannot change its verdict.
    const conj = await gather(
      buildConjunctionSystem(
        m.entry.search === 'results+text' ? undefined : LINK_CONJUNCTION_BUDGET,
      ),
      buildConjunctionTurn(a, c),
      LINK_CONJUNCTION_BUDGET,
      LINK_STEP.both,
    )
    const early = assess(
      { ...a, pile: { docs: [], sound: true } },
      { ...c, pile: { docs: [], sound: true } },
      toPile(conj),
    )

    let linkage: Linkage = early
    let aPile: LinkPile = { docs: [], sound: true }
    let cPile: LinkPile = { docs: [], sound: true }

    if (early.reading !== 'already-made') {
      const gatherSystem = buildGatherSystem(
        m.entry.search === 'results+text' ? undefined : LINK_GATHER_BUDGET,
      )
      aPile = toPile(
        await gather(
          gatherSystem,
          buildGatherTurn(s.session, a),
          LINK_GATHER_BUDGET,
          LINK_STEP.side(a.label),
        ),
      )
      cPile = toPile(
        await gather(
          gatherSystem,
          buildGatherTurn(s.session, c),
          LINK_GATHER_BUDGET,
          LINK_STEP.side(c.label),
        ),
      )
      linkage = assess({ ...a, pile: aPile }, { ...c, pile: cPile }, toPile(conj))
    }

    // The reasoning pass, and ONLY on the one reading that earned it. Every other
    // reading is a statement about the search, which no model is needed to make.
    let mechanism: string | undefined
    let via: string | undefined
    if (worthReasoning(linkage)) {
      const res = await streamOpObjects(opId, {
        model: m,
        system: buildLinkSystem(),
        user: buildLinkTurn(s.session, a, c, linkage.intermediates, linkage.aDocs, linkage.cDocs),
        schema: LINK_SCHEMA,
        effort: resolvePassEffort({ pass: 'linkage', levels: s.effortLevels }),
        maxTokens: 6000,
        // No `web`: this pass reasons over terms already harvested. Declaring a
        // search here would let it cite a page nothing gated.
        signal: controller.signal,
        onObject: (obj) => {
          if (mechanism !== undefined) return
          const r = obj as Record<string, unknown>
          if (r.holds !== true) return
          const term = str(r.via, 120)
          // The supplied list is the whole permitted vocabulary. A term the
          // arithmetic did not count is a term the search did not return, and
          // accepting it would put the mechanism back on the model's memory.
          if (!linkage.intermediates.some((i) => i.term === term)) return
          via = term
          mechanism = str(r.mechanism, 1200)
        },
        onThinking: (d) => {
          thought += d
          useStore.getState().setThought(thought)
        },
      })
      useStore.getState().addSpend(res.spend, opId)
    }

    // The SECOND look: the same test over OpenAlex, with no model in the loop
    // (`engine/indexPiles.ts`). Gated the way every corpus-door fetch is gated —
    // the row must be on, and "prefer my sources" promises nothing leaves the
    // machine — and its failure is recorded as an absence rather than as a
    // reading: an index that could not be asked says nothing about the pair,
    // and the web reading above stands on its own. Read by the ladder as a
    // stop (`prior-art`) when it finds the pair joined; see `IndexLinkage`.
    let index: IndexLinkage | undefined
    const indexOn =
      !s.preferCorpus && enabledLiveRows(s.sourcesOn).some((row) => row.id === 'openalex')
    if (indexOn && !controller.signal.aborted) {
      useStore.getState().setSearching('searching OpenAlex for the conjunction')
      const evidenceOf = (e: BridgeEnd): string[] | undefined =>
        e.where === 'local' ? s.session.nodes[e.nodeId]?.evidence : undefined
      const got = await scanIndexPiles(
        { label: a.label, claim: a.claim, evidence: evidenceOf(bridge.a) },
        { label: c.label, claim: c.claim, evidence: evidenceOf(bridge.b) },
        { signal: controller.signal, mailto: s.sourceMailto },
      )
      if (indexScanFailed(got)) failures.push(got.failed)
      else index = got
      useStore.getState().setSearching(null)
    }

    const stored: StoredLinkage = {
      reading: linkage.reading,
      intermediates: linkage.intermediates.map((i) => ({
        term: i.term,
        aDocs: i.aDocs,
        cDocs: i.cDocs,
      })),
      ...(mechanism ? { mechanism, via } : {}),
      joinedBy: linkage.joinedBy.map((d) => toJoining(d, conj)),
      queries,
      aDocs: linkage.aDocs,
      cDocs: linkage.cDocs,
      depth: linkage.depth,
      ...(linkage.reading === 'unsound' && failures.length ? { failures } : {}),
      ...(index ? { index } : {}),
      scannedAt: Date.now(),
      model: stamp,
    }

    useStore.getState().patchBridge(bridgeId, { linkage: stored })
    trackRung(bridgeId)
    useStore.getState().endOp(opId, [], { thinking: thought })
    // The chip's own words, with what the search drew riding beside them. A toast
    // that summarised the reading differently from the card it is about would be
    // two accounts of one pass.
    if (!opts.quiet) {
      const charge = chargeSuffix(opId)
      useStore
        .getState()
        .toast(
          LINK_READING_LABEL[linkage.reading] + charge.text + '.',
          charge.over ? 'warn' : 'info',
        )
    }
    return true
  } catch (err) {
    if (err instanceof ModelError && err.spent) useStore.getState().addSpend(err.spent, opId)
    const msg = reportError(err)
    useStore.getState().endOp(opId, [], { error: msg, errorKind: kindOf(err), thinking: thought })
    if (msg !== 'Stopped.' && !opts.quiet) useStore.getState().toast(msg, 'bad')
    return false
  } finally {
    if (inFlight === controller) inFlight = null
  }
}

/** Turn a candidate bridge into a real synthesis node spanning both branches. */
export async function realizeBridge(bridge: Bridge): Promise<void> {
  const s = useStore.getState()
  // Out loud, as the link scan and the cross-examination refuse: the Overview's
  // press stays focusable under the lock now (D225), so a press made there
  // reaches this, and a silent return read as a button that did nothing.
  if (s.busy) {
    s.toast('One thought at a time — let the current pass finish.', 'warn')
    return
  }

  // The synthesis has to hang somewhere in THIS graph, so the anchor is a local
  // end — and both ends must still be readable, because fusing with a branch
  // that has been pruned is fusing with nothing.
  if (!bridgeReadable(bridge, s.session.nodes)) {
    s.toast('One side of that pair has been pruned, so there is nothing left to fuse.', 'warn')
    return
  }
  const anchorEnd = bridge.a.where === 'local' ? bridge.a : bridge.b
  if (anchorEnd.where !== 'local') {
    s.toast(
      'Both sides of that pair are in other explorations — there is nowhere here to attach it.',
      'warn',
    )
    return
  }
  const a = s.session.nodes[anchorEnd.nodeId]!
  const farEnd = anchorEnd === bridge.a ? bridge.b : bridge.a
  // A local far end contributes its full reasoning. A foreign one contributes the
  // copy taken when the candidate was found, and the prompt says so.
  // A foreign end is re-read from its stored document when that document is still
  // there, so the fusion gets the full branch rather than the one sentence copied
  // at scan time. When it is gone, the copy is all there is — and the prompt says
  // so, dates it, and forbids attributing anything further to it.
  const foreignLive =
    farEnd.where === 'foreign' ? loadForeignNode(farEnd.ref.sessionId, farEnd.ref.nodeId) : null
  const far =
    farEnd.where === 'local'
      ? { node: s.session.nodes[farEnd.nodeId]!, ref: null, foreign: null }
      : { node: null, ref: farEnd.ref, foreign: foreignLive }

  const m = resolveFor(s, 'reason')
  if (!m) {
    s.toast(`Add your ${providerFor(s, 'reason').name} key to realise a synthesis.`, 'warn')
    s.askForMissingKey('reason', SYNTHESIS_NEED)
    return
  }

  // One step covers the synthesis node, dropping the candidate, and the select.
  s.mark(OP_LABEL.bridge)

  const opId = s.beginOp('bridge', far.node ? [a.id, far.node.id] : [a.id], {
    model: stampOf(m.entry),
  })
  const controller = new AbortController()
  inFlight = controller
  const produced: string[] = []
  let thought = ''

  try {
    const res = await streamOpObjects(opId, {
      model: m,
      system: buildSystem(s.lens, s.temper, s.session.frame, s.session.brief),
      user: `${buildUserTurn(s.session, a.id, 'bridge')}

${
  far.node
    ? `${buildOtherBranch({
        label: `[${far.node.id}] ${far.node.label}`,
        claim: far.node.claim,
        reasoning: reasoningOf(far.node),
      })}${buildSourceRecordBlock(
        s.session,
        far.node.id,
        "SOURCE CHECKS ON THE OTHER LOCAL BRANCH'S DEPENDENCY PATH",
      )}`
    : far.foreign
      ? buildOtherBranch(
          {
            label: far.foreign.label,
            claim: far.foreign.claim,
            // Through the one rule, as the local end is: a foreign node rewritten
            // without new reasoning carries an argument for other words.
            reasoning: reasoningOf(far.foreign),
          },
          undefined,
          far.ref!.seed,
        )
      : buildOtherBranch(
          { label: far.ref!.label, claim: far.ref!.claim },
          { seed: far.ref!.seed, at: far.ref!.at },
        )
}

PROPOSED FUSION: ${bridge.title}
${bridge.why}

Write the synthesis itself. What does the combination make possible, what does it now require, and what is the first thing that would have to be true for it to hold?`,
      schema: NODES_SCHEMA,
      effort: resolvePassEffort({ pass: 'synthesis', levels: s.effortLevels }),
      maxTokens: 14000,
      signal: controller.signal,
      onObject: (obj) => {
        const n = toNode(obj, a.id, 'bridge', stampOf(m.entry))
        if (!n) return
        // `bridgeFrom` names two nodes the layout can seat and the renderer can
        // draw a second strand to. A foreign partner has no position on this
        // canvas, so it goes in `foreignFrom` instead and the canvas draws a stub.
        if (far.node) n.bridgeFrom = [a.id, far.node.id]
        else n.foreignFrom = far.ref!
        if (n.kind !== 'synthesis' && n.kind !== 'insight') n.kind = 'synthesis'
        useStore.getState().addNodes([n])
        produced.push(n.id)
      },
      onThinking: (d) => {
        thought += d
        useStore.getState().setThought(thought)
      },
    })

    useStore.getState().addSpend(res.spend, opId)
    pulses.mark(far.node ? [a.id, far.node.id] : [a.id])
    useStore.getState().endOp(opId, produced, { thinking: thought })

    if (produced[0]) {
      // The candidate STAYS in the list, now pointing at its synthesis. It used
      // to be dropped here, which is why `realizedNodeId` was a field nothing
      // wrote: a realised pair vanished from the one surface that could say
      // what happened to it next. On the ladder a realised candidate is the
      // one worth the most attention — every rung above `realised` is earned
      // on the synthesis node — so it is kept, sorted to the top, and the card
      // offers the presses that climb from here (D82).
      useStore.getState().patchBridge(bridge.id, { realizedNodeId: produced[0] })
      trackRung(bridge.id)
      useStore.getState().select(produced[0])
      useStore.getState().setRightTab('node')
      useStore.getState().toast('Synthesis realised.', 'good')
    } else {
      useStore.getState().toast('Those branches did not fuse into anything new.', 'info')
    }
  } catch (err) {
    if (err instanceof ModelError && err.spent) {
      useStore.getState().addSpend(err.spent, opId)
    }
    const msg = reportError(err)
    useStore
      .getState()
      .endOp(opId, produced, { error: msg, errorKind: kindOf(err), thinking: thought })
    if (msg !== 'Stopped.') useStore.getState().toast(msg, 'bad')
  } finally {
    if (inFlight === controller) inFlight = null
  }
}

/* ── The cross-provider audit ──────────────────────────────────────────── */

/**
 * How many claims one cross-examination covers.
 *
 * Exported because it is also this pass's node YIELD, and `run.ts` has to know
 * it: every claim handed over can come back with an objection that lands, so one
 * audit step alone can reach `SCAN_FLOOR`. A second copy of the number over
 * there would be one edit away from being the smaller of two.
 */
export const AUDIT_MAX_CLAIMS = 12

/**
 * The branch to hand over: the node and its descendants, in arrival order.
 *
 * Capped, and the cap is why the survival count says how many claims it covers
 * rather than implying it covers the branch. A node the user rewrote is included
 * — their words are as attackable as the model's, and excluding them would let a
 * graph launder its weakest claims past the audit by having been edited.
 */
export function auditTargets(s: Session, rootId: string): ReasonNode[] {
  const kids = new Map<string, string[]>()
  for (const id of s.order) {
    const p = s.nodes[id]?.parentId
    if (!p) continue
    const l = kids.get(p)
    if (l) l.push(id)
    else kids.set(p, [id])
  }
  const out: ReasonNode[] = []
  const stack = [rootId]
  const seen = new Set<string>()
  while (stack.length && out.length < AUDIT_MAX_CLAIMS) {
    const id = stack.shift()!
    if (seen.has(id)) continue
    seen.add(id)
    const n = s.nodes[id]
    if (!n) continue
    if (n.parentId !== null) out.push(n)
    for (const c of kids.get(id) ?? []) stack.push(c)
  }
  return out
}

/**
 * The auditor a cross-examination of this branch would ACTUALLY run on.
 *
 * `runAudit` resolves against the branch's own author — the selection is only
 * the fallback for a wholly unstamped branch — so a surface that prices the
 * pass by resolving against the selected provider names the wrong model, and
 * can lean LOW, exactly when the two differ: an estimate's one forbidden
 * direction. Exported for the run gate's chip, which states an audit's figure
 * before the go; one definition, so the figure and the pass cannot disagree.
 */
export function auditorFor(
  s: Pick<
    Parameters<typeof resolveAuditor>[0],
    'modelId' | 'keys' | 'customBase' | 'customModel'
  > & {
    session: Session
  },
  rootId: string,
): ResolvedModel | null {
  const root = s.session.nodes[rootId]
  if (!root) return null
  const against = branchAuthor(root, auditTargets(s.session, rootId), activeEntry(s).provider)
  return resolveAuditor(s, against)
}

function toObjectionNode(raw: unknown, parentId: string, model: ModelStamp): ReasonNode | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const retained = textRetention('admission')
  const label = retained.text('label', o.label, 90)
  const claim = retained.text('claim', o.claim)
  if (!label || !claim) return null
  return makeNode({
    parentId,
    label,
    claim,
    reasoning: retained.text('reasoning', o.reasoning),
    kind: 'contradiction',
    confidence: num(o.confidence, 0.6),
    // Not asked for and not invented. Novelty is distance from the obvious in a
    // graph this model has not been reasoning in, so it has no basis to rate it
    // and `makeNode`'s default stands rather than a number nobody computed.
    tension: num(o.tension, 0.8),
    evidence: retained.list('evidence', o.evidence, 2),
    nextSteps: [],
    terminal: false,
    origin: 'audit',
    model,
    ...retained.fields(),
  })
}

/** One bounded claim per physical request. Coverage is written before dispatch,
 * so a closed tab leaves an unknown attempt instead of an apparent pass. The
 * caller authorizes the bounded batch; no response can schedule more work. */
export async function runAudit(
  rootId: string,
  opts: {
    mark?: boolean
    quiet?: boolean
    maxClaims?: number
    maxTokens?: number
    continuationOf?: string
    onStart?: (opId: string) => void
    onVerdicts?: (v: Audit['verdict'][]) => void
  } = {},
): Promise<boolean> {
  const s = useStore.getState()
  if (s.busy) {
    if (!opts.quiet) s.toast('One thought at a time — let the current pass finish.', 'warn')
    return false
  }
  const root = s.session.nodes[rootId]
  if (!root) return false
  const branch = auditTargets(s.session, rootId)
  const m = auditorFor(s, rootId)
  if (!m || !branch.length) return false
  let eligible = auditableBy(branch, m.entry.provider)
  if (opts.continuationOf) {
    const previous = s.session.ops.find((op) => op.id === opts.continuationOf)
    if (previous?.kind !== 'audit' || !previous.auditCoverage || previous.targetIds[0] !== rootId)
      return false
    const remaining = new Set(
      previous.auditCoverage.claims.filter((c) => c.research !== 'completed').map((c) => c.nodeId),
    )
    eligible = eligible.filter((node) => remaining.has(node.id))
  }
  const limit = Math.max(
    1,
    Math.min(AUDIT_MAX_CLAIMS, Math.floor(opts.maxClaims ?? AUDIT_MAX_CLAIMS)),
  )
  const targets = eligible.slice(0, limit)
  if (!targets.length) return false
  const requested = targets.map((n) => n.id)
  const coverage: import('../core/types').AuditCoverage = {
    version: 1,
    requested,
    skipped: branch.filter((n) => !requested.includes(n.id)).map((n) => n.id),
    ...(opts.continuationOf ? { continuationOf: opts.continuationOf } : {}),
    claims: targets.map((n) => ({
      nodeId: n.id,
      transport: 'not-started',
      truncated: null,
      structured: 'missing',
      research: 'incomplete',
    })),
  }
  if (opts.mark !== false) s.mark(OP_LABEL.audit)
  const stamp = stampOf(m.entry)
  const opId = s.beginOp('audit', [rootId], { model: stamp })
  s.setAuditCoverage(opId, coverage)
  opts.onStart?.(opId)
  const controller = new AbortController()
  inFlight = controller
  const produced: string[] = []
  const verdicts: Audit['verdict'][] = []
  let thought = ''
  let activeIndex = -1
  const saveCoverage = () => useStore.getState().setAuditCoverage(opId, coverage)
  try {
    for (const [index, target] of targets.entries()) {
      controller.signal.throwIfAborted()
      activeIndex = index
      const row = coverage.claims[index]!
      row.callId = `${opId}:call:${index + 1}`
      saveCoverage()
      const findings: Record<string, unknown>[] = []
      const res = await streamOpObjects(opId, {
        model: m,
        system: buildAuditSystem(s.session.brief),
        user: buildAuditTurn(s.session, [target], target),
        researchContext: {
          session: s.session,
          targetIds: [target.id],
          onPrepared: () => {
            row.transport = 'unknown'
            saveCoverage()
          },
          purpose: `Review this claim separately for statement, derivation, assumptions, scope, novelty and relevance to the exact research question.${opts.continuationOf ? ` This is a separately authorized continuation of ${opts.continuationOf}; it does not resolve or replay that attempt.` : ''}`,
        },
        schema: AUDIT_SCHEMA,
        effort: resolvePassEffort({ pass: 'audit', levels: s.effortLevels }),
        maxTokens: Math.max(1000, Math.min(20000, Math.floor(opts.maxTokens ?? 20000))),
        maxRetries: 0,
        signal: controller.signal,
        onObject: (obj) => {
          if (obj && typeof obj === 'object') findings.push(obj as Record<string, unknown>)
        },
        onThinking: (d) => {
          thought += d
          useStore
            .getState()
            .setThought(
              `${m.entry.name} is reviewing claim ${index + 1} of ${targets.length}. ${thought.slice(-560)}`,
            )
        },
      })
      useStore.getState().addSpend(res.spend, opId)
      row.transport = res.refused ? 'refused' : 'completed'
      row.truncated = res.truncated
      const math = useStore.getState().session.research?.mathematics
      const mathematicalTargets = math?.enabled
        ? math.obligations.filter(
            (o, i, rows) =>
              o.nodeId === target.id && rows.findLastIndex((v) => v.id === o.id) === i,
          )
        : []
      const finding =
        findings.length === 1
          ? parseAuditFinding(findings[0]!, target.id, mathematicalTargets)
          : null
      row.structured = finding ? 'valid' : findings.length ? 'invalid' : 'missing'
      // A parseable prefix from a truncated answer is not a completed review.
      if (finding && !res.truncated && !res.refused) {
        const at = Date.now()
        const landed =
          finding.verdict === 'objection'
            ? toObjectionNode(finding.objection, target.id, stamp)
            : null
        if (landed) {
          // Novelty and relevance objections do not contradict the statement.
          if (finding.scope === 'novelty' || finding.scope === 'relevance')
            landed.kind = 'implication'
          landed.reasoning = `Proposed criticism of ${finding.scope}; not independently checked. ${landed.reasoning}`
          useStore.getState().addNodes([landed])
          produced.push(landed.id)
        }
        row.research =
          landed && nodeTextTruncationNotes(landed).length
            ? 'incomplete'
            : finding.verdict === 'insufficient-context'
              ? 'insufficient-context'
              : 'completed'
        verdicts.push(finding.verdict)
        useStore.getState().patchNode(target.id, {
          audit: {
            model: stamp,
            verdict: finding.verdict,
            scope: finding.scope,
            ...(finding.proofStep ? { proofStep: finding.proofStep } : {}),
            status:
              finding.verdict === 'objection'
                ? 'proposed'
                : finding.verdict === 'pass'
                  ? 'no-objection'
                  : 'needs-context',
            ...(landed ? { objectionNodeId: landed.id } : {}),
            note: finding.note,
            at,
          },
          lastPulse: at,
        })
      }
      saveCoverage()
      // Refusals end this authorized batch, without converting them into a
      // research conclusion. Truncation leaves this claim incomplete and the
      // remaining finite, preselected claims can still receive their own review.
      if (res.refused) break
    }
    const completed = coverage.claims.filter((row) => row.research === 'completed').length
    const context = coverage.claims.filter((row) => row.research === 'insufficient-context').length
    const note = `${completed}/${targets.length} claim reviews completed; ${context} need context; ${coverage.skipped.length} outside this batch.`
    useStore.getState().endOp(opId, produced, {
      ...(completed < targets.length ? { error: note } : {}),
      thinking: thought.trim().slice(0, 8000),
    })
    opts.onVerdicts?.(verdicts)
    // Two counts and no claim: how many were examined, how many broke.
    track('audit_verdict', {
      examined: String(verdicts.length),
      objected: String(verdicts.filter((v) => v === 'objection').length),
    })
    if (!opts.quiet) useStore.getState().toast(note, completed < targets.length ? 'warn' : 'info')
    return completed === targets.length
  } catch (err) {
    if (err instanceof ModelError && err.spent) useStore.getState().addSpend(err.spent, opId)
    if (activeIndex >= 0) {
      const call = useStore
        .getState()
        .session.ops.find((op) => op.id === opId)
        ?.modelCalls?.at(-1)
      if (
        call &&
        call.id === coverage.claims[activeIndex]!.callId &&
        call.outcome.kind === 'failed'
      )
        coverage.claims[activeIndex]!.transport = 'failed'
    }
    saveCoverage()
    if (err instanceof ModelError && err.kind === 'auth')
      useStore.getState().askForKey(m.entry.provider)
    const msg = reportError(err)
    useStore.getState().endOp(opId, produced, {
      error: msg,
      errorKind: kindOf(err),
      thinking: thought.trim().slice(0, 8000),
    })
    opts.onVerdicts?.(verdicts)
    if (!opts.quiet) useStore.getState().toast(msg, 'bad')
    return false
  } finally {
    if (inFlight === controller) inFlight = null
  }
}
